import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Carousel, Modal, Spinner } from 'react-bootstrap';
import {
  BellFill,
  BoxArrowUpRight,
  ChevronDown,
  ChevronUp,
  Images,
  InfoCircle,
  ReplyFill,
  Search,
  Star,
  StarFill,
  TrophyFill,
  Trash3Fill,
  Wallet2,
  X,
} from 'react-bootstrap-icons';
import { useNavigate } from 'react-router-dom';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useAuth } from '../AuthContext';
import ChatBottomBar from '../ChatBottomBar';
import Codee from '../ui/Codee';
import { showGlassToast } from '../ui/GlassToast';
import { useTheme } from '../../ThemeContext';
import { WS_URL } from '../../config/endpoints';
import './ChatPage.css';




const PAGE_SIZE = 40;
const SELLER_ITEMS_PAGE_SIZE = 30;
const BOTTOM_THRESHOLD_PX = 140;
const TOP_FETCH_THRESHOLD_PX = 420;
const LONG_PRESS_MS = 260;
const MAX_REPLY_JUMP_LOADS = 12;
const SWIPE_LOCK_THRESHOLD_PX = 10;
const SWIPE_DIRECTION_DOMINANCE_PX = 6;
const SWIPE_REPLY_TRIGGER_PX = 56;
const SWIPE_DELETE_TRIGGER_PX = 76;
const SWIPE_MAX_OFFSET_PX = 92;
const DELETE_ANIMATION_MS = 240;
const SELECTION_LOCK_MS = 700;
const CHAT_MAX_RENDERED_MESSAGES = 260;
const CHAT_TRIM_TO_MESSAGES = 200;
const CHAT_SEARCH_PAGE_SIZE = 30;
const CHAT_DELETED_REPLY_TEXT = 'Сообщение удалено';
const CHAT_PRODUCT_REPLY_TEXT = 'Товар';
const formatContestTimer = (endsAt, nowMs = Date.now()) => {
  const endMs = Date.parse(String(endsAt || ''));
  if (!Number.isFinite(endMs)) return '';
  const diffMs = Math.max(0, endMs - Number(nowMs || Date.now()));
  const totalSeconds = Math.floor(diffMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (value) => String(value).padStart(2, '0');
  if (days > 0) return `${days}д ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

const getContestParticipantName = (entry) => {
  const profile = entry?.profile || {};
  const handle = String(entry?.chat_handle || profile.chat_handle || '').trim();
  if (handle) return `@${handle}`;
  return String(entry?.display_name || profile.display_name || profile.first_name || '').trim()
    || `User ${entry?.account_id || ''}`.trim();
};

const CHAT_REACTIONS = [
  { key: 'smile', fallback: '😁', title: 'Смешно', asset: '/reactions/smile.svg' },
  { key: 'kiss', fallback: '😘', title: 'Целую', asset: '/reactions/kiss.svg' },
  { key: 'hundred', fallback: '💯', title: 'На все сто', asset: '/reactions/hundred.svg' },
  { key: 'sob', fallback: '😭', title: 'Плачу', asset: '/reactions/sob.svg' },
  { key: 'sad', fallback: '😢', title: 'Грустно', asset: '/reactions/sad.svg' },
  { key: 'flushed', fallback: '😳', title: 'Ого', asset: '/reactions/flushed.svg' },
  { key: 'love', fallback: '🥰', title: 'Мило', asset: '/reactions/love.svg' },
  { key: 'heart', fallback: '❤️', title: 'Люблю', asset: '/reactions/heart.svg' },
  { key: 'heart_eyes', fallback: '😍', title: 'Влюблен', asset: '/reactions/heart_eyes.svg' },
  { key: 'fire', fallback: '🔥', title: 'Огонь', asset: '/reactions/fire.svg' },
  { key: 'wow', fallback: '🤩', title: 'Вау', asset: '/reactions/wow.svg' },
  { key: 'slay', fallback: '💅', title: 'Красиво', asset: '/reactions/slay.svg' },
  { key: 'angry', fallback: '🤬', title: 'Злюсь', asset: '/reactions/angry.svg' },
];
const CHAT_REACTION_KEYS = new Set(CHAT_REACTIONS.map((item) => item.key));
const CHAT_PHOTO_PREVIEW_MAX_WIDTH = 300;
const CHAT_PHOTO_PREVIEW_MAX_HEIGHT = 320;
const CHAT_IMAGE_VIEWER_CLOSE_SWIPE_PX = 96;
const CHAT_IMAGE_VIEWER_MAX_DRAG_PX = 220;
const MESSAGE_TOKEN_RE = /((https?:\/\/|www\.)[^\s<>"']+)|@([A-Za-z0-9_]{3,32})/gi;
const CHAT_HANDLE_RE = /^[a-z0-9_]{3,32}$/;
const PROMO_INTENT_KEYWORDS = new Set(['пром', 'промо', 'промик', 'промики', 'промокод', 'промокоды']);
const PROMO_INTENT_REQUEST_MARKERS = [
  'дай',
  'дайте',
  'подбери',
  'подобрать',
  'нужен',
  'нужны',
  'ищу',
  'найди',
  'покажи',
  'посоветуй',
  'посоветуй',
  'хочу',
  'есть',
  'на ',
  'для ',
  'что ',
  'какие',
  'какой',
];
const PROMO_INTENT_DISCUSSION_MARKERS = [
  'не работает',
  'сработал',
  'сработало',
  'скинул',
  'скинула',
  'отправил',
  'отправила',
  'выше',
  'ниже',
  'у кого',
  'этот',
  'тот',
  'проверил',
  'проверяла',
  'использовал',
  'использовала',
];
const CHAT_PUSH_MODE_OPTIONS = [
  {
    value: 'off',
    title: 'Выключены',
    description: 'Push по чату не приходят.',
  },
  {
    value: 'mentions',
    title: 'Только упоминания',
    description: 'Push только когда вас упомянули по username.',
  },
  {
    value: 'replies_mentions',
    title: 'Ответы и упоминания',
    description: 'Push когда вам ответили или упомянули вас в чате.',
  },
  {
    value: 'all',
    title: 'Все сообщения',
    description: 'Push на каждое новое сообщение, пока приложение не онлайн.',
  },
];

const formatChatWsError = (error, scope = '') => {
  const code = String(error || '').trim();
  const normalizedScope = String(scope || '').trim();
  const baseCode = code.split(':')[0].trim();

  if (baseCode === 'rate_limited') {
    if (normalizedScope === 'chat:send') return 'Слишком много сообщений подряд. Подождите немного.';
    if (normalizedScope === 'chat:edit') return 'Слишком много правок подряд. Подождите немного.';
    if (normalizedScope === 'chat:delete') return 'Слишком много действий подряд. Подождите немного.';
    if (normalizedScope === 'chat:reaction:set') return 'Слишком много реакций подряд. Подождите немного.';
    if (normalizedScope === 'chat:subscribe') return 'История загружается слишком часто. Подождите немного.';
    if (normalizedScope === 'chat:read') return 'Слишком частое обновление прочтения. Подождите немного.';
    return 'Слишком много действий подряд. Подождите немного.';
  }

  const messages = {
    invalid_message_body: 'Сообщение пустое или слишком длинное.',
    invalid_client_msg_id: 'Не удалось отправить сообщение. Обновите страницу и попробуйте ещё раз.',
    invalid_message_id: 'Не удалось найти выбранное сообщение.',
    message_not_found: 'Сообщение не найдено или уже удалено.',
    invalid_photo_media_id: 'Не удалось прикрепить фото. Попробуйте выбрать его заново.',
    invalid_photo_thumb_media_id: 'Не удалось прикрепить миниатюру фото. Попробуйте выбрать фото заново.',
    forbidden_photo_media: 'Это фото недоступно для отправки в чат.',
    chat_message_edit_failed: 'Не удалось сохранить правку сообщения.',
    unknown_chat_event: 'Неизвестное действие чата. Обновите страницу.',
    invalid_json: 'Ошибка формата сообщения. Обновите страницу.',
    product_preview_rate_limited: 'Карточку товара можно отправлять не чаще одного раза в 30 секунд.',
    product_preview_not_found: 'Не удалось собрать карточку товара по этой ссылке.',
    product_preview_invalid: 'Ссылка на товар некорректна или товар больше недоступен.',
    product_preview_fetch_failed: 'Не удалось загрузить данные товара. Попробуйте ещё раз чуть позже.',
    invalid_reaction: 'Такую реакцию нельзя поставить.',
  };

  if (messages[baseCode]) return messages[baseCode];
  if (baseCode === 'send_failed') return 'Не удалось отправить сообщение. Попробуйте ещё раз.';
  if (baseCode === 'edit_failed') return 'Не удалось отредактировать сообщение. Попробуйте ещё раз.';
  if (baseCode === 'delete_failed') return 'Не удалось удалить сообщение. Попробуйте ещё раз.';
  if (baseCode === 'reaction_failed') return 'Не удалось поставить реакцию. Попробуйте ещё раз.';

  return code || 'Ошибка чата';
};

const appendUniqueById = (prevItems, nextItems) => {
  const seenIds = new Set(prevItems.map((item) => Number(item?.id || 0)));
  const uniqueNext = nextItems.filter((item) => {
    const id = Number(item?.id || 0);
    if (!id || seenIds.has(id)) return false;
    seenIds.add(id);
    return true;
  });
  return [...prevItems, ...uniqueNext];
};

const formatTs = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getChatPhotoPreviewStyle = (ratio) => {
  const normalizedRatio = Number(ratio || 0);
  if (!Number.isFinite(normalizedRatio) || normalizedRatio <= 0) {
    return {
      '--chat-photo-width': '260px',
      '--chat-photo-ratio': '1',
    };
  }

  let previewWidth = CHAT_PHOTO_PREVIEW_MAX_WIDTH;
  let previewHeight = previewWidth / normalizedRatio;

  if (previewHeight > CHAT_PHOTO_PREVIEW_MAX_HEIGHT) {
    previewHeight = CHAT_PHOTO_PREVIEW_MAX_HEIGHT;
    previewWidth = previewHeight * normalizedRatio;
  }

  return {
    '--chat-photo-width': `${Math.round(previewWidth)}px`,
    '--chat-photo-ratio': String(Number(normalizedRatio.toFixed(4))),
  };
};

const normalizePromoIntentText = (value) => String(value || '')
  .toLowerCase()
  .replaceAll('ё', 'е')
  .replace(/\s+/g, ' ')
  .trim();

const detectPromoIntent = (value) => {
  const normalized = normalizePromoIntentText(value);
  if (!normalized) {
    return {
      active: false,
      normalized: '',
      query: '',
    };
  }

  const tokens = normalized.split(' ').filter(Boolean);
  const hasPromoKeyword = tokens.some((token) => PROMO_INTENT_KEYWORDS.has(token));
  if (!hasPromoKeyword) {
    return {
      active: false,
      normalized,
      query: normalized,
    };
  }

  const hasDiscussionMarker = PROMO_INTENT_DISCUSSION_MARKERS.some((marker) => normalized.includes(marker));
  const hasRequestMarker = PROMO_INTENT_REQUEST_MARKERS.some((marker) => normalized.includes(marker));
  const active = hasRequestMarker || (!hasDiscussionMarker && tokens.length <= 8);

  return {
    active,
    normalized,
    query: normalized,
  };
};

const avatarPalette = [
  '#2d9cdb',
  '#27ae60',
  '#f2994a',
  '#9b51e0',
  '#eb5757',
  '#56ccf2',
  '#6fcf97',
];

const normalizeAuthor = (author) => {
  if (!author || typeof author !== 'object') return null;
  const avatarThumb = String(
    author.avatar_thumb_url
    || author.avatar_url
    || ''
  );
  const avatarFull = String(
    author.avatar_url
    || avatarThumb
    || ''
  );
  return {
    ...author,
    avatar_thumb_url: avatarThumb || null,
    avatar_url: avatarFull || null,
  };
};

const normalizeHandle = (value) => {
  const handle = String(value || '').trim().toLowerCase();
  if (!CHAT_HANDLE_RE.test(handle)) return '';
  return handle;
};

const copyTextToClipboard = async (text) => {
  const normalizedText = String(text || '');
  if (!normalizedText) return;

  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(normalizedText);
      return;
    } catch (ex) {
      console.warn('navigator clipboard failed, trying fallback', ex);
    }
  }

  const helper = document.createElement('textarea');
  helper.value = normalizedText;
  helper.setAttribute('readonly', '');
  helper.style.position = 'fixed';
  helper.style.top = '0';
  helper.style.left = '-9999px';
  helper.style.opacity = '0';
  document.body.appendChild(helper);
  helper.focus();
  helper.select();
  const copied = document.execCommand('copy');
  document.body.removeChild(helper);
  if (!copied) throw new Error('copy_failed');
};

const normalizeMessage = (msg) => {
  if (!msg || typeof msg !== 'object') return msg;
  const next = { ...msg };
  next.author = normalizeAuthor(next.author);
  next.photo_url = String(next.photo_url || '').trim() || null;
  next.photo_thumb_url = String(next.photo_thumb_url || next.photo_url || '').trim() || null;
  next.meta = next.meta && typeof next.meta === 'object' ? { ...next.meta } : null;
  const embed = next.meta?.embed && typeof next.meta.embed === 'object'
    ? { ...next.meta.embed }
    : null;
  const snapshot = embed?.snapshot && typeof embed.snapshot === 'object'
    ? { ...embed.snapshot }
    : null;
  const embedMarketplace = String(embed?.marketplace || snapshot?.marketplace || '').trim().toLowerCase();
  const embedArticle = Number(embed?.article || snapshot?.article || 0) || null;
  const defaultSourceUrl = embedMarketplace === 'ozon'
    ? `https://ozon.ru/products/${embedArticle || ''}/`
    : embedMarketplace === 'detmir'
      ? `https://www.detmir.ru/product/index/id/${embedArticle || ''}/`
      : embedMarketplace === 'yandex'
        ? `https://market.yandex.ru/`
        : `https://www.wildberries.ru/catalog/${embedArticle || 0}/detail.aspx`;
  next.product_embed = (
    embed
    && snapshot
    && String(embed.kind || '').trim().toLowerCase() === 'product'
    && ['wb', 'ozon', 'detmir', 'yandex'].includes(embedMarketplace)
  )
    ? {
      ...embed,
      snapshot,
      marketplace: embedMarketplace,
      source_url: String(embed.source_url || '').trim() || defaultSourceUrl,
      article: embedArticle,
    }
    : null;
  next.channel_post_embed = (
    embed
    && String(embed.kind || '').trim().toLowerCase() === 'channel_post'
  )
    ? {
      ...embed,
      title: String(embed.title || 'Промокоды').trim() || 'Промокоды',
      body: String(embed.body || '').trim(),
      marketplace: ['wb', 'ozon', 'detmir', 'yandex'].includes(embedMarketplace) ? embedMarketplace : 'other',
      photo_url: String(embed.photo_url || '').trim() || null,
      photo_ratio: Number(embed.photo_ratio || 0) || null,
      source_url: String(embed.source_url || '').trim() || null,
    }
    : null;
  if (next.reply_to && typeof next.reply_to === 'object') {
    const replyDeleted = Boolean(next.reply_to.deleted || next.reply_to.deleted_at);
    next.reply_to = {
      ...next.reply_to,
      deleted: replyDeleted,
      body_preview: replyDeleted
        ? CHAT_DELETED_REPLY_TEXT
        : (next.reply_to.body_preview || next.reply_to.body || 'Сообщение'),
      author: normalizeAuthor(next.reply_to.author),
    };
  }
  const mentions = Array.isArray(next.mentions) ? next.mentions : [];
  next.mentions = mentions
    .map((raw) => normalizeHandle(raw))
    .filter(Boolean);
  if (Array.isArray(next.mention_profiles)) {
    next.mention_profiles = next.mention_profiles.map((entry) => {
      const profile = normalizeAuthor(entry?.profile);
      const handle = normalizeHandle(entry?.handle || profile?.chat_handle);
      const accountId = Number(entry?.account_id || profile?.account_id || 0);
      return {
        ...entry,
        handle: handle || null,
        account_id: accountId > 0 ? accountId : null,
        profile: profile || null,
      };
    });
  } else {
    next.mention_profiles = [];
  }
  const reactions = Array.isArray(next.reactions) ? next.reactions : [];
  next.reactions = reactions
    .map((entry) => {
      const key = String(entry?.key || entry?.reaction_key || '').trim().toLowerCase();
      if (!CHAT_REACTION_KEYS.has(key)) return null;
      return {
        key,
        count: Math.max(0, Number(entry?.count || 0) || 0),
        mine: Boolean(entry?.mine),
      };
    })
    .filter((entry) => entry && entry.count > 0);
  return next;
};

const applyReactionUpdateToMessages = (messages, payload, myUserId) => {
  const messageId = Number(payload?.message_id || 0);
  if (!messageId) return messages || [];
  const actorId = Number(payload?.actor_account_id || 0);
  const actorReactionKey = String(payload?.reaction_key || '').trim().toLowerCase() || null;
  const reactions = Array.isArray(payload?.reactions) ? payload.reactions : [];

  return (messages || []).map((msg) => {
    if (Number(msg?.id || 0) !== messageId) return msg;
    const previousMineKey = (Array.isArray(msg?.reactions) ? msg.reactions : [])
      .find((entry) => entry?.mine)?.key || null;
    const nextMineKey = actorId === Number(myUserId || 0)
      ? actorReactionKey
      : previousMineKey;
    const normalizedReactions = reactions
      .map((entry) => {
        const key = String(entry?.key || entry?.reaction_key || '').trim().toLowerCase();
        if (!CHAT_REACTION_KEYS.has(key)) return null;
        return {
          key,
          count: Math.max(0, Number(entry?.count || 0) || 0),
          mine: nextMineKey === key,
        };
      })
      .filter((entry) => entry && entry.count > 0);
    return {
      ...msg,
      reactions: normalizedReactions,
    };
  });
};

const applyOptimisticReactionToMessages = (messages, messageId, nextMineKey) => {
  const normalizedMessageId = Number(messageId || 0);
  const normalizedNextKey = String(nextMineKey || '').trim().toLowerCase() || null;
  if (!normalizedMessageId) return messages || [];

  return (messages || []).map((msg) => {
    if (Number(msg?.id || 0) !== normalizedMessageId) return msg;

    const previousReactions = Array.isArray(msg?.reactions) ? msg.reactions : [];
    const previousMineKey = previousReactions.find((entry) => entry?.mine)?.key || null;
    if (previousMineKey === normalizedNextKey) return msg;

    const nextMap = new Map();
    previousReactions.forEach((entry) => {
      const key = String(entry?.key || '').trim().toLowerCase();
      if (!CHAT_REACTION_KEYS.has(key)) return;
      nextMap.set(key, {
        key,
        count: Math.max(0, Number(entry?.count || 0) || 0),
        mine: false,
      });
    });

    if (previousMineKey && nextMap.has(previousMineKey)) {
      const previous = nextMap.get(previousMineKey);
      previous.count = Math.max(0, Number(previous.count || 0) - 1);
    }

    if (normalizedNextKey && CHAT_REACTION_KEYS.has(normalizedNextKey)) {
      const next = nextMap.get(normalizedNextKey) || {
        key: normalizedNextKey,
        count: 0,
        mine: false,
      };
      next.count = Number(next.count || 0) + 1;
      next.mine = true;
      nextMap.set(normalizedNextKey, next);
    }

    const order = new Map(CHAT_REACTIONS.map((reaction, index) => [reaction.key, index]));
    const nextReactions = Array.from(nextMap.values())
      .filter((entry) => Number(entry.count || 0) > 0)
      .sort((left, right) => {
        if (Number(right.count || 0) !== Number(left.count || 0)) {
          return Number(right.count || 0) - Number(left.count || 0);
        }
        return (order.get(left.key) ?? 99) - (order.get(right.key) ?? 99);
      });

    return {
      ...msg,
      reactions: nextReactions,
    };
  });
};

const getMessagePhotoThumbUrl = (message) => String(message?.photo_thumb_url || message?.photo_url || '').trim();

const getMessagePhotoUrl = (message) => String(message?.photo_url || message?.photo_thumb_url || '').trim();

const messageHasPhoto = (message) => Boolean(
  message?.photo_media_id
  || message?.photo_thumb_media_id
  || getMessagePhotoThumbUrl(message)
  || getMessagePhotoUrl(message)
);

const getChatReactionConfig = (key) => (
  CHAT_REACTIONS.find((reaction) => reaction.key === key) || null
);

const ChatReactionVisual = ({ reaction, className = '' }) => {
  const [assetFailed, setAssetFailed] = useState(false);
  const asset = !assetFailed ? String(reaction?.asset || '').trim() : '';
  if (asset) {
    return (
      <img
        src={asset}
        alt={reaction?.title || reaction?.fallback || ''}
        className={`chat-reaction-img ${className}`}
        onError={() => setAssetFailed(true)}
        draggable={false}
      />
    );
  }
  return (
    <span className={`chat-reaction-emoji ${className}`} aria-hidden="true">
      {reaction?.fallback || '•'}
    </span>
  );
};

const getMessageReplyPreview = (message) => {
  const body = String(message?.body || '').trim();
  const hasPhoto = messageHasPhoto(message);
  const hasProductEmbed = Boolean(message?.product_embed);
  if (hasPhoto && body) return `Фото · ${body.slice(0, 160)}`;
  if (hasPhoto) return 'Фото';
  if (hasProductEmbed && body) return `${CHAT_PRODUCT_REPLY_TEXT} · ${body.slice(0, 160)}`;
  if (hasProductEmbed) return CHAT_PRODUCT_REPLY_TEXT;
  return body.slice(0, 160);
};

const sortSearchResultsNewestFirst = (items) => (
  [...(items || [])].sort((a, b) => Number(b?.id || 0) - Number(a?.id || 0))
);

const parseChatProductPhotos = (linkphoto) => (
  typeof linkphoto === 'string' && linkphoto.trim()
    ? linkphoto.split(',').map((item) => String(item || '').trim()).filter(Boolean)
    : []
);

const parseChatProductSizes = (sizesString) => {
  if (!sizesString || typeof sizesString !== 'string') return [];
  return sizesString.split('|').map((item) => {
    const trimmed = item.trim();
    if (!trimmed) return null;
    const isW = trimmed.startsWith('(W)');
    const isP = trimmed.startsWith('(P)');
    return {
      color: isW ? 'green' : isP ? 'orange' : 'gray',
      text: trimmed.replace('(W)', '').replace('(P)', '').trim(),
    };
  }).filter(Boolean);
};

const parseChatProductStocks = (stockText) => {
  const source = String(stockText || '').trim();
  if (!source) return [];
  const cleanStockText = (value) => {
    const raw = String(value || '')
      .replace(/<[^>]*>/g, '')
      .replace(/😍\s*/gi, '')
      .replace(/⚠️\s*/gi, '')
      .replace(/\s+/g, ' ')
      .trim();
    const withoutLabels = raw
      .replace(/^WB\s*-\s*/i, '')
      .replace(/^ПРОД\s*-\s*/i, '')
      .replace(/^ПРОДАВ(?:ЕЦ|ЦА)\s*-\s*/i, '')
      .trim();
    if (!withoutLabels) return '';
    if (/шт/i.test(withoutLabels)) return withoutLabels;
    if (/^\d+(?:[.,]\d+)?$/.test(withoutLabels)) return `${withoutLabels} шт.`;
    return withoutLabels;
  };
  return source.split('|').map((chunk) => {
    const text = String(chunk || '').trim();
    if (!text) return null;
    const isSeller = /прод/i.test(text);
    const displayText = cleanStockText(text);
    if (!displayText) return null;
    return {
      text: displayText,
      color: isSeller ? 'seller' : 'wb',
    };
  }).filter(Boolean);
};

const formatChatProductPrice = (value) => {
  const price = Number(value || 0);
  if (!Number.isFinite(price) || price <= 0) return '';
  return price.toLocaleString('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumSignificantDigits: 21,
  });
};

const buildChatProductRangeText = (minValue, maxValue) => {
  const minPrice = Number(minValue || 0);
  const maxPrice = Number(maxValue || 0);
  if (!minPrice || !maxPrice) return '';
  return `${formatChatProductPrice(minPrice)} - ${formatChatProductPrice(maxPrice)}`;
};

const normalizeChatProductDiapaz = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (!Array.isArray(item) || item.length < 2) return null;
      const ts = Number(item[0] || 0);
      const price = Number(item[1] || 0);
      if (!Number.isFinite(ts) || !Number.isFinite(price) || ts <= 0 || price <= 0) {
        return null;
      }
      return [Math.round(ts), Math.round(price)];
    })
    .filter(Boolean)
    .sort((left, right) => left[0] - right[0]);
};

const getChatProductRangeBounds = (diapaz, fallbackMin = null, fallbackMax = null) => {
  const normalized = normalizeChatProductDiapaz(diapaz);
  if (normalized.length > 0) {
    const prices = normalized.map((item) => item[1]);
    return {
      diapaz: normalized,
      min: Math.min(...prices),
      max: Math.max(...prices),
    };
  }

  const minPrice = Number(fallbackMin || 0);
  const maxPrice = Number(fallbackMax || 0);
  if (Number.isFinite(minPrice) && Number.isFinite(maxPrice) && minPrice > 0 && maxPrice > 0) {
    return {
      diapaz: [],
      min: Math.round(minPrice),
      max: Math.round(maxPrice),
    };
  }

  return null;
};

const getChatProductPriceChange = (diapaz) => {
  if (!Array.isArray(diapaz) || diapaz.length < 2) return null;
  const last = Number(diapaz[diapaz.length - 1]?.[1] || 0);
  const prev = Number(diapaz[diapaz.length - 2]?.[1] || 0);
  if (!last || !prev) return null;
  const diff = last - prev;
  const percent = prev ? ((diff / prev) * 100).toFixed(1) : '0.0';
  return {
    diff,
    percent,
    isIncrease: diff > 0,
    isDecrease: diff < 0,
    hasChange: diff !== 0,
  };
};

const processChatProductPrices = (priceHistory) => {
  if (!Array.isArray(priceHistory) || priceHistory.length === 0) return [];
  const sortedHistory = priceHistory.slice().sort((left, right) => left[0] - right[0]);
  const currentTs = Math.floor(Date.now() / 1000);
  return sortedHistory.map((item, index) => {
    const startTs = Number(item[0] || 0);
    const endTs = index < sortedHistory.length - 1
      ? Number(sortedHistory[index + 1][0] || startTs)
      : currentTs;
    const price = Number(item[1] || 0);
    return [startTs, endTs, price];
  }).filter((item) => item[0] > 0 && item[1] >= item[0] && item[2] > 0);
};

const removeHighOutliers = (prices, threshold = 1.0) => {
  if (!Array.isArray(prices) || prices.length < 4) return Array.isArray(prices) ? prices : [];
  const sortedPrices = prices.slice().sort((left, right) => left - right);
  const q1 = sortedPrices[Math.floor(sortedPrices.length / 4)];
  const q3 = sortedPrices[Math.floor(3 * sortedPrices.length / 4)];
  const iqr = q3 - q1;
  const upperBound = q3 + threshold * iqr;
  return prices.filter((price) => price <= upperBound);
};

const calculateChatProductWeightedMedian = (timeRanges) => {
  if (!Array.isArray(timeRanges) || timeRanges.length === 0) return null;
  const totalDuration = timeRanges.reduce((sum, item) => sum + Math.max(0, Number(item[1] || 0) - Number(item[0] || 0)), 0);
  if (totalDuration <= 0) return null;

  const weightedPrices = timeRanges.map((item) => {
    const start = Number(item[0] || 0);
    const end = Number(item[1] || 0);
    const price = Number(item[2] || 0);
    return [price, Math.max(0, end - start) / totalDuration];
  }).filter((item) => item[0] > 0 && item[1] > 0);

  if (weightedPrices.length === 0) return null;

  const prices = weightedPrices.map((item) => item[0]);
  const filteredPrices = removeHighOutliers(prices, 1.0);
  let filteredWeighted = weightedPrices.filter((item) => filteredPrices.includes(item[0]));
  if (filteredWeighted.length === 0) {
    filteredWeighted = weightedPrices;
  }

  filteredWeighted.sort((left, right) => left[0] - right[0]);
  let cumulative = 0;
  for (const [price, weight] of filteredWeighted) {
    cumulative += weight;
    if (cumulative >= 0.5) return price;
  }
  return filteredWeighted[filteredWeighted.length - 1]?.[0] || null;
};

const filterChatProductDiapazByPeriod = (diapaz, selectedPeriod) => {
  if (!Array.isArray(diapaz) || diapaz.length === 0) return [];
  if (selectedPeriod === 'all') return diapaz;

  const now = new Date();
  let startDate;
  switch (selectedPeriod) {
    case '1m':
      startDate = new Date(now.setMonth(now.getMonth() - 1));
      break;
    case '3m':
      startDate = new Date(now.setMonth(now.getMonth() - 3));
      break;
    case '6m':
      startDate = new Date(now.setMonth(now.getMonth() - 6));
      break;
    default:
      return diapaz;
  }

  const startTs = Math.floor(startDate.getTime() / 1000);
  return diapaz.filter((item) => Number(item?.[0] || 0) >= startTs);
};

const ChatProductPriceChart = ({ data, theme, selectedPeriod }) => {
  const filteredData = filterChatProductDiapazByPeriod(data, selectedPeriod);
  if (!filteredData || filteredData.length === 0) {
    return (
      <div className="text-center py-4">
        <div className="h5">📭 Нет данных за выбранный период</div>
        <p>Попробуйте выбрать другой временной диапазон</p>
      </div>
    );
  }

  const chartData = filteredData.map(([ts, price]) => ({
    date: new Date(ts * 1000).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: 'short',
    }),
    price,
    timestamp: ts,
  }));

  const textColor = theme === 'dark' ? '#fff' : '#000';
  const lineColor = theme === 'dark' ? '#8884d8' : '#413ea0';
  const gridColor = theme === 'dark' ? '#424242' : '#e0e0e0';
  const tooltipStyle = {
    backgroundColor: theme === 'dark' ? '#2a2a2a' : '#fff',
    border: 'none',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  };

  return (
    <div style={{ height: '400px', width: '100%' }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
          <XAxis
            dataKey="date"
            stroke={textColor}
            tick={{ fill: textColor }}
            tickLine={{ stroke: textColor }}
          />
          <YAxis
            stroke={textColor}
            tick={{ fill: textColor }}
            tickLine={{ stroke: textColor }}
            tickFormatter={(value) => `${Number(value || 0).toLocaleString('ru-RU')}₽`}
          />
          <Tooltip
            contentStyle={tooltipStyle}
            formatter={(value) => [`${Number(value || 0).toLocaleString('ru-RU')} ₽`, 'Цена']}
            labelStyle={{ color: textColor }}
          />
          <Line
            type="monotone"
            dataKey="price"
            stroke={lineColor}
            strokeWidth={2}
            dot={{ fill: lineColor, strokeWidth: 2 }}
            activeDot={{ r: 8 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const getChatProductMarketplaceLabel = (marketplaceValue) => {
  const marketplace = String(marketplaceValue || '').trim().toLowerCase();
  if (!marketplace) return 'WB';
  if (marketplace === 'yandex') return 'Яндекс';
  return marketplace.toUpperCase();
};

const getChatProductMarketplaceColor = (marketplaceValue) => {
  const marketplace = String(marketplaceValue || '').trim().toLowerCase();
  if (marketplace === 'wb') return 'rgba(138, 43, 226, 0.52)';
  if (marketplace === 'ozon') return 'rgba(0, 127, 255, 0.52)';
  if (marketplace === 'yandex') return 'rgba(255, 211, 67, 0.66)';
  return 'rgba(0, 194, 252, 0.52)';
};


const buildChatMediaItem = (message) => {
  const msg = normalizeMessage(message);
  const messageId = Number(msg?.id || 0);
  if (!messageId) return null;

  const productEmbed = msg.product_embed || null;
  const productSnapshot = productEmbed?.snapshot || null;
  const channelPostEmbed = msg.channel_post_embed || null;
  const messagePhotoUrl = getMessagePhotoUrl(msg);
  const messagePhotoThumbUrl = getMessagePhotoThumbUrl(msg);
  const authorName = getDisplayName(msg?.author, msg?.author_account_id);

  if (productEmbed && productSnapshot) {
    const photos = parseChatProductPhotos(productSnapshot.linkphoto);
    const imageUrl = String(photos[0] || '').trim();
    if (!imageUrl) return null;
    const marketplace = String(productEmbed.marketplace || productSnapshot.marketplace || '').trim().toLowerCase();
    return {
      id: `product-${messageId}`,
      message_id: messageId,
      type: 'product',
      title: String(productSnapshot.name || productSnapshot.brand || 'Товар').trim(),
      label: getChatProductMarketplaceLabel(marketplace),
      thumb_url: imageUrl,
      photo_url: imageUrl,
      source_url: productEmbed.source_url || productSnapshot.link || '',
      author: authorName,
      created_at: msg.created_at,
      message: msg,
    };
  }

  if (channelPostEmbed) {
    const imageUrl = String(channelPostEmbed.photo_url || '').trim();
    if (!imageUrl) return null;
    return {
      id: `channel-${messageId}`,
      message_id: messageId,
      type: 'channel_post',
      title: String(channelPostEmbed.title || channelPostEmbed.body || 'Промокоды').trim(),
      label: getChatProductMarketplaceLabel(channelPostEmbed.marketplace || channelPostEmbed.source_label || 'promo'),
      thumb_url: imageUrl,
      photo_url: imageUrl,
      source_url: channelPostEmbed.source_url || '',
      author: authorName,
      created_at: msg.created_at,
      message: msg,
    };
  }

  if (messagePhotoThumbUrl || messagePhotoUrl) {
    return {
      id: `photo-${messageId}`,
      message_id: messageId,
      type: 'photo',
      title: authorName || 'Фото',
      label: 'Фото',
      thumb_url: messagePhotoThumbUrl || messagePhotoUrl,
      photo_url: messagePhotoUrl || messagePhotoThumbUrl,
      source_url: '',
      author: authorName,
      created_at: msg.created_at,
      message: msg,
    };
  }

  return null;
};

const renderChatProductStars = (ratingValue) => {
  const rating = Number(ratingValue || 0);
  if (!Number.isFinite(rating) || rating <= 0) return null;
  return [1, 2, 3, 4, 5].map((star) => (
    rating < star
      ? <Star key={star} size={14} color="orange" style={{ marginTop: 1 }} />
      : <StarFill key={star} size={14} color="orange" style={{ marginTop: 1 }} />
  ));
};

const getChatPushModeLabel = (mode) => {
  switch (String(mode || '').trim().toLowerCase()) {
    case 'mentions':
      return 'Упоминания';
    case 'replies_mentions':
      return 'Ответы и упоминания';
    case 'all':
      return 'Все сообщения';
    default:
      return 'Выключены';
  }
};

const renderChatProductSubject = (subjectValue) => {
  const subject = String(subjectValue || '').trim();
  if (!subject) return null;

  const lines = subject
    .split(/\r?\n/)
    .map((line) => String(line || '').trim())
    .filter(Boolean);

  if (lines.length === 0) return null;

  return lines.map((line, lineIndex) => (
    <div key={`subject-line-${lineIndex}`} className="chat-product-card-subject-line">
      {line.split(/(<code>.*?<\/code>)/g).filter(Boolean).map((part, partIndex) => {
        if (part.startsWith('<code>') && part.endsWith('</code>')) {
          const codeValue = part.replace(/<\/?code>/g, '').trim();
          if (!codeValue) return null;
          return (
            <Codee key={`subject-code-${lineIndex}-${partIndex}`}>
              {codeValue}
            </Codee>
          );
        }

        return (
          <span
            key={`subject-text-${lineIndex}-${partIndex}`}
            className="chat-product-card-subject-text"
          >
            {part}
          </span>
        );
      })}
    </div>
  ));
};

const getDisplayName = (author, fallbackId = null) => {
  const name = String(author?.display_name || '').trim();
  if (name) return name;
  if (fallbackId) return `User ${fallbackId}`;
  return 'User';
};

const getInitials = (rawName) => {
  const safe = String(rawName || '').trim();
  if (!safe) return 'U';
  const parts = safe.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  const token = parts[0];
  return token.slice(0, 2).toUpperCase();
};

const pickAvatarColor = (seed) => {
  const text = String(seed || '0');
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) {
    hash = ((hash << 5) - hash) + text.charCodeAt(i);
    hash |= 0;
  }
  return avatarPalette[Math.abs(hash) % avatarPalette.length];
};

const uniqById = (messages) => {
  const map = new Map();
  (messages || []).forEach((msg) => {
    const normalized = normalizeMessage(msg);
    const id = Number(normalized?.id || 0);
    if (!id) return;
    map.set(id, normalized);
  });
  return Array.from(map.values()).sort((a, b) => Number(a.id) - Number(b.id));
};

const applyEditedMessage = (messages, editedMessage) => {
  const edited = normalizeMessage(editedMessage);
  const editedId = Number(edited?.id || 0);
  if (!editedId) return messages || [];

  const editedPreview = getMessageReplyPreview(edited);
  const nextMessages = (messages || []).map((msg) => {
    const normalized = normalizeMessage(msg);
    const messageId = Number(normalized?.id || 0);
    if (messageId === editedId) {
      return edited;
    }

    const replyId = Number(normalized?.reply_to?.id || 0);
    if (replyId === editedId) {
      return {
        ...normalized,
        reply_to: {
          ...(normalized.reply_to || {}),
          body_preview: editedPreview,
          author: edited.author || normalized.reply_to?.author || null,
          deleted: false,
        },
      };
    }

    return normalized;
  });

  return uniqById(nextMessages);
};

const applyDeletedMessageToReplies = (messages, deletedMessageId, deletedAt = null) => {
  const deletedId = Number(deletedMessageId || 0);
  if (!deletedId) return messages || [];

  return (messages || []).map((msg) => {
    const normalized = normalizeMessage(msg);
    const replyId = Number(normalized?.reply_to?.id || 0);
    if (replyId !== deletedId) return normalized;

    return {
      ...normalized,
      reply_to: {
        ...(normalized.reply_to || {}),
        id: deletedId,
        body: '',
        body_preview: CHAT_DELETED_REPLY_TEXT,
        deleted: true,
        deleted_at: deletedAt || normalized.reply_to?.deleted_at || null,
      },
    };
  });
};

const ChatPage = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const {
    userInfo,
    getWsTicket,
    getMarketItemsBySeller,
    getPublicProfile,
    updatePublicProfile,
    ensurePushToken,
    uploadMedia,
    searchPromoAssistant,
  } = useAuth();

  const [messages, setMessages] = useState([]);
  const [chatState, setChatState] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [isEditingMessageSaving, setIsEditingMessageSaving] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [hasMoreOlder, setHasMoreOlder] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [subscriptionEnded, setSubscriptionEnded] = useState(false);
  const [deviceLimitReached, setDeviceLimitReached] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState(0);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const [newBelowCount, setNewBelowCount] = useState(0);
  const [replyMentionState, setReplyMentionState] = useState({
    count: 0,
    next_message_id: null,
  });
  const [isReplyMentionLoading, setIsReplyMentionLoading] = useState(false);
  const [highlightedMessageId, setHighlightedMessageId] = useState(0);
  const [pendingReplyJumpId, setPendingReplyJumpId] = useState(0);
  const [composerHeight, setComposerHeight] = useState(92);
  const [isLandscape, setIsLandscape] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth > window.innerHeight;
  });
  const [isWideDesktopLayout, setIsWideDesktopLayout] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth >= 1080;
  });
  const [myChatHandle, setMyChatHandle] = useState('');
  const [messageMenu, setMessageMenu] = useState({
    open: false,
    message: null,
    x: 0,
    y: 0,
    mobile: false,
    placement: 'below',
  });
  const [sellerModal, setSellerModal] = useState({
    show: false,
    sellerId: null,
    profile: null,
    items: [],
    loading: false,
    loadingMore: false,
    hasMore: false,
    selectedItem: null,
  });
  const [sellerCopyNotice, setSellerCopyNotice] = useState('');
  const [swipePreview, setSwipePreview] = useState({
    messageId: 0,
    offsetX: 0,
    action: '',
    dragging: false,
  });
  const [deletingMessageIds, setDeletingMessageIds] = useState([]);
  const [chatNotice, setChatNotice] = useState({
    show: false,
    text: '',
    variant: 'info',
  });
  const [attachedPhoto, setAttachedPhoto] = useState(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [chatPhotoRatios, setChatPhotoRatios] = useState({});
  const [promoResult, setPromoResult] = useState(null);
  const [promoExpandedMarkets, setPromoExpandedMarkets] = useState({});
  const [promoDismissedQuery, setPromoDismissedQuery] = useState('');
  const [chatPushMode, setChatPushMode] = useState('off');
  const [isChatPushModalOpen, setIsChatPushModalOpen] = useState(false);
  const [isSavingChatPushMode, setIsSavingChatPushMode] = useState(false);
  const [isPromoLoading, setIsPromoLoading] = useState(false);
  const [promoCopyNotice, setPromoCopyNotice] = useState('');
  const [chatImageViewer, setChatImageViewer] = useState({
    show: false,
    src: '',
    alt: '',
  });
  const [chatImageViewerDragY, setChatImageViewerDragY] = useState(0);
  const [chatMediaViewerTrackX, setChatMediaViewerTrackX] = useState(-33.333333);
  const [isChatMediaTrackDragging, setIsChatMediaTrackDragging] = useState(false);
  const [chatProductGallery, setChatProductGallery] = useState({
    show: false,
    images: [],
    activeIndex: 0,
    title: '',
  });
  const [chatProductChart, setChatProductChart] = useState({
    show: false,
    title: '',
    diapaz: [],
  });
  const [chatProductChartPeriod, setChatProductChartPeriod] = useState('all');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchTab, setSearchTab] = useState('messages');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchAuthor, setSearchAuthor] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchNextCursor, setSearchNextCursor] = useState(null);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [hasSearchCompleted, setHasSearchCompleted] = useState(false);
  const [mediaResults, setMediaResults] = useState([]);
  const [mediaNextCursor, setMediaNextCursor] = useState(null);
  const [isMediaLoading, setIsMediaLoading] = useState(false);
  const [mediaError, setMediaError] = useState('');
  const [hasMediaCompleted, setHasMediaCompleted] = useState(false);
  const [chatMediaViewer, setChatMediaViewer] = useState({
    show: false,
    index: 0,
  });
  const [contestState, setContestState] = useState(null);
  const [isContestCollapsed, setIsContestCollapsed] = useState(false);
  const [isContestDismissed, setIsContestDismissed] = useState(false);
  const [isContestInfoOpen, setIsContestInfoOpen] = useState(false);
  const [contestNowMs, setContestNowMs] = useState(Date.now());
  const [chatHistoryMode, setChatHistoryMode] = useState('live');
  const [isLoadingNewer, setIsLoadingNewer] = useState(false);
  const [hasMoreNewer, setHasMoreNewer] = useState(false);

  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const mountedRef = useRef(false);
  const chatStateRef = useRef(null);
  const chatScrollRef = useRef(null);
  const sellerProductsScrollRef = useRef(null);
  const composerRef = useRef(null);
  const chatImageViewerTouchRef = useRef({
    active: false,
    startX: 0,
    startY: 0,
    lastY: 0,
  });
  const chatMediaViewerTouchRef = useRef({
    active: false,
    startX: 0,
    startY: 0,
    lastX: 0,
  });
  const messagesRef = useRef([]);
  const lastMessageIdRef = useRef(0);
  const seenMessageIdRef = useRef(0);
  const autoScrollOnNextMessageRef = useRef(false);
  const initialViewportAppliedRef = useRef(false);
  const olderAnchorRef = useRef(null);
  const scrollRafRef = useRef(null);
  const readAckedIdRef = useRef(0);
  const readQueuedIdRef = useRef(0);
  const readInFlightRef = useRef(false);
  const readFlushTimerRef = useRef(null);
  const readWsAckTimerRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const initialHistoryLoadedRef = useRef(false);
  const olderRequestTimeoutRef = useRef(null);
  const isLoadingOlderRef = useRef(false);
  const historyRequestSeqRef = useRef(0);
  const activeHistoryRequestRef = useRef(null);
  const getWsTicketRef = useRef(getWsTicket);
  const userStatusRef = useRef(userInfo?.status);
  const myProfileLoadedRef = useRef(false);
  const subscriptionEndedRef = useRef(false);
  const deviceLimitReachedRef = useRef(false);
  const replyJumpHighlightTimerRef = useRef(null);
  const replyJumpLoadAttemptsRef = useRef(0);
  const selectionLockTimerRef = useRef(null);
  const touchSwipeRef = useRef({
    active: false,
    messageId: 0,
    startX: 0,
    startY: 0,
    currentOffset: 0,
    action: '',
    horizontalLocked: false,
    horizontal: false,
  });
  const deletingMessageTimersRef = useRef(new Map());
  const pendingEditMessageIdRef = useRef(0);
  const pendingSendRef = useRef(null);
  const pendingSendTimeoutRef = useRef(null);
  const ownSentClientMsgIdsRef = useRef(new Set());
  const ownSentClientMsgTimersRef = useRef(new Map());
  const sellerItemsLoadingRef = useRef(false);
  const sellerItemsRequestSeqRef = useRef(0);
  const sellerProductsScrollRafRef = useRef(null);
  const sellerCopyNoticeTimerRef = useRef(null);
  const chatNoticeTimerRef = useRef(null);
  const promoCopyNoticeTimerRef = useRef(null);
  const chatSearchRequestSeqRef = useRef(0);
  const chatMediaRequestSeqRef = useRef(0);
  const activeChatSearchRequestRef = useRef(null);
  const activeChatMediaRequestRef = useRef(null);
  const chatSearchDebounceRef = useRef(null);
  const chatAroundRequestSeqRef = useRef(0);
  const activeChatAroundRequestRef = useRef(null);
  const aroundMessageAlignTimerRef = useRef(null);
  const replyMentionSeenInFlightRef = useRef(new Set());
  const chatHistoryModeRef = useRef('live');
  const isLoadingNewerRef = useRef(false);
  const activeNewerRequestRef = useRef(null);
  const newerAutoScrollRef = useRef(false);
  const lastNewerRequestAtRef = useRef(0);
  const suppressContextNewerUntilRef = useRef(0);
  const suppressContextNewerTimerRef = useRef(null);
  const deferredLiveMessageIdsRef = useRef(new Set());
  const unreadContextRequestSeqRef = useRef(0);
  const activeUnreadContextRequestRef = useRef(null);
  const unreadContextLoadedRef = useRef(false);
  const returnToLatestRequestedRef = useRef(false);
  const unreadInitialCountRef = useRef(0);
  const unreadBoundaryIdRef = useRef(0);
  const firstUnreadMessageIdRef = useRef(0);
  const unreadConsumedIdsRef = useRef(new Set());

  const myUserId = Number(userInfo?.user_id || 0);
  const isDark = theme === 'dark';
  const activeContest = contestState?.contest || null;
  const hasActiveContest = Boolean(activeContest?.id);
  const contestStartMs = hasActiveContest ? Date.parse(String(activeContest.starts_at || '')) : NaN;
  const contestEndMs = hasActiveContest ? Date.parse(String(activeContest.ends_at || '')) : NaN;
  const contestPhase = !hasActiveContest
    ? 'none'
    : Number.isFinite(contestStartMs) && contestNowMs < contestStartMs
      ? 'upcoming'
      : Number.isFinite(contestEndMs) && contestNowMs >= contestEndMs
        ? 'ended'
        : 'running';
  const contestTimerText = contestPhase === 'upcoming'
    ? formatContestTimer(activeContest.starts_at, contestNowMs)
    : contestPhase === 'running'
      ? formatContestTimer(activeContest.ends_at, contestNowMs)
      : '';
  const contestStatusText = contestPhase === 'ended' ? 'Завершен' : contestTimerText;
  const contestTop = Array.isArray(contestState?.top) ? contestState.top.slice(0, 3) : [];
  const contestMe = contestState?.me || null;
  const shouldShowContestRanks = contestPhase === 'running' || contestPhase === 'ended';
  const contestEmptyText = contestPhase === 'upcoming'
    ? 'Скоро начнется'
    : contestPhase === 'ended'
      ? 'Победители пока не определены'
      : 'Станьте первым участником';
  const contestPrizes = Array.isArray(activeContest?.prizes) ? activeContest.prizes : [];
  const contestRules = Array.isArray(activeContest?.rules) ? activeContest.rules : [];
  const shouldShowContestPanel = hasActiveContest && !isContestDismissed;
  const contestSpace = shouldShowContestPanel ? 154 : 0;

  const hideChatNotice = useCallback(() => {
    if (chatNoticeTimerRef.current) {
      clearTimeout(chatNoticeTimerRef.current);
      chatNoticeTimerRef.current = null;
    }
    setChatNotice((prev) => (
      prev.show ? { ...prev, show: false } : prev
    ));
  }, []);

  const showChatNotice = useCallback((text, variant = 'info') => {
    const normalizedText = String(text || '').trim();
    if (!normalizedText) return;

    showGlassToast(normalizedText, {
      variant,
      duration: variant === 'error' ? 3200 : 2400,
    });
  }, []);

  const updateChatPushMode = useCallback(async (nextMode) => {
    const normalizedMode = String(nextMode || '').trim().toLowerCase();
    if (!['off', 'mentions', 'replies_mentions', 'all'].includes(normalizedMode)) {
      showChatNotice('Некорректный режим уведомлений', 'error');
      return;
    }
    if (normalizedMode === chatPushMode || isSavingChatPushMode) {
      setIsChatPushModalOpen(false);
      return;
    }

    if (normalizedMode !== 'off') {
      const pushToken = await ensurePushToken({ showInstructions: true });
      if (!pushToken) {
        return;
      }
    }

    try {
      setIsSavingChatPushMode(true);
      const response = await updatePublicProfile({ chat_push_mode: normalizedMode });
      const profile = response?.profile || {};
      const resolvedMode = String(
        profile?.chat_push_mode
        || (profile?.chat_notify_mentions_only ? 'mentions' : normalizedMode)
        || 'off'
      ).trim().toLowerCase();
      setChatPushMode(
        ['off', 'mentions', 'replies_mentions', 'all'].includes(resolvedMode)
          ? resolvedMode
          : normalizedMode
      );
      setIsChatPushModalOpen(false);
      showChatNotice(`Push чата: ${getChatPushModeLabel(resolvedMode || normalizedMode)}`, 'info');
    } catch (ex) {
      console.error('chat push mode update failed', ex);
      showChatNotice(ex?.message || 'Не удалось сохранить настройки push', 'error');
    } finally {
      setIsSavingChatPushMode(false);
    }
  }, [chatPushMode, ensurePushToken, isSavingChatPushMode, showChatNotice, updatePublicProfile]);

  const clearAttachedPhoto = useCallback(() => {
    setAttachedPhoto(null);
  }, []);

  useEffect(() => () => {
    if (promoCopyNoticeTimerRef.current) {
      clearTimeout(promoCopyNoticeTimerRef.current);
      promoCopyNoticeTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    const previewUrl = String(attachedPhoto?.previewUrl || '');
    if (!previewUrl.startsWith('blob:')) return undefined;
    return () => {
      try {
        URL.revokeObjectURL(previewUrl);
      } catch {
        // no-op
      }
    };
  }, [attachedPhoto?.previewUrl]);

  const clearEditingDraft = useCallback((options = {}) => {
    const shouldClearInput = options.clearInput !== false;
    pendingEditMessageIdRef.current = 0;
    setEditingMessage(null);
    setIsEditingMessageSaving(false);
    if (shouldClearInput) {
      setInputValue('');
    }
  }, []);

  const clearPendingSend = useCallback((clientMsgId = null) => {
    const pending = pendingSendRef.current;
    if (clientMsgId && pending?.clientMsgId && pending.clientMsgId !== clientMsgId) return null;

    if (pendingSendTimeoutRef.current) {
      clearTimeout(pendingSendTimeoutRef.current);
      pendingSendTimeoutRef.current = null;
    }
    pendingSendRef.current = null;
    setIsSendingMessage(false);
    return pending;
  }, []);

  const forgetOwnSentClientMsgId = useCallback((clientMsgId) => {
    const normalizedId = String(clientMsgId || '').trim();
    if (!normalizedId) return;

    ownSentClientMsgIdsRef.current.delete(normalizedId);
    const timer = ownSentClientMsgTimersRef.current.get(normalizedId);
    if (timer) {
      clearTimeout(timer);
      ownSentClientMsgTimersRef.current.delete(normalizedId);
    }
  }, []);

  const rememberOwnSentClientMsgId = useCallback((clientMsgId) => {
    const normalizedId = String(clientMsgId || '').trim();
    if (!normalizedId) return;

    ownSentClientMsgIdsRef.current.add(normalizedId);
    const prevTimer = ownSentClientMsgTimersRef.current.get(normalizedId);
    if (prevTimer) clearTimeout(prevTimer);

    const timer = setTimeout(() => {
      ownSentClientMsgTimersRef.current.delete(normalizedId);
      ownSentClientMsgIdsRef.current.delete(normalizedId);
    }, 20000);
    ownSentClientMsgTimersRef.current.set(normalizedId, timer);
  }, []);

  const clearOwnSentClientMsgIds = useCallback(() => {
    ownSentClientMsgTimersRef.current.forEach((timer) => clearTimeout(timer));
    ownSentClientMsgTimersRef.current.clear();
    ownSentClientMsgIdsRef.current.clear();
  }, []);

  const scheduleMessageDelete = useCallback((messageId, deletedAt = null) => {
    const deletedId = Number(messageId || 0);
    if (deletedId <= 0) return;

    setMessages((prev) => applyDeletedMessageToReplies(prev, deletedId, deletedAt));
    setDeletingMessageIds((prev) => (prev.includes(deletedId) ? prev : [...prev, deletedId]));
    setReplyTo((prev) => (Number(prev?.id || 0) === deletedId ? null : prev));
    setEditingMessage((prev) => {
      if (Number(prev?.id || 0) !== deletedId) return prev;
      pendingEditMessageIdRef.current = 0;
      setIsEditingMessageSaving(false);
      setInputValue('');
      return null;
    });

    if (deletingMessageTimersRef.current.has(deletedId)) return;

    const timer = setTimeout(() => {
      deletingMessageTimersRef.current.delete(deletedId);
      setMessages((prev) => prev.filter((msg) => Number(msg?.id || 0) !== deletedId));
      setDeletingMessageIds((prev) => prev.filter((id) => id !== deletedId));
    }, DELETE_ANIMATION_MS);

    deletingMessageTimersRef.current.set(deletedId, timer);
  }, []);

  useEffect(() => {
    subscriptionEndedRef.current = subscriptionEnded;
  }, [subscriptionEnded]);

  useEffect(() => {
    deviceLimitReachedRef.current = deviceLimitReached;
  }, [deviceLimitReached]);

  useEffect(() => {
    getWsTicketRef.current = getWsTicket;
  }, [getWsTicket]);

  useEffect(() => {
    userStatusRef.current = userInfo?.status;
  }, [userInfo?.status]);

  useEffect(() => {
    if (userInfo?.status === true) {
      setSubscriptionEnded(false);
      setDeviceLimitReached(false);
    }
  }, [userInfo?.status]);

  useEffect(() => {
    if (!hasActiveContest) return undefined;
    setIsContestDismissed(false);
    setContestNowMs(Date.now());
    const timer = setInterval(() => {
      setContestNowMs(Date.now());
    }, 1000);
    return () => clearInterval(timer);
  }, [hasActiveContest, activeContest?.id]);

  const lockChatBySubscription = useCallback((ended = true) => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    setSubscriptionEnded(Boolean(ended));
    setIsConnected(false);
    setIsLoading(false);
    setMessages([]);
    setChatState(null);
    setReplyTo(null);
    setEditingMessage(null);
    setIsEditingMessageSaving(false);
    setIsSendingMessage(false);
    setInputValue('');
    pendingEditMessageIdRef.current = 0;
    clearPendingSend();
    clearOwnSentClientMsgIds();
    clearOwnSentClientMsgIds();
    setMessageMenu({
      open: false,
      message: null,
      x: 0,
      y: 0,
    });
    setShowScrollDown(false);
    setNewBelowCount(0);
    setHighlightedMessageId(0);
    setPendingReplyJumpId(0);
    setIsLoadingOlder(false);
    setHasMoreOlder(false);
    setIsLoadingNewer(false);
    setHasMoreNewer(false);
    setChatHistoryMode('live');
    setErrorText('');
    hideChatNotice();
    isLoadingOlderRef.current = false;
    isLoadingNewerRef.current = false;
    activeHistoryRequestRef.current = null;
    activeNewerRequestRef.current = null;
    activeChatAroundRequestRef.current = null;
    activeUnreadContextRequestRef.current = null;
    unreadContextLoadedRef.current = false;
    returnToLatestRequestedRef.current = false;
    unreadInitialCountRef.current = 0;
    unreadBoundaryIdRef.current = 0;
    firstUnreadMessageIdRef.current = 0;
    unreadConsumedIdsRef.current.clear();
    replyMentionSeenInFlightRef.current.clear();
    setReplyMentionState({ count: 0, next_message_id: null });
    setIsReplyMentionLoading(false);
    deferredLiveMessageIdsRef.current.clear();
    autoScrollOnNextMessageRef.current = false;
    replyJumpLoadAttemptsRef.current = 0;
    if (aroundMessageAlignTimerRef.current) {
      clearTimeout(aroundMessageAlignTimerRef.current);
      aroundMessageAlignTimerRef.current = null;
    }
    if (readFlushTimerRef.current) {
      clearTimeout(readFlushTimerRef.current);
      readFlushTimerRef.current = null;
    }
    if (replyJumpHighlightTimerRef.current) {
      clearTimeout(replyJumpHighlightTimerRef.current);
      replyJumpHighlightTimerRef.current = null;
    }
    if (readWsAckTimerRef.current) {
      clearTimeout(readWsAckTimerRef.current);
      readWsAckTimerRef.current = null;
    }
    readInFlightRef.current = false;
  }, [clearOwnSentClientMsgIds, clearPendingSend, hideChatNotice]);

  const updatePresence = useCallback((payload) => {
    const online = Number(payload?.online_users);
    if (Number.isFinite(online) && online >= 0) {
      setOnlineUsers(online);
    }
  }, []);

  const countMessagesAfterId = useCallback((afterId, list = messages) => {
    const normalizedAfter = Number(afterId || 0);
    return (list || []).reduce((acc, msg) => {
      const id = Number(msg?.id || 0);
      return id > normalizedAfter ? acc + 1 : acc;
    }, 0);
  }, [messages]);

  const isNearBottom = useCallback((node) => {
    if (!node) return true;
    return node.scrollHeight - node.scrollTop - node.clientHeight <= BOTTOM_THRESHOLD_PX;
  }, []);

  const getHighestVisibleMessageId = useCallback(() => {
    const container = chatScrollRef.current;
    if (!container) return 0;

    const containerRect = container.getBoundingClientRect();
    const sampleX = Math.max(
      containerRect.left + 16,
      Math.min(containerRect.right - 16, containerRect.left + (containerRect.width * 0.35))
    );
    const bottomLimit = containerRect.bottom - Math.max(24, composerHeight + 8);
    const sampleYs = [
      containerRect.top + 96,
      containerRect.top + (containerRect.height * 0.45),
      Math.max(containerRect.top + 96, bottomLimit),
    ];

    for (const sampleY of sampleYs) {
      if (sampleY < containerRect.top || sampleY > containerRect.bottom) continue;
      const elements = document.elementsFromPoint(sampleX, sampleY);
      const row = elements.find((el) => el?.dataset?.msgId);
      const id = Number(row?.dataset?.msgId || 0);
      if (id > 0) return id;
    }

    const rows = Array.from(container.querySelectorAll('[data-msg-id]'));
    for (const row of rows) {
      const rect = row.getBoundingClientRect();
      if (rect.bottom > containerRect.top + 80) {
        const id = Number(row.dataset?.msgId || 0);
        if (id > 0) return id;
      }
    }

    return 0;
  }, [composerHeight]);

  const suppressContextNewerAfterProgrammaticScroll = useCallback((durationMs = 700) => {
    const timeoutMs = Math.max(120, Number(durationMs || 0));
    suppressContextNewerUntilRef.current = Date.now() + timeoutMs;
    if (suppressContextNewerTimerRef.current) {
      window.clearTimeout(suppressContextNewerTimerRef.current);
    }
    suppressContextNewerTimerRef.current = window.setTimeout(() => {
      suppressContextNewerTimerRef.current = null;
      if (Date.now() >= suppressContextNewerUntilRef.current) {
        suppressContextNewerUntilRef.current = 0;
      }
    }, timeoutMs + 40);
  }, []);

  const scrollToBottom = useCallback((behavior = 'smooth') => {
    const container = chatScrollRef.current;
    if (!container) return;
    container.scrollTo({
      top: container.scrollHeight,
      behavior,
    });
    const lastId = Number(lastMessageIdRef.current || 0);
    if (lastId) {
      seenMessageIdRef.current = lastId;
    }
    setShowScrollDown(false);
    setNewBelowCount(0);
  }, []);

  const markMessageAsJumpTarget = useCallback((messageId) => {
    const normalized = Number(messageId || 0);
    if (!normalized) return;
    setHighlightedMessageId(normalized);
    if (replyJumpHighlightTimerRef.current) {
      clearTimeout(replyJumpHighlightTimerRef.current);
    }
    replyJumpHighlightTimerRef.current = setTimeout(() => {
      replyJumpHighlightTimerRef.current = null;
      setHighlightedMessageId((prev) => (prev === normalized ? 0 : prev));
    }, 1600);
  }, []);

  const scrollToMessageById = useCallback((messageId, behavior = 'smooth') => {
    const targetId = Number(messageId || 0);
    if (!targetId) return false;
    const container = chatScrollRef.current;
    if (!container) return false;

    const row = container.querySelector(`[data-msg-id="${targetId}"]`);
    if (!row) return false;

    const targetTop = row.offsetTop - Math.max(96, Math.floor(container.clientHeight * 0.28));
    container.scrollTo({
      top: Math.max(0, targetTop),
      behavior,
    });
    markMessageAsJumpTarget(targetId);
    return true;
  }, [markMessageAsJumpTarget]);

  const scheduleAroundMessageAlignment = useCallback((messageId, behavior = 'auto') => {
    const targetId = Number(messageId || 0);
    if (!targetId) return;

    if (aroundMessageAlignTimerRef.current) {
      clearTimeout(aroundMessageAlignTimerRef.current);
      aroundMessageAlignTimerRef.current = null;
    }

    let passesLeft = 6;
    let highlighted = false;

    const align = () => {
      const container = chatScrollRef.current;
      const row = container?.querySelector?.(`[data-msg-id="${targetId}"]`);
      if (container && row) {
        const targetTop = row.offsetTop - Math.max(96, Math.floor(container.clientHeight * 0.28));
        container.scrollTo({
          top: Math.max(0, targetTop),
          behavior: highlighted ? 'auto' : behavior,
        });
        if (!highlighted) {
          markMessageAsJumpTarget(targetId);
          highlighted = true;
        }
      }

      passesLeft -= 1;
      if (passesLeft <= 0) {
        aroundMessageAlignTimerRef.current = null;
        return;
      }

      aroundMessageAlignTimerRef.current = setTimeout(() => {
        requestAnimationFrame(align);
      }, 70);
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(align);
    });
  }, [markMessageAsJumpTarget]);

  const applyReplyMentionState = useCallback((state) => {
    replyMentionSeenInFlightRef.current.clear();
    setIsReplyMentionLoading(false);
    const count = Math.max(0, Number(state?.count || 0));
    const nextMessageId = Number(state?.next_message_id || 0);
    setReplyMentionState({
      count,
      next_message_id: nextMessageId > 0 ? nextMessageId : null,
    });
  }, []);

  const markReplyMentionSeen = useCallback((messageId) => {
    const targetId = Number(messageId || 0);
    if (!targetId || replyMentionSeenInFlightRef.current.has(targetId)) return false;
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;

    replyMentionSeenInFlightRef.current.add(targetId);
    try {
      ws.send(JSON.stringify({
        type: 'chat:reply_mentions:seen',
        message_id: targetId,
      }));
      return true;
    } catch (ex) {
      replyMentionSeenInFlightRef.current.delete(targetId);
      console.error('chat reply mention seen send failed', ex);
      return false;
    }
  }, []);

  const isReplyToMe = useCallback((message) => {
    if (!myUserId) return false;
    if (Number(message?.author_account_id || 0) === myUserId) return false;
    const replyPayload = message?.reply_to || null;
    if (!replyPayload || replyPayload.deleted || replyPayload.deleted_at) return false;
    const replyAuthor = replyPayload.author || null;
    const replyAuthorId = Number(replyAuthor?.account_id || replyPayload.author_account_id || 0);
    return replyAuthorId === myUserId;
  }, [myUserId]);

  const getSearchProductName = useCallback((message) => {
    const embed = message?.meta?.embed;
    if (!embed || embed.kind !== 'product') return '';
    return String(embed.snapshot?.name || '').trim();
  }, []);

  const getSearchPreview = useCallback((message) => {
    const body = String(message?.body || '').trim();
    if (body) return body.length > 160 ? `${body.slice(0, 160)}...` : body;
    const productName = getSearchProductName(message);
    if (productName) return productName.length > 160 ? `${productName.slice(0, 160)}...` : productName;
    if (message?.photo_url || message?.photo_thumb_url) return 'Фото';
    return 'Сообщение';
  }, [getSearchProductName]);

  const requestChatSearch = useCallback(({ append = false, cursor = null } = {}) => {
    const ws = wsRef.current;
    const query = String(searchQuery || '').trim();
    const author = normalizeHandle(String(searchAuthor || '').replace(/^@+/, ''));
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      setSearchError('Нет соединения с чатом');
      return false;
    }
    if (query.length < 2 && !author) {
      activeChatSearchRequestRef.current = null;
      setSearchResults([]);
      setSearchNextCursor(null);
      setSearchError('');
      setHasSearchCompleted(false);
      setIsSearchLoading(false);
      return false;
    }

    const requestId = `chat_search_${Date.now()}_${chatSearchRequestSeqRef.current += 1}`;
    activeChatSearchRequestRef.current = {
      requestId,
      append,
      query,
      author,
    };
    setIsSearchLoading(true);
    setSearchError('');
    if (!append) {
      setHasSearchCompleted(false);
    }

    const payload = {
      type: 'chat:search',
      request_id: requestId,
      query,
      limit: CHAT_SEARCH_PAGE_SIZE,
    };
    if (author) payload.author_handle = author;
    if (cursor) payload.before_id = cursor;

    try {
      ws.send(JSON.stringify(payload));
      return true;
    } catch (ex) {
      console.error('chat search send failed', ex);
      activeChatSearchRequestRef.current = null;
      setIsSearchLoading(false);
      setSearchError('Не удалось запустить поиск');
      return false;
    }
  }, [searchAuthor, searchQuery]);


  const requestChatMedia = useCallback(({ append = false, cursor = null } = {}) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      setMediaError('Нет соединения с чатом');
      setIsMediaLoading(false);
      return false;
    }

    const requestId = `chat_media_${Date.now()}_${chatMediaRequestSeqRef.current += 1}`;
    activeChatMediaRequestRef.current = {
      requestId,
      append: Boolean(append),
    };
    setIsMediaLoading(true);
    setMediaError('');
    if (!append) {
      setHasMediaCompleted(false);
      setMediaResults([]);
      setMediaNextCursor(null);
    }

    try {
      ws.send(JSON.stringify({
        type: 'chat:media',
        request_id: requestId,
        limit: 36,
        before_id: cursor || null,
      }));
      return true;
    } catch (ex) {
      console.error('chat media send failed', ex);
      activeChatMediaRequestRef.current = null;
      setIsMediaLoading(false);
      setMediaError('Не удалось загрузить медиа');
      return false;
    }
  }, []);

  const requestNewerFromContext = useCallback((options = {}) => {
    if (!['search_context', 'unread_context'].includes(chatHistoryModeRef.current)) return false;
    if (isLoadingNewerRef.current || !hasMoreNewer) return false;

    const ws = wsRef.current;
    const list = Array.isArray(messagesRef.current) ? messagesRef.current : [];
    const lastId = Number(list[list.length - 1]?.id || 0);
    if (!ws || ws.readyState !== WebSocket.OPEN || !lastId) return false;

    const now = Date.now();
    const minDelayMs = Math.max(0, Number(options.minDelayMs ?? 180));
    if (minDelayMs > 0 && now - Number(lastNewerRequestAtRef.current || 0) < minDelayMs) {
      return false;
    }
    lastNewerRequestAtRef.current = now;

    activeNewerRequestRef.current = { afterId: lastId };
    newerAutoScrollRef.current = Boolean(options.autoScroll);
    isLoadingNewerRef.current = true;
    setIsLoadingNewer(true);

    try {
      ws.send(JSON.stringify({
        type: 'chat:sync',
        after_id: lastId,
        limit: PAGE_SIZE,
      }));
      return true;
    } catch (ex) {
      console.error('chat newer sync send failed', ex);
      activeNewerRequestRef.current = null;
      newerAutoScrollRef.current = false;
      isLoadingNewerRef.current = false;
      setIsLoadingNewer(false);
      return false;
    }
  }, [hasMoreNewer]);

  const requestUnreadContext = useCallback(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;

    const requestId = `chat_unread_${Date.now()}_${unreadContextRequestSeqRef.current += 1}`;
    activeUnreadContextRequestRef.current = { requestId };

    try {
      ws.send(JSON.stringify({
        type: 'chat:unread_context',
        request_id: requestId,
        before: 12,
        after: Math.max(PAGE_SIZE, 50),
      }));
      console.debug('[chat:ws] unread_context:request', { request_id: requestId });
      return true;
    } catch (ex) {
      activeUnreadContextRequestRef.current = null;
      console.error('chat unread context send failed', ex);
      return false;
    }
  }, []);

  const resetUnreadLocalProgress = useCallback((lastReadId = 0, unreadCount = 0) => {
    unreadBoundaryIdRef.current = Number(lastReadId || 0);
    unreadInitialCountRef.current = Math.max(0, Number(unreadCount || 0));
    unreadConsumedIdsRef.current.clear();
    setNewBelowCount(unreadInitialCountRef.current);
    setShowScrollDown(unreadInitialCountRef.current > 0);
  }, []);

  const consumeUnreadThrough = useCallback((targetId) => {
    const normalizedTarget = Number(targetId || 0);
    if (!normalizedTarget || chatHistoryModeRef.current !== 'unread_context') return;

    const boundaryId = Number(unreadBoundaryIdRef.current || 0);
    const initialUnread = Math.max(0, Number(unreadInitialCountRef.current || 0));
    if (!boundaryId || !initialUnread) return;

    const consumed = unreadConsumedIdsRef.current;
    const list = Array.isArray(messagesRef.current) ? messagesRef.current : [];
    for (const msg of list) {
      const id = Number(msg?.id || 0);
      if (!id || id <= boundaryId || id > normalizedTarget) continue;
      if (Number(msg?.author_account_id || 0) === myUserId) continue;
      consumed.add(id);
    }

    const nextUnread = Math.max(0, initialUnread - consumed.size);
    setNewBelowCount(nextUnread);
    setShowScrollDown(true);
    setChatState((prev) => ({
      ...(prev || {}),
      unread_count: nextUnread,
      has_unread: nextUnread > 0,
    }));
  }, [myUserId]);

  const requestAroundMessage = useCallback((messageId, options = {}) => {
    const targetId = Number(messageId || 0);
    if (!targetId) return false;
    if (scrollToMessageById(targetId, 'smooth')) {
      if (!options.preserveSearch) {
        setIsSearchOpen(false);
      }
      if (options.source === 'reply_mention') {
        if (!markReplyMentionSeen(targetId)) {
          setIsReplyMentionLoading(false);
        }
      }
      return true;
    }
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      showChatNotice('Нет соединения с чатом', 'error');
      return false;
    }
    const requestId = `chat_around_${Date.now()}_${chatAroundRequestSeqRef.current += 1}`;
    activeChatAroundRequestRef.current = {
      requestId,
      targetId,
      source: options.source || '',
      preserveSearch: Boolean(options.preserveSearch),
    };
    try {
      ws.send(JSON.stringify({
        type: 'chat:around_message',
        request_id: requestId,
        message_id: targetId,
      }));
      return true;
    } catch (ex) {
      console.error('chat around send failed', ex);
      activeChatAroundRequestRef.current = null;
      showChatNotice('Не удалось открыть сообщение', 'error');
      return false;
    }
  }, [markReplyMentionSeen, scrollToMessageById, showChatNotice]);

  const handleReplyMentionClick = useCallback(() => {
    const targetId = Number(replyMentionState?.next_message_id || 0);
    if (!targetId || isReplyMentionLoading) return;
    setIsReplyMentionLoading(true);
    const started = requestAroundMessage(targetId, { source: 'reply_mention' });
    if (!started) {
      setIsReplyMentionLoading(false);
    }
  }, [isReplyMentionLoading, replyMentionState?.next_message_id, requestAroundMessage]);

  const focusComposerInput = useCallback((options = {}) => {
    const doFocus = () => {
      const composer = composerRef.current;
      if (composer && typeof composer.focusInput === 'function') {
        composer.focusInput();
        return;
      }

      const input = document.querySelector('.chat-composer-input');
      if (input && typeof input.focus === 'function') {
        input.focus();
        const nextPosition = String(input.value || '').length;
        if (typeof input.setSelectionRange === 'function') {
          input.setSelectionRange(nextPosition, nextPosition);
        }
      }
    };

    doFocus();

    if (options.repeatAfterFrame) {
      window.requestAnimationFrame(doFocus);
    }
  }, []);

  const handlePickPhoto = useCallback((file) => {
    if (!file) return;
    if (isEditingMessageSaving || isSendingMessage || isUploadingPhoto || editingMessage) return;
    if (!String(file.type || '').toLowerCase().startsWith('image/')) {
      showChatNotice('Можно прикрепить только изображение', 'error');
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    setAttachedPhoto({
      file,
      previewUrl,
      fileName: String(file.name || 'image.jpg'),
      photoMediaId: null,
      photoThumbMediaId: null,
      photoUrl: null,
      photoThumbUrl: null,
    });
    focusComposerInput({ repeatAfterFrame: true });
  }, [editingMessage, focusComposerInput, isEditingMessageSaving, isSendingMessage, isUploadingPhoto, showChatNotice]);

  const clearLongPressTimer = useCallback(() => {
    if (!longPressTimerRef.current) return;
    clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = null;
  }, []);

  const clearNativeSelection = useCallback(() => {
    try {
      const selection = window.getSelection?.();
      if (selection && selection.rangeCount > 0) {
        selection.removeAllRanges();
      }
    } catch {
      // no-op
    }
  }, []);

  const lockNativeSelection = useCallback(() => {
    clearNativeSelection();
    const root = document.documentElement;
    root.classList.add('chat-native-selection-blocked');

    if (selectionLockTimerRef.current) {
      clearTimeout(selectionLockTimerRef.current);
    }

    selectionLockTimerRef.current = setTimeout(() => {
      selectionLockTimerRef.current = null;
      root.classList.remove('chat-native-selection-blocked');
      clearNativeSelection();
    }, SELECTION_LOCK_MS);
  }, [clearNativeSelection]);

  const resetSwipePreview = useCallback(() => {
    setSwipePreview((prev) => {
      if (!prev.messageId && !prev.offsetX && !prev.action && !prev.dragging) return prev;
      return {
        messageId: 0,
        offsetX: 0,
        action: '',
        dragging: false,
      };
    });
  }, []);

  const resetTouchSwipe = useCallback(() => {
    touchSwipeRef.current = {
      active: false,
      messageId: 0,
      startX: 0,
      startY: 0,
      currentOffset: 0,
      action: '',
      horizontalLocked: false,
      horizontal: false,
    };
  }, []);

  const computeSwipeOffset = useCallback((msg, rawOffset) => {
    const isMine = Number(msg?.author_account_id || 0) === myUserId;
    let adjusted = Number(rawOffset || 0);
    if (!isMine && adjusted < 0) {
      adjusted = 0;
    }
    const minValue = isMine ? -SWIPE_MAX_OFFSET_PX : 0;
    return Math.max(minValue, Math.min(SWIPE_MAX_OFFSET_PX, adjusted));
  }, [myUserId]);

  const computeSwipeAction = useCallback((msg, offsetX) => {
    const isMine = Number(msg?.author_account_id || 0) === myUserId;
    const normalized = Number(offsetX || 0);
    if (isMine) {
      if (normalized >= SWIPE_DELETE_TRIGGER_PX) return 'delete';
      if (normalized <= -SWIPE_REPLY_TRIGGER_PX) return 'reply';
      return '';
    }
    if (normalized >= SWIPE_REPLY_TRIGGER_PX) return 'reply';
    return '';
  }, [myUserId]);

  const closeMessageMenu = useCallback(() => {
    setMessageMenu((prev) => ({ ...prev, open: false, message: null, mobile: false }));
  }, []);

  const openMessageMenuAt = useCallback((msg, anchor = {}) => {
    if (!msg) return;
    lockNativeSelection();
    resetSwipePreview();
    resetTouchSwipe();

    const margin = 10;
    const isActionMenu = true;
    const menuWidth = Math.min(302, Math.max(236, window.innerWidth - 32));
    const menuHeight = Number(msg?.author_account_id || 0) === myUserId ? 190 : 108;
    const composerBlockHeight = Math.max(0, Number(composerHeight || 0));
    const bottomReserved = composerBlockHeight + 16;
    const targetEl = anchor?.targetEl || null;
    let targetRect = null;
    let x = Number(anchor?.x);
    let y = Number(anchor?.y);

    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      if (targetEl && typeof targetEl.getBoundingClientRect === 'function') {
        targetRect = targetEl.getBoundingClientRect();
        x = targetRect.left + (targetRect.width * 0.5);
        y = targetRect.top + Math.min(targetRect.height, 24);
      } else {
        x = window.innerWidth * 0.5;
        y = window.innerHeight * 0.5;
      }
    } else if (targetEl && typeof targetEl.getBoundingClientRect === 'function') {
      targetRect = targetEl.getBoundingClientRect();
    }

    let placement = 'below';

    if (isActionMenu) {
      const viewportWidth = window.innerWidth || 360;
      const viewportHeight = window.innerHeight || 640;
      const safeTop = 14;
      const availableBottom = viewportHeight - bottomReserved - margin;
      const rect = targetRect || {
        left: Math.max(margin, x - 120),
        top: y,
        bottom: y + 32,
        width: 240,
      };
      const centerX = rect.left + (rect.width * 0.5);
      const belowY = rect.bottom + 12;
      const aboveY = rect.top - menuHeight - 12;
      placement = belowY + menuHeight <= availableBottom || aboveY < safeTop ? 'below' : 'above';
      x = Math.max(16, Math.min(centerX - (menuWidth * 0.5), viewportWidth - menuWidth - 16));
      y = placement === 'below'
        ? Math.min(belowY, availableBottom - menuHeight)
        : Math.max(safeTop, aboveY);

      if (navigator.vibrate) {
        try {
          navigator.vibrate(12);
        } catch {
          // no-op
        }
      }
    }

    const maxX = Math.max(margin, window.innerWidth - menuWidth - margin);
    const maxY = Math.max(margin, window.innerHeight - bottomReserved - menuHeight - margin);
    const clampedX = Math.max(margin, Math.min(x, maxX));
    const clampedY = Math.max(margin, Math.min(y, maxY));

    setMessageMenu({
      open: true,
      message: msg,
      x: clampedX,
      y: clampedY,
      mobile: isActionMenu,
      placement,
    });
  }, [composerHeight, lockNativeSelection, myUserId, resetSwipePreview, resetTouchSwipe]);

  const scheduleLongPress = useCallback((msg, anchor = {}) => {
    clearLongPressTimer();
    longPressTimerRef.current = setTimeout(() => {
      longPressTimerRef.current = null;
      openMessageMenuAt(msg, anchor);
    }, LONG_PRESS_MS);
  }, [clearLongPressTimer, openMessageMenuAt]);

  const handleMessagePointerDown = useCallback((event, msg) => {
    if (!msg) return;
    if (event.button !== undefined && event.button !== 0) return;
    if (event.target?.closest('button,input,textarea,a')) return;
    scheduleLongPress(msg, {
      targetEl: event.currentTarget,
      x: event.clientX,
      y: event.clientY,
      pointerType: event.pointerType,
    });
  }, [scheduleLongPress]);

  const handleMessageTouchStart = useCallback((event, msg) => {
    if (!msg) return;
    if (event.target?.closest('button,input,textarea,a')) return;
    const touch = event.touches?.[0];
    if (!touch) return;
    resetTouchSwipe();
    touchSwipeRef.current = {
      active: true,
      messageId: Number(msg?.id || 0),
      startX: touch.clientX,
      startY: touch.clientY,
      currentOffset: 0,
      action: '',
      horizontalLocked: false,
      horizontal: false,
    };
    resetSwipePreview();
    scheduleLongPress(msg, {
      targetEl: event.currentTarget,
      x: touch.clientX,
      y: touch.clientY,
      mobile: true,
      pointerType: 'touch',
    });
  }, [resetSwipePreview, resetTouchSwipe, scheduleLongPress]);

  const handleMessageTouchMove = useCallback((event, msg) => {
    if (!msg) return;
    const messageId = Number(msg?.id || 0);
    const swipe = touchSwipeRef.current;
    if (!swipe.active || swipe.messageId !== messageId) return;

    const touch = event.touches?.[0];
    if (!touch) return;

    const dxRaw = touch.clientX - swipe.startX;
    const dyRaw = touch.clientY - swipe.startY;
    const absX = Math.abs(dxRaw);
    const absY = Math.abs(dyRaw);

    if (!swipe.horizontalLocked) {
      if (absX < SWIPE_LOCK_THRESHOLD_PX && absY < SWIPE_LOCK_THRESHOLD_PX) return;
      swipe.horizontalLocked = true;
      if (absX > absY + SWIPE_DIRECTION_DOMINANCE_PX) {
        swipe.horizontal = true;
      } else {
        swipe.horizontal = false;
      }
      clearLongPressTimer();
    }

    if (!swipe.horizontal) return;

    if (event.cancelable) {
      event.preventDefault();
    }

    clearLongPressTimer();
    const offsetX = computeSwipeOffset(msg, dxRaw);
    const action = computeSwipeAction(msg, offsetX);
    swipe.currentOffset = offsetX;
    swipe.action = action;

    setSwipePreview({
      messageId,
      offsetX,
      action,
      dragging: true,
    });
  }, [clearLongPressTimer, computeSwipeAction, computeSwipeOffset]);

  const handleMessageTouchEnd = useCallback((_event, msg) => {
    if (!msg) {
      resetTouchSwipe();
      resetSwipePreview();
      clearLongPressTimer();
      return;
    }

    const messageId = Number(msg?.id || 0);
    const swipe = touchSwipeRef.current;

    clearLongPressTimer();

    if (swipe.active && swipe.messageId === messageId && swipe.horizontal) {
      const action = swipe.action || computeSwipeAction(msg, swipe.currentOffset);
      if (action === 'reply') {
        clearEditingDraft();
        setReplyTo(msg);
        focusComposerInput({ repeatAfterFrame: true });
      } else if (action === 'delete') {
        const confirmed = window.confirm('Удалить сообщение?');
        if (confirmed) {
          if (Number(msg?.author_account_id || 0) === myUserId) {
            const ws = wsRef.current;
            if (!ws || ws.readyState !== WebSocket.OPEN) {
              setErrorText('Нет соединения с чатом');
            } else {
              ws.send(JSON.stringify({
                type: 'chat:delete',
                message_id: messageId,
              }));
              console.debug('[chat:ws] delete:request', { message_id: messageId });
            }
          }
        }
      }
    }

    clearNativeSelection();
    resetTouchSwipe();
    resetSwipePreview();
  }, [clearEditingDraft, clearLongPressTimer, clearNativeSelection, computeSwipeAction, focusComposerInput, myUserId, resetSwipePreview, resetTouchSwipe]);

  const handleMessageTouchCancel = useCallback(() => {
    clearLongPressTimer();
    clearNativeSelection();
    resetTouchSwipe();
    resetSwipePreview();
  }, [clearLongPressTimer, clearNativeSelection, resetSwipePreview, resetTouchSwipe]);

  const handleMessageContextMenu = useCallback((event, msg) => {
    if (!msg) return;
    event.preventDefault();
    clearLongPressTimer();
    clearNativeSelection();
    openMessageMenuAt(msg, {
      targetEl: event.currentTarget,
      x: event.clientX,
      y: event.clientY,
    });
  }, [clearLongPressTimer, clearNativeSelection, openMessageMenuAt]);

  const handleMenuReply = useCallback((msg) => {
    if (!msg) return;
    closeMessageMenu();
    clearEditingDraft();
    setReplyTo(msg);
    focusComposerInput();
  }, [clearEditingDraft, closeMessageMenu, focusComposerInput]);

  const handleMenuEdit = useCallback((msg) => {
    const messageId = Number(msg?.id || 0);
    if (!messageId || Number(msg?.author_account_id || 0) !== myUserId) {
      closeMessageMenu();
      return;
    }

    setReplyTo(null);
    clearAttachedPhoto();
    pendingEditMessageIdRef.current = 0;
    setIsEditingMessageSaving(false);
    setEditingMessage(msg);
    setInputValue(String(msg.body || ''));
    closeMessageMenu();

    focusComposerInput();
  }, [clearAttachedPhoto, closeMessageMenu, focusComposerInput, myUserId]);

  const handleMenuCopy = useCallback(async (msg) => {
    const text = String(msg?.body || '').trim();
    if (!text) {
      closeMessageMenu();
      return;
    }
    try {
      await copyTextToClipboard(text);
      console.debug('[chat:ui] copied message', { message_id: msg?.id || null });
    } catch (ex) {
      console.error('copy message failed', ex);
      setErrorText('Не удалось скопировать сообщение');
    } finally {
      closeMessageMenu();
    }
  }, [closeMessageMenu]);

  const handleMenuDelete = useCallback((msg) => {
    const messageId = Number(msg?.id || 0);
    if (!messageId) {
      closeMessageMenu();
      return;
    }
    if (Number(msg?.author_account_id || 0) !== myUserId) {
      closeMessageMenu();
      return;
    }
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      setErrorText('Нет соединения с чатом');
      closeMessageMenu();
      return;
    }
    ws.send(JSON.stringify({
      type: 'chat:delete',
      message_id: messageId,
    }));
    console.debug('[chat:ws] delete:request', { message_id: messageId });
    closeMessageMenu();
  }, [closeMessageMenu, myUserId]);

  const handleMenuReaction = useCallback((msg, reactionKey) => {
    const messageId = Number(msg?.id || 0);
    const normalizedKey = String(reactionKey || '').trim().toLowerCase();
    if (!messageId || !CHAT_REACTION_KEYS.has(normalizedKey)) {
      closeMessageMenu();
      return;
    }

    const currentMine = (Array.isArray(msg?.reactions) ? msg.reactions : [])
      .find((entry) => entry?.mine);
    const nextReactionKey = currentMine?.key === normalizedKey ? null : normalizedKey;
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      showChatNotice('Нет соединения с чатом', 'error');
      closeMessageMenu();
      return;
    }

    ws.send(JSON.stringify({
      type: 'chat:reaction:set',
      message_id: messageId,
      reaction_key: nextReactionKey,
    }));
    setMessages((prev) => applyOptimisticReactionToMessages(prev, messageId, nextReactionKey));
    setReplyTo((prev) => (
      Number(prev?.id || 0) === messageId
        ? applyOptimisticReactionToMessages([prev], messageId, nextReactionKey)[0] || prev
        : prev
    ));
    closeMessageMenu();
  }, [closeMessageMenu, showChatNotice]);

  const renderMessageBody = useCallback((messagePayload, options = {}) => {
    const body = String(messagePayload?.body || '');
    if (!body) return '';
    const {
      currentUserHandle = '',
      onMentionClick = null,
    } = options || {};
    const lines = body.split('\n');

    return lines.map((line, lineIndex) => {
      MESSAGE_TOKEN_RE.lastIndex = 0;
      const parts = [];
      let lastIndex = 0;
      let match;

      while ((match = MESSAGE_TOKEN_RE.exec(line)) !== null) {
        const matchText = String(match[0] || '');
        const matchUrl = String(match[1] || '');
        const matchHandle = String(match[3] || '');
        const start = Number(match.index || 0);
        const end = start + matchText.length;
        const charBefore = start > 0 ? line[start - 1] : '';
        const mentionBoundaryOk = !/[A-Za-z0-9_]/.test(charBefore);

        if (start > lastIndex) {
          parts.push(line.slice(lastIndex, start));
        }

        if (matchUrl) {
          const href = /^https?:\/\//i.test(matchText) ? matchText : `https://${matchText}`;
          parts.push(
            <a
              key={`lnk-${lineIndex}-${start}`}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="chat-msg-link"
            >
              {matchText}
            </a>
          );
        } else if (matchHandle && mentionBoundaryOk) {
          const normalizedHandle = normalizeHandle(matchHandle);
          const mentionClass = `chat-msg-mention ${normalizedHandle && normalizedHandle === currentUserHandle ? 'chat-msg-mention--mine' : ''}`;
          if (normalizedHandle && typeof onMentionClick === 'function') {
            parts.push(
              <button
                key={`mnt-${lineIndex}-${start}`}
                type="button"
                className={mentionClass}
                onClick={(event) => onMentionClick(event, normalizedHandle)}
              >
                @{matchHandle}
              </button>
            );
          } else {
            parts.push(
              <span key={`mnt-${lineIndex}-${start}`} className={mentionClass}>
                @{matchHandle}
              </span>
            );
          }
        } else {
          parts.push(matchText);
        }
        lastIndex = end;
      }

      if (lastIndex < line.length) {
        parts.push(line.slice(lastIndex));
      }

      return (
        <React.Fragment key={`line-${lineIndex}`}>
          {parts}
          {lineIndex < lines.length - 1 && <br />}
        </React.Fragment>
      );
    });
  }, []);

  const flushReadState = useCallback(async () => {
    if (readInFlightRef.current) return;

    const queuedId = Number(readQueuedIdRef.current || 0);
    const ackedId = Number(readAckedIdRef.current || 0);
    if (!queuedId || queuedId <= ackedId) return;

    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      if (readFlushTimerRef.current) return;
      readFlushTimerRef.current = setTimeout(() => {
        readFlushTimerRef.current = null;
        void flushReadState();
      }, 800);
      return;
    }

    readInFlightRef.current = true;
    try {
      ws.send(JSON.stringify({
        type: 'chat:read',
        last_read_message_id: queuedId,
      }));

      if (readWsAckTimerRef.current) {
        clearTimeout(readWsAckTimerRef.current);
      }
      readWsAckTimerRef.current = setTimeout(() => {
        readWsAckTimerRef.current = null;
        readInFlightRef.current = false;
        const pending = Number(readQueuedIdRef.current || 0);
        const acked = Number(readAckedIdRef.current || 0);
        if (pending > acked) {
          if (readFlushTimerRef.current) clearTimeout(readFlushTimerRef.current);
          readFlushTimerRef.current = setTimeout(() => {
            readFlushTimerRef.current = null;
            void flushReadState();
          }, 180);
        }
      }, 1400);
    } catch (ex) {
      readInFlightRef.current = false;
      console.error('chat:read ws send failed', ex);
    }
  }, []);

  const queueReadState = useCallback((targetId, { immediate = false, delayMs = 480 } = {}) => {
    const normalized = Number(targetId || 0);
    if (!normalized) return;

    if (normalized > readQueuedIdRef.current) {
      readQueuedIdRef.current = normalized;
    }
    if (readQueuedIdRef.current <= readAckedIdRef.current) return;

    if (immediate) {
      if (readFlushTimerRef.current) {
        clearTimeout(readFlushTimerRef.current);
        readFlushTimerRef.current = null;
      }
      void flushReadState();
      return;
    }

    if (readFlushTimerRef.current) return;
    readFlushTimerRef.current = setTimeout(() => {
      readFlushTimerRef.current = null;
      void flushReadState();
    }, delayMs);
  }, [flushReadState]);

  const shouldSendReadReceipt = useCallback((targetId) => {
    const target = Number(targetId || 0);
    if (!target) return false;

    const ackedId = Number(readAckedIdRef.current || 0);
    if (target <= ackedId) return false;

    const unreadFromState = Number(chatState?.unread_count || 0);
    if (unreadFromState > 0) return true;

    return (messages || []).some((msg) => {
      const id = Number(msg?.id || 0);
      if (!id || id <= ackedId || id > target) return false;
      return Number(msg?.author_account_id || 0) !== myUserId;
    });
  }, [chatState?.unread_count, messages, myUserId]);

  const syncReadState = useCallback((targetId = null, options = {}) => {
    const lastId = Number(targetId || messages[messages.length - 1]?.id || 0);
    if (!lastId) return;
    if (!shouldSendReadReceipt(lastId)) return;
    queueReadState(lastId, options);
  }, [messages, queueReadState, shouldSendReadReceipt]);

  const requestSubscribe = useCallback(({ beforeId = null, limit = PAGE_SIZE, mode = null } = {}) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;

    const normalizedBeforeId = beforeId !== null && beforeId !== undefined
      ? Number(beforeId)
      : null;
    const requestMode = mode || (normalizedBeforeId ? 'older' : 'initial');
    const requestId = `chat_history_${Date.now()}_${historyRequestSeqRef.current += 1}`;
    const payload = {
      type: 'chat:subscribe',
      request_id: requestId,
      mode: requestMode,
      limit: Math.max(1, Math.min(Number(limit || PAGE_SIZE), 100)),
    };
    if (normalizedBeforeId !== null && Number.isFinite(normalizedBeforeId)) {
      payload.before_id = normalizedBeforeId;
    }
    console.debug('[chat:ws] subscribe:request', {
      request_id: requestId,
      mode: requestMode,
      before_id: payload.before_id ?? null,
      limit: payload.limit,
    });
    try {
      ws.send(JSON.stringify(payload));
      const requestMeta = {
        requestId,
        mode: requestMode,
        beforeId: payload.before_id ?? null,
        limit: payload.limit,
      };
      activeHistoryRequestRef.current = requestMeta;
      return requestMeta;
    } catch (ex) {
      console.error('chat:subscribe ws send failed', ex);
      return false;
    }
  }, []);

  const returnToLatestMessages = useCallback(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      showChatNotice('Нет соединения с чатом', 'error');
      return;
    }

    activeChatAroundRequestRef.current = null;
    activeNewerRequestRef.current = null;
    newerAutoScrollRef.current = false;
    activeUnreadContextRequestRef.current = null;
    unreadContextLoadedRef.current = false;
    unreadInitialCountRef.current = 0;
    unreadBoundaryIdRef.current = 0;
    firstUnreadMessageIdRef.current = 0;
    unreadConsumedIdsRef.current.clear();
    isLoadingNewerRef.current = false;
    isLoadingOlderRef.current = false;
    initialViewportAppliedRef.current = false;
    initialHistoryLoadedRef.current = false;
    olderAnchorRef.current = null;
    returnToLatestRequestedRef.current = true;

    setChatHistoryMode('live');
    setIsLoading(true);
    setMessages([]);
    setIsLoadingOlder(false);
    setIsLoadingNewer(false);
    setHasMoreOlder(true);
    setHasMoreNewer(false);
    setShowScrollDown(false);
    setNewBelowCount(0);
    setPendingReplyJumpId(0);

    const requestMeta = requestSubscribe({ limit: PAGE_SIZE, mode: 'initial' });
    if (!requestMeta) {
      returnToLatestRequestedRef.current = false;
      setIsLoading(false);
    }
  }, [requestSubscribe, showChatNotice]);

  const connectChatWs = useCallback(async () => {
    if (!mountedRef.current) return;
    if (subscriptionEndedRef.current) return;
    if (deviceLimitReachedRef.current) return;
    const currentUserStatus = userStatusRef.current;
    if (currentUserStatus === false) {
      setIsConnected(false);
      setIsLoading(false);
      if (!subscriptionEndedRef.current) {
        setErrorText('Подписка не активна. Оформите подписку для доступа к чату.');
      }
      if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
        wsRef.current.close(1008, 'subscription is not active');
      }
      return;
    }
    if (currentUserStatus !== true) {
      return;
    }
    setErrorText('');
    if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
      return;
    }
    try {
      const ticketResp = await getWsTicketRef.current();
      const ticket = String(ticketResp?.ticket || '').trim();
      if (!ticket) return;
      if (!mountedRef.current || subscriptionEndedRef.current || userStatusRef.current !== true) {
        return;
      }

      const ws = new WebSocket(`${WS_URL}/ws/chat?ticket=${encodeURIComponent(ticket)}`);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        setSubscriptionEnded(false);
        setErrorText('');
        console.debug('[chat:ws] connected');
        void requestSubscribe({ limit: PAGE_SIZE });
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'chat:ready' || data.type === 'chat:presence') {
            if (data.type === 'chat:ready') {
              console.debug('[chat:ws] ready', {
                online_users: data.online_users,
                online_connections: data.online_connections,
              });
            }
            updatePresence(data);
            return;
          }

          if (data.type === 'chat:history' && Array.isArray(data.messages)) {
            const incoming = Array.isArray(data.messages) ? data.messages : [];
            const responseRequestId = data.request_id ? String(data.request_id) : '';
            const responseBeforeIdRaw = data.before_id;
            const responseBeforeId = responseBeforeIdRaw !== null && responseBeforeIdRaw !== undefined
              ? Number(responseBeforeIdRaw)
              : null;
            const normalizedResponseBeforeId = Number.isFinite(responseBeforeId) ? responseBeforeId : null;
            const activeHistoryRequest = activeHistoryRequestRef.current;
            let responseMode = String(data.mode || '').trim();
            let isOlderResponse = false;

            if (responseRequestId) {
              if (!activeHistoryRequest || activeHistoryRequest.requestId !== responseRequestId) {
                console.debug('[chat:ws] history:stale_response_ignored', {
                  request_id: responseRequestId,
                  active_request_id: activeHistoryRequest?.requestId ?? null,
                  mode: responseMode || null,
                  before_id: normalizedResponseBeforeId,
                  count: incoming.length,
                });
                return;
              }

              responseMode = responseMode || activeHistoryRequest.mode || 'initial';
              const activeBeforeId = activeHistoryRequest.beforeId ?? null;
              if (activeBeforeId !== normalizedResponseBeforeId) {
                activeHistoryRequestRef.current = null;
                if (activeHistoryRequest.mode === 'older') {
                  isLoadingOlderRef.current = false;
                  setIsLoadingOlder(false);
                  if (olderRequestTimeoutRef.current) {
                    clearTimeout(olderRequestTimeoutRef.current);
                    olderRequestTimeoutRef.current = null;
                  }
                }
                console.debug('[chat:ws] history:before_id_mismatch_ignored', {
                  request_id: responseRequestId,
                  expected_before_id: activeBeforeId,
                  response_before_id: normalizedResponseBeforeId,
                  mode: responseMode,
                });
                return;
              }

              activeHistoryRequestRef.current = null;
              isOlderResponse = responseMode === 'older';
            } else {
              isOlderResponse = isLoadingOlderRef.current;
              responseMode = isOlderResponse ? 'older' : 'initial_or_refresh';
            }

            if (isOlderResponse) {
              isLoadingOlderRef.current = false;
              setIsLoadingOlder(false);
              if (olderRequestTimeoutRef.current) {
                clearTimeout(olderRequestTimeoutRef.current);
                olderRequestTimeoutRef.current = null;
              }
            }

            if (!isOlderResponse && data.reply_mentions_state) {
              applyReplyMentionState(data.reply_mentions_state);
            }

            if (
              !isOlderResponse
              && responseMode !== 'refresh'
              && data.state
              && !unreadContextLoadedRef.current
              && !returnToLatestRequestedRef.current
            ) {
              const stateUnreadCount = Number(data.state.unread_count || 0);
              const stateLastReadId = Number(data.state.last_read_message_id || 0);
              const lastReadInLoaded = stateLastReadId > 0
                && incoming.some((msg) => Number(msg?.id || 0) === stateLastReadId);

              if (stateUnreadCount > PAGE_SIZE && stateLastReadId > 0 && !lastReadInLoaded) {
                setChatState((prev) => ({ ...(prev || {}), ...data.state }));
                readAckedIdRef.current = stateLastReadId;
                readQueuedIdRef.current = stateLastReadId;
                seenMessageIdRef.current = stateLastReadId;
                resetUnreadLocalProgress(stateLastReadId, stateUnreadCount);
                if (requestUnreadContext()) {
                  setChatHistoryMode('unread_context');
                  console.debug('[chat:ws] history:redirect_to_unread_context', {
                    unread_count: stateUnreadCount,
                    last_read_message_id: stateLastReadId,
                  });
                  return;
                }
              }
            }

            if (isOlderResponse) {
              const container = chatScrollRef.current;
              const restoreAnchor = olderAnchorRef.current;
              const fallbackTop = Number(container?.scrollTop || 0);
              const fallbackHeight = Number(container?.scrollHeight || 0);

              setMessages((prev) => (
                ['search_context', 'unread_context'].includes(chatHistoryModeRef.current)
                  ? uniqById([...incoming, ...prev]).sort((a, b) => Number(a?.id || 0) - Number(b?.id || 0))
                  : uniqById([...incoming, ...prev])
              ));

              window.requestAnimationFrame(() => {
                const target = chatScrollRef.current;
                if (!target) return;

                if (restoreAnchor?.messageId) {
                  const row = target.querySelector(`[data-msg-id="${restoreAnchor.messageId}"]`);
                  if (row) {
                    const containerRect = target.getBoundingClientRect();
                    const rowTop = row.getBoundingClientRect().top - containerRect.top;
                    const delta = rowTop - Number(restoreAnchor.offsetTop || 0);
                    target.scrollTop += delta;
                    return;
                  }
                }

                const heightDelta = target.scrollHeight - fallbackHeight;
                target.scrollTop = fallbackTop + Math.max(0, heightDelta);
              });
              olderAnchorRef.current = null;
            } else {
              setMessages((prev) => uniqById([...prev, ...incoming]));
            }

            console.debug('[chat:ws] history:response', {
              request_id: responseRequestId || null,
              mode: responseMode,
              before_id: normalizedResponseBeforeId,
              count: incoming.length,
              has_state: Boolean(data.state),
            });

            if (data.state) {
              setChatState((prev) => ({ ...(prev || {}), ...data.state }));
              if (!initialHistoryLoadedRef.current) {
                const initialReadId = Number(data.state.last_read_message_id || 0);
                readAckedIdRef.current = initialReadId;
                readQueuedIdRef.current = initialReadId;
              }
            }
            if (isOlderResponse && data.reply_mentions_state) {
              applyReplyMentionState(data.reply_mentions_state);
            }
            if (Object.prototype.hasOwnProperty.call(data, 'contest_state')) {
              setContestState(data.contest_state || null);
            }

            if (!initialHistoryLoadedRef.current) {
              initialHistoryLoadedRef.current = true;
              setHasMoreOlder(incoming.length >= PAGE_SIZE);
              setIsLoading(false);
            } else if (isOlderResponse && incoming.length < PAGE_SIZE) {
              setHasMoreOlder(false);
            }

            updatePresence(data);
            return;
          }

          if (data.type === 'chat:search_result') {
            const responseRequestId = String(data.request_id || '');
            const activeRequest = activeChatSearchRequestRef.current;
            if (!activeRequest || activeRequest.requestId !== responseRequestId) {
              console.debug('[chat:ws] search:stale_response_ignored', {
                request_id: responseRequestId,
                active_request_id: activeRequest?.requestId ?? null,
              });
              return;
            }

            const incoming = sortSearchResultsNewestFirst(Array.isArray(data.items) ? data.items : []);
            activeChatSearchRequestRef.current = null;
            setIsSearchLoading(false);
            setSearchNextCursor(data.next_cursor || null);
            setSearchResults((prev) => (
              activeRequest.append
                ? sortSearchResultsNewestFirst(uniqById([...prev, ...incoming]))
                : incoming
            ));
            setSearchError('');
            setHasSearchCompleted(true);
            console.debug('[chat:ws] search:response', {
              request_id: responseRequestId,
              count: incoming.length,
              append: activeRequest.append,
              next_cursor: data.next_cursor ?? null,
            });
            return;
          }

          if (data.type === 'chat:media_result') {
            const responseRequestId = String(data.request_id || '');
            const activeRequest = activeChatMediaRequestRef.current;
            if (!activeRequest || activeRequest.requestId !== responseRequestId) {
              console.debug('[chat:ws] media:stale_response_ignored', {
                request_id: responseRequestId,
                active_request_id: activeRequest?.requestId ?? null,
              });
              return;
            }

            const incoming = (Array.isArray(data.items) ? data.items : [])
              .map(buildChatMediaItem)
              .filter(Boolean);
            activeChatMediaRequestRef.current = null;
            setIsMediaLoading(false);
            setMediaNextCursor(data.next_cursor || null);
            setMediaResults((prev) => {
              const merged = activeRequest.append ? [...prev, ...incoming] : incoming;
              const seen = new Set();
              return merged.filter((item) => {
                if (!item?.id || seen.has(item.id)) return false;
                seen.add(item.id);
                return true;
              }).sort((a, b) => Number(b.message_id || 0) - Number(a.message_id || 0));
            });
            setMediaError('');
            setHasMediaCompleted(!data.next_cursor);
            return;
          }

          if (data.type === 'chat:unread_context_result') {
            const responseRequestId = String(data.request_id || '');
            const activeRequest = activeUnreadContextRequestRef.current;
            if (!activeRequest || activeRequest.requestId !== responseRequestId) {
              console.debug('[chat:ws] unread_context:stale_response_ignored', {
                request_id: responseRequestId,
                active_request_id: activeRequest?.requestId ?? null,
              });
              return;
            }

            activeUnreadContextRequestRef.current = null;
            unreadContextLoadedRef.current = true;
            const incoming = Array.isArray(data.messages) ? data.messages : [];
            const firstUnreadId = Number(data.first_unread_id || 0);
            const lastReadId = Number(data.last_read_message_id || data.state?.last_read_message_id || 0);
            const unreadCount = Number(data.state?.unread_count || chatStateRef.current?.unread_count || 0);
            firstUnreadMessageIdRef.current = firstUnreadId || 0;

            setChatHistoryMode('unread_context');
            setMessages(
              uniqById(incoming)
                .sort((a, b) => Number(a?.id || 0) - Number(b?.id || 0))
            );
            setHasMoreOlder(Boolean(data.has_more_older));
            setHasMoreNewer(Boolean(data.has_more_newer));
            setIsLoading(false);
            setIsLoadingOlder(false);
            setIsLoadingNewer(false);
            setShowScrollDown(true);
            setNewBelowCount(Math.max(0, unreadCount));
            if (data.state) {
              setChatState((prev) => ({ ...(prev || {}), ...data.state }));
            }
            if (lastReadId > 0) {
              readAckedIdRef.current = lastReadId;
              readQueuedIdRef.current = lastReadId;
              seenMessageIdRef.current = lastReadId;
            }
            resetUnreadLocalProgress(lastReadId, unreadCount);
            initialViewportAppliedRef.current = true;
            initialHistoryLoadedRef.current = true;

            window.requestAnimationFrame(() => {
              const targetId = firstUnreadId || Number(incoming[0]?.id || 0);
              if (targetId) {
                scrollToMessageById(targetId, 'auto');
              }
            });

            console.debug('[chat:ws] unread_context:response', {
              request_id: responseRequestId,
              first_unread_id: firstUnreadId || null,
              count: incoming.length,
              unread_count: unreadCount,
              has_more_newer: Boolean(data.has_more_newer),
            });
            return;
          }

          if (data.type === 'chat:contest:state') {
            setContestState(data.state || null);
            return;
          }

          if (data.type === 'chat:contest:update') {
            const nextState = data.state || null;
            setContestState((prev) => {
              if (!nextState) return null;
              const prevContestId = Number(prev?.contest?.id || 0);
              const nextContestId = Number(nextState?.contest?.id || 0);
              return {
                ...(prevContestId && prevContestId === nextContestId ? prev : {}),
                ...nextState,
                me: prevContestId && prevContestId === nextContestId ? prev?.me : nextState?.me,
              };
            });
            return;
          }

          if (data.type === 'chat:reply_mentions:state') {
            applyReplyMentionState(data.state || {});
            return;
          }

          if (data.type === 'chat:around_message_result') {
            const responseRequestId = String(data.request_id || '');
            const activeRequest = activeChatAroundRequestRef.current;
            if (!activeRequest || activeRequest.requestId !== responseRequestId) {
              console.debug('[chat:ws] around:stale_response_ignored', {
                request_id: responseRequestId,
                active_request_id: activeRequest?.requestId ?? null,
              });
              return;
            }

            activeChatAroundRequestRef.current = null;
            const targetId = Number(data.target_id || activeRequest.targetId || 0);
            if (!data.found || !Array.isArray(data.messages) || !data.messages.length) {
              if (activeRequest.source === 'reply_mention') {
                setIsReplyMentionLoading(false);
              }
              showChatNotice('Сообщение не найдено или удалено', 'error');
              return;
            }

            setChatHistoryMode('search_context');
            if (!activeRequest.preserveSearch) {
              setIsSearchOpen(false);
            }
            setMessages(
              uniqById(data.messages)
                .sort((a, b) => Number(a?.id || 0) - Number(b?.id || 0))
            );
            setHasMoreOlder(true);
            setHasMoreNewer(data.messages.length >= 30);
            setShowScrollDown(true);
            setNewBelowCount(0);
            initialViewportAppliedRef.current = true;
            initialHistoryLoadedRef.current = true;
            scheduleAroundMessageAlignment(targetId, 'auto');
            if (activeRequest.source === 'reply_mention') {
              window.requestAnimationFrame(() => {
                if (!markReplyMentionSeen(targetId)) {
                  setIsReplyMentionLoading(false);
                }
              });
            }
            console.debug('[chat:ws] around:response', {
              request_id: responseRequestId,
              target_id: targetId,
              count: data.messages.length,
            });
            return;
          }

          if ((data.type === 'chat:new' || data.type === 'chat:sync') && (data.message || data.messages)) {
            const incoming = data.message ? [data.message] : data.messages;
            const incomingList = Array.isArray(incoming) ? incoming : [];

            if (data.type === 'chat:sync' && activeNewerRequestRef.current) {
              const activeNewerRequest = activeNewerRequestRef.current;
              const responseAfterId = Number(data.after_id || 0);
              if (Number(activeNewerRequest.afterId || 0) !== responseAfterId) {
                console.debug('[chat:ws] context:newer_stale_response_ignored', {
                  expected_after_id: activeNewerRequest.afterId,
                  response_after_id: responseAfterId,
                });
                return;
              }

              activeNewerRequestRef.current = null;
              isLoadingNewerRef.current = false;
              setIsLoadingNewer(false);
              const shouldAutoScrollNewer = Boolean(newerAutoScrollRef.current);
              newerAutoScrollRef.current = false;
              const reachedLatest = incomingList.length < PAGE_SIZE;
              let deliveredDeferredCount = 0;
              for (const msg of incomingList) {
                const id = Number(msg?.id || 0);
                if (id && deferredLiveMessageIdsRef.current.delete(id)) {
                  deliveredDeferredCount += 1;
                }
              }
              setMessages((prev) => (
                uniqById([...prev, ...incomingList])
                  .sort((a, b) => Number(a?.id || 0) - Number(b?.id || 0))
              ));
              setHasMoreNewer(!reachedLatest);
              if (reachedLatest) {
                deferredLiveMessageIdsRef.current.clear();
                setChatHistoryMode('live');
                setShowScrollDown(false);
              } else {
                setShowScrollDown(true);
              }
              setNewBelowCount((prev) => (
                reachedLatest
                  ? 0
                  : Math.max(0, Number(prev || 0) - deliveredDeferredCount)
              ));
              if (shouldAutoScrollNewer && incomingList.length) {
                const newestIncomingId = incomingList.reduce((maxId, msg) => (
                  Math.max(maxId, Number(msg?.id || 0))
                ), 0);
                window.requestAnimationFrame(() => {
                  const newestLoadedId = newestIncomingId;
                  if (newestLoadedId) {
                    scrollToMessageById(newestLoadedId, 'smooth');
                    if (chatHistoryModeRef.current === 'unread_context') {
                      seenMessageIdRef.current = Math.max(seenMessageIdRef.current, newestLoadedId);
                      consumeUnreadThrough(newestLoadedId);
                      syncReadState(newestLoadedId);
                    }
                  }
                });
              }
              console.debug('[chat:ws] context:newer_response', {
                count: incomingList.length,
                has_more: !reachedLatest,
                delivered_deferred: deliveredDeferredCount,
              });
              return;
            }

            if (['search_context', 'unread_context'].includes(chatHistoryModeRef.current) && data.type === 'chat:new') {
              let addedDeferredCount = 0;
              for (const msg of incomingList) {
                const id = Number(msg?.id || 0);
                if (!id || deferredLiveMessageIdsRef.current.has(id)) continue;
                deferredLiveMessageIdsRef.current.add(id);
                addedDeferredCount += 1;
              }
              setShowScrollDown(true);
              setNewBelowCount((prev) => prev + addedDeferredCount);
              console.debug('[chat:ws] context:live_message_deferred', {
                count: incomingList.length,
                added: addedDeferredCount,
              });
              return;
            }

            const hasOwnSentMessage = incomingList.some((msg) => {
              const clientMsgId = String(msg?.client_msg_id || '').trim();
              if (!clientMsgId || !ownSentClientMsgIdsRef.current.has(clientMsgId)) return false;
              if (Number(msg?.author_account_id || 0) !== myUserId) return false;
              forgetOwnSentClientMsgId(clientMsgId);
              return true;
            });
            const wasNearBottom = isNearBottom(chatScrollRef.current);
            const replyToMeMessages = incomingList.filter((msg) => isReplyToMe(msg));
            autoScrollOnNextMessageRef.current = hasOwnSentMessage || wasNearBottom;
            setMessages((prev) => uniqById([...prev, ...incomingList]));
            if (replyToMeMessages.length && wasNearBottom && data.type === 'chat:new') {
              window.requestAnimationFrame(() => {
                for (const msg of replyToMeMessages) {
                  const messageId = Number(msg?.id || 0);
                  if (!messageId) continue;
                  markMessageAsJumpTarget(messageId);
                  markReplyMentionSeen(messageId);
                }
              });
            }
            console.debug('[chat:ws] incoming', {
              type: data.type,
              count: incomingList.length,
              own_sent: hasOwnSentMessage,
            });
            return;
          }

          if (data.type === 'chat:edit' && data.message) {
            const editedMessage = normalizeMessage(data.message);
            const editedId = Number(editedMessage?.id || 0);
            if (editedId > 0) {
              setMessages((prev) => applyEditedMessage(prev, editedMessage));
              setReplyTo((prev) => (
                Number(prev?.id || 0) === editedId
                  ? { ...prev, ...editedMessage }
                  : prev
              ));
              setMessageMenu((prev) => (
                Number(prev?.message?.id || 0) === editedId
                  ? { ...prev, message: { ...(prev.message || {}), ...editedMessage } }
                  : prev
              ));

              if (pendingEditMessageIdRef.current === editedId) {
                pendingEditMessageIdRef.current = 0;
                setIsEditingMessageSaving(false);
                setEditingMessage(null);
                setInputValue('');
              }

              console.debug('[chat:ws] edit:apply', { message_id: editedId });
            }
            return;
          }

          if (data.type === 'chat:delete') {
            const deletedId = Number(data.message_id || 0);
            if (deletedId > 0) {
              scheduleMessageDelete(deletedId, data.deleted_at || null);
              console.debug('[chat:ws] delete:apply', { message_id: deletedId });
            }
            return;
          }

          if (data.type === 'chat:reaction:update') {
            const messageId = Number(data.message_id || 0);
            if (messageId > 0) {
              setMessages((prev) => applyReactionUpdateToMessages(prev, data, myUserId));
              setReplyTo((prev) => (
                Number(prev?.id || 0) === messageId
                  ? { ...prev, reactions: applyReactionUpdateToMessages([prev], data, myUserId)[0]?.reactions || [] }
                  : prev
              ));
              setMessageMenu((prev) => (
                Number(prev?.message?.id || 0) === messageId
                  ? {
                    ...prev,
                    message: {
                      ...(prev.message || {}),
                      reactions: applyReactionUpdateToMessages([prev.message], data, myUserId)[0]?.reactions || [],
                    },
                  }
                  : prev
              ));
              console.debug('[chat:ws] reaction:apply', { message_id: messageId });
            }
            return;
          }

          if (data.type === 'chat:ack' && data.scope === 'chat:edit') {
            const ackId = Number(data.message_id || 0);
            if (ackId > 0 && pendingEditMessageIdRef.current === ackId) {
              pendingEditMessageIdRef.current = 0;
              setIsEditingMessageSaving(false);
            }
            return;
          }

          if (data.type === 'chat:ack' && (!data.scope || data.scope === 'chat:send')) {
            const clientMsgId = String(data.client_msg_id || '');
            const pending = clearPendingSend(clientMsgId || null);
            if (pending) {
              setInputValue((currentValue) => (
                String(currentValue || '') === String(pending.inputValue || '')
                  || String(currentValue || '').trim() === pending.body
                  ? ''
                  : currentValue
              ));
              setReplyTo((currentReply) => (
                Number(currentReply?.id || 0) === Number(pending.replyToId || 0)
                  ? null
                  : currentReply
              ));
              if (pending.hadPhoto) {
                clearAttachedPhoto();
              }
              console.debug('[chat:ws] send:ack', {
                client_msg_id: clientMsgId || null,
                message_id: data.message_id ?? null,
              });
            }
            return;
          }

          if (data.type === 'chat:read:ack' && data.state) {
            const ackFromServer = Number(data.state.last_read_message_id || 0);
            if (ackFromServer > readAckedIdRef.current) {
              readAckedIdRef.current = ackFromServer;
            }
            if (readWsAckTimerRef.current) {
              clearTimeout(readWsAckTimerRef.current);
              readWsAckTimerRef.current = null;
            }
            readInFlightRef.current = false;
            const pending = Number(readQueuedIdRef.current || 0);
            if (pending > readAckedIdRef.current) {
              if (readFlushTimerRef.current) clearTimeout(readFlushTimerRef.current);
              readFlushTimerRef.current = setTimeout(() => {
                readFlushTimerRef.current = null;
                void flushReadState();
              }, 120);
            }
            setChatState((prev) => {
              const prevState = prev || {};
              const merged = { ...prevState, ...data.state };
              const serverUnread = data.state.unread_count;
              if (serverUnread === null || serverUnread === undefined) {
                const prevReadId = Number(prevState.last_read_message_id || 0);
                const nextReadId = Number(merged.last_read_message_id || 0);
                const list = Array.isArray(messagesRef.current) ? messagesRef.current : [];
                const consumedLoaded = list.reduce((acc, msg) => {
                  const id = Number(msg?.id || 0);
                  if (!id || id <= prevReadId || id > nextReadId) return acc;
                  if (Number(msg?.author_account_id || 0) === myUserId) return acc;
                  return acc + 1;
                }, 0);
                const prevUnread = Number(prevState.unread_count);
                if (Number.isFinite(prevUnread) && prevUnread >= 0) {
                  const nextUnread = Math.max(0, prevUnread - consumedLoaded);
                  merged.unread_count = nextUnread;
                  merged.has_unread = nextUnread > 0;
                } else {
                  const fallbackUnread = list.reduce((acc, msg) => {
                    const id = Number(msg?.id || 0);
                    if (!id || id <= nextReadId) return acc;
                    if (Number(msg?.author_account_id || 0) === myUserId) return acc;
                    return acc + 1;
                  }, 0);
                  merged.unread_count = fallbackUnread;
                  merged.has_unread = fallbackUnread > 0;
                }
              }
              return merged;
            });
            console.debug('[chat:ws] read:ack', {
              last_read_message_id: data.state.last_read_message_id ?? null,
              unread_count: data.state.unread_count ?? null,
            });
            return;
          }

          if (data.type === 'chat:error') {
            console.warn('[chat:ws] error', data);
            if (data.scope === 'chat:edit') {
              pendingEditMessageIdRef.current = 0;
              setIsEditingMessageSaving(false);
            }
            if (data.scope === 'chat:send') {
              const failedClientMsgId = data.client_msg_id ? String(data.client_msg_id) : null;
              clearPendingSend(failedClientMsgId);
              if (failedClientMsgId) {
                forgetOwnSentClientMsgId(failedClientMsgId);
              }
            }
            setErrorText(formatChatWsError(data.error, data.scope));
          }
        } catch (ex) {
          console.error('Chat WS parse error', ex);
        }
      };

      ws.onerror = () => {
        setIsConnected(false);
        console.warn('[chat:ws] socket error');
      };

      ws.onclose = (event) => {
        setIsConnected(false);
        if (pendingSendRef.current) {
          clearPendingSend();
          setErrorText('Сообщение не подтверждено. Проверьте соединение и отправьте ещё раз.');
        }
        activeHistoryRequestRef.current = null;
        const closeReason = String(event?.reason || '').toLowerCase();
        const isSubscriptionClosed = event?.code === 1008
          && (closeReason.includes('subscription ended') || closeReason.includes('subscription is not active'));
        const isDeviceLimitClosed = event?.code === 1008
          && closeReason.includes('too many devices');
        if (isSubscriptionClosed) {
          lockChatBySubscription(true);
          console.debug('[chat:ws] disconnected:subscription', {
            code: event?.code ?? null,
            reason: event?.reason ?? '',
          });
          return;
        }
        if (isDeviceLimitClosed) {
          deviceLimitReachedRef.current = true;
          setDeviceLimitReached(true);
          setIsLoading(false);
          setIsLoadingOlder(false);
          isLoadingOlderRef.current = false;
          showChatNotice('Чат открыт на слишком большом количестве устройств. Закройте лишние вкладки и обновите страницу.', 'error');
          console.debug('[chat:ws] disconnected:device-limit', {
            code: event?.code ?? null,
            reason: event?.reason ?? '',
          });
          return;
        }
        console.debug('[chat:ws] disconnected, scheduling reconnect');
        if (!mountedRef.current || subscriptionEndedRef.current || deviceLimitReachedRef.current) return;
        reconnectTimerRef.current = setTimeout(() => {
          void connectChatWs();
        }, 1200);
      };
    } catch (ex) {
      console.error('connectChatWs error', ex);
    }
  }, [applyReplyMentionState, clearAttachedPhoto, clearPendingSend, flushReadState, forgetOwnSentClientMsgId, isReplyToMe, lockChatBySubscription, markMessageAsJumpTarget, markReplyMentionSeen, requestSubscribe, requestUnreadContext, scheduleAroundMessageAlignment, scheduleMessageDelete, scrollToMessageById, showChatNotice, updatePresence]);

  const loadInitial = useCallback(() => {
    initialViewportAppliedRef.current = false;
    initialHistoryLoadedRef.current = false;
    autoScrollOnNextMessageRef.current = false;
    isLoadingOlderRef.current = false;
    activeHistoryRequestRef.current = null;
    activeUnreadContextRequestRef.current = null;
    unreadContextLoadedRef.current = false;
    returnToLatestRequestedRef.current = false;
    unreadInitialCountRef.current = 0;
    unreadBoundaryIdRef.current = 0;
    firstUnreadMessageIdRef.current = 0;
    unreadConsumedIdsRef.current.clear();
    deviceLimitReachedRef.current = false;
    olderAnchorRef.current = null;
    if (scrollRafRef.current) {
      window.cancelAnimationFrame(scrollRafRef.current);
      scrollRafRef.current = null;
    }
    lastMessageIdRef.current = 0;
    seenMessageIdRef.current = 0;
    readAckedIdRef.current = 0;
    readQueuedIdRef.current = 0;
    readInFlightRef.current = false;
    if (readFlushTimerRef.current) {
      clearTimeout(readFlushTimerRef.current);
      readFlushTimerRef.current = null;
    }
    if (readWsAckTimerRef.current) {
      clearTimeout(readWsAckTimerRef.current);
      readWsAckTimerRef.current = null;
    }
    if (olderRequestTimeoutRef.current) {
      clearTimeout(olderRequestTimeoutRef.current);
      olderRequestTimeoutRef.current = null;
    }
    deletingMessageTimersRef.current.forEach((timer) => clearTimeout(timer));
    deletingMessageTimersRef.current.clear();
    setMessages([]);
    setDeletingMessageIds([]);
    setChatHistoryMode('live');
    setIsLoadingNewer(false);
    setHasMoreNewer(false);
    isLoadingNewerRef.current = false;
    newerAutoScrollRef.current = false;
    activeNewerRequestRef.current = null;
    activeChatAroundRequestRef.current = null;
    deferredLiveMessageIdsRef.current.clear();
    setEditingMessage(null);
    setIsEditingMessageSaving(false);
    setIsSendingMessage(false);
    setInputValue('');
    pendingEditMessageIdRef.current = 0;
    clearPendingSend();
    setChatState(null);
    setIsLoadingOlder(false);
    setHasMoreOlder(true);
    setShowScrollDown(false);
    setNewBelowCount(0);
    setHighlightedMessageId(0);
    setPendingReplyJumpId(0);
    setIsLoading(true);
    setErrorText('');
    setDeviceLimitReached(false);
    replyJumpLoadAttemptsRef.current = 0;
  }, [clearOwnSentClientMsgIds, clearPendingSend]);

  useEffect(() => {
    mountedRef.current = true;
    void loadInitial();
    void connectChatWs();
    return () => {
      mountedRef.current = false;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (readFlushTimerRef.current) clearTimeout(readFlushTimerRef.current);
      if (readWsAckTimerRef.current) clearTimeout(readWsAckTimerRef.current);
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
      if (selectionLockTimerRef.current) {
        clearTimeout(selectionLockTimerRef.current);
        selectionLockTimerRef.current = null;
      }
      document.documentElement.classList.remove('chat-native-selection-blocked');
      if (olderRequestTimeoutRef.current) clearTimeout(olderRequestTimeoutRef.current);
      activeHistoryRequestRef.current = null;
      if (pendingSendTimeoutRef.current) clearTimeout(pendingSendTimeoutRef.current);
      pendingSendTimeoutRef.current = null;
      pendingSendRef.current = null;
      ownSentClientMsgTimersRef.current.forEach((timer) => clearTimeout(timer));
      ownSentClientMsgTimersRef.current.clear();
      ownSentClientMsgIdsRef.current.clear();
      if (scrollRafRef.current) window.cancelAnimationFrame(scrollRafRef.current);
      if (replyJumpHighlightTimerRef.current) clearTimeout(replyJumpHighlightTimerRef.current);
      deletingMessageTimersRef.current.forEach((timer) => clearTimeout(timer));
      deletingMessageTimersRef.current.clear();
      if (sellerProductsScrollRafRef.current) window.cancelAnimationFrame(sellerProductsScrollRafRef.current);
      if (sellerCopyNoticeTimerRef.current) clearTimeout(sellerCopyNoticeTimerRef.current);
      if (chatNoticeTimerRef.current) clearTimeout(chatNoticeTimerRef.current);
      if (chatSearchDebounceRef.current) clearTimeout(chatSearchDebounceRef.current);
      if (suppressContextNewerTimerRef.current) clearTimeout(suppressContextNewerTimerRef.current);
      activeChatSearchRequestRef.current = null;
      activeChatMediaRequestRef.current = null;
      activeChatAroundRequestRef.current = null;
      activeUnreadContextRequestRef.current = null;
      unreadConsumedIdsRef.current.clear();
      sellerItemsRequestSeqRef.current += 1;
      sellerItemsLoadingRef.current = false;
      if (wsRef.current) wsRef.current.close();
    };
  }, [connectChatWs, loadInitial, userInfo?.status]);

  useEffect(() => {
    const updateOrientation = () => {
      const viewportWidth = Number(window?.innerWidth || 0);
      const viewportHeight = Number(window?.innerHeight || 0);
      if (window?.matchMedia) {
        setIsLandscape(window.matchMedia('(orientation: landscape)').matches);
      } else {
        setIsLandscape(viewportWidth > viewportHeight);
      }
      setIsWideDesktopLayout(viewportWidth >= 1080);
    };
    updateOrientation();
    window.addEventListener('resize', updateOrientation);
    window.addEventListener('orientationchange', updateOrientation);
    return () => {
      window.removeEventListener('resize', updateOrientation);
      window.removeEventListener('orientationchange', updateOrientation);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadMyProfile = async () => {
      if (!myUserId || myProfileLoadedRef.current) return;
      myProfileLoadedRef.current = true;
      try {
        const resp = await getPublicProfile();
        const profile = resp?.profile || {};
        const nextHandle = normalizeHandle(profile?.chat_handle);
        const nextChatPushMode = String(
          profile?.chat_push_mode
          || (profile?.chat_notify_mentions_only ? 'mentions' : 'off')
          || 'off'
        ).trim().toLowerCase();
        if (!cancelled) {
          setMyChatHandle(nextHandle);
          setChatPushMode(
            ['off', 'mentions', 'replies_mentions', 'all'].includes(nextChatPushMode)
              ? nextChatPushMode
              : 'off'
          );
        }
      } catch (ex) {
        console.debug('[chat:profile] getPublicProfile failed', ex);
      }
    };
    void loadMyProfile();
    return () => {
      cancelled = true;
    };
  }, [getPublicProfile, myUserId]);

  useEffect(() => {
    if (userInfo?.status === false || subscriptionEnded) return undefined;
    const node = typeof composerRef.current?.getElement === 'function'
      ? composerRef.current.getElement()
      : composerRef.current;
    if (!node) return undefined;

    let rafId = 0;
    const updateComposerHeight = () => {
      const next = Math.max(92, Math.ceil(node.offsetHeight || node.getBoundingClientRect().height));
      setComposerHeight((prev) => (Math.abs(prev - next) > 1 ? next : prev));
    };
    const scheduleUpdate = () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(updateComposerHeight);
    };

    updateComposerHeight();

    const observer = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(scheduleUpdate)
      : null;
    observer?.observe(node);

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      observer?.disconnect();
    };
  }, [userInfo?.status, subscriptionEnded]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    chatStateRef.current = chatState;
  }, [chatState]);

  useEffect(() => {
    chatHistoryModeRef.current = chatHistoryMode;
  }, [chatHistoryMode]);

  useEffect(() => {
    isLoadingNewerRef.current = isLoadingNewer;
  }, [isLoadingNewer]);

  useEffect(() => {
    if (!isSearchOpen || searchTab !== 'messages') {
      if (chatSearchDebounceRef.current) {
        clearTimeout(chatSearchDebounceRef.current);
        chatSearchDebounceRef.current = null;
      }
      activeChatSearchRequestRef.current = null;
      setIsSearchLoading(false);
      setHasSearchCompleted(false);
      return;
    }

    const query = String(searchQuery || '').trim();
    const author = normalizeHandle(String(searchAuthor || '').replace(/^@+/, ''));
    if (query.length < 2 && !author) {
      activeChatSearchRequestRef.current = null;
      setSearchResults([]);
      setSearchNextCursor(null);
      setSearchError('');
      setHasSearchCompleted(false);
      setIsSearchLoading(false);
      return;
    }

    if (chatSearchDebounceRef.current) clearTimeout(chatSearchDebounceRef.current);
    chatSearchDebounceRef.current = setTimeout(() => {
      chatSearchDebounceRef.current = null;
      requestChatSearch({ append: false });
    }, 450);

    return () => {
      if (chatSearchDebounceRef.current) {
        clearTimeout(chatSearchDebounceRef.current);
        chatSearchDebounceRef.current = null;
      }
    };
  }, [isSearchOpen, requestChatSearch, searchAuthor, searchQuery, searchTab]);


  useEffect(() => {
    if (!isSearchOpen || searchTab !== 'media') {
      activeChatMediaRequestRef.current = null;
      setIsMediaLoading(false);
      return;
    }
    if (mediaResults.length === 0 && !isMediaLoading && !hasMediaCompleted) {
      requestChatMedia({ append: false });
    }
  }, [hasMediaCompleted, isMediaLoading, isSearchOpen, mediaResults.length, requestChatMedia, searchTab]);

  useEffect(() => {
    if (messages.length <= CHAT_MAX_RENDERED_MESSAGES) return;
    if (chatHistoryModeRef.current !== 'live') return;
    if (isLoading || isLoadingOlderRef.current || isLoadingOlder) return;
    if (isLoadingNewerRef.current || isLoadingNewer) return;
    if (pendingReplyJumpId) return;
    if (highlightedMessageId) return;

    const container = chatScrollRef.current;
    if (!container || !isNearBottom(container)) return;

    setMessages((prev) => {
      if (prev.length <= CHAT_MAX_RENDERED_MESSAGES) return prev;
      const next = prev.slice(-CHAT_TRIM_TO_MESSAGES);
      const firstKeptId = Number(next[0]?.id || 0);
      console.debug('[chat] trim_old_messages', {
        before: prev.length,
        after: next.length,
        first_kept_id: firstKeptId || null,
      });
      return next;
    });
    setHasMoreOlder(true);
  }, [highlightedMessageId, isLoading, isLoadingNewer, isLoadingOlder, isNearBottom, messages.length, pendingReplyJumpId]);

  useEffect(() => {
    const stateReadId = Number(chatState?.last_read_message_id || 0);
    if (stateReadId > readAckedIdRef.current) {
      readAckedIdRef.current = stateReadId;
    }
    if (readQueuedIdRef.current < readAckedIdRef.current) {
      readQueuedIdRef.current = readAckedIdRef.current;
    }
  }, [chatState]);

  useEffect(() => {
    if (isLoading || initialViewportAppliedRef.current) return;

    const lastId = Number(messages[messages.length - 1]?.id || 0);
    const lastReadId = Number(chatState?.last_read_message_id || 0);
    const unreadCountFromState = Number(chatState?.unread_count || 0);

    const rafId = window.requestAnimationFrame(() => {
      const container = chatScrollRef.current;
      if (!container || !messages.length) {
        initialViewportAppliedRef.current = true;
        returnToLatestRequestedRef.current = false;
        setShowScrollDown(false);
        setNewBelowCount(0);
        return;
      }

      if (returnToLatestRequestedRef.current) {
        container.scrollTop = container.scrollHeight;
        seenMessageIdRef.current = lastId;
        setNewBelowCount(0);
        setShowScrollDown(false);
        if (lastId) {
          syncReadState(lastId, { immediate: true });
        }
        returnToLatestRequestedRef.current = false;
      } else if (lastReadId > 0 && unreadCountFromState > 0) {
        const targetIndex = messages.findIndex((msg) => Number(msg?.id || 0) === lastReadId);
        if (targetIndex >= 0) {
          const targetId = Number(messages[targetIndex]?.id || 0);
          const row = targetId ? container.querySelector(`[data-msg-id="${targetId}"]`) : null;
          if (row) {
            container.scrollTop = Math.max(0, row.offsetTop - Math.floor(container.clientHeight * 0.45));
          }
        } else {
          container.scrollTop = 0;
        }

        seenMessageIdRef.current = lastReadId;
        const unreadFromLoaded = countMessagesAfterId(lastReadId, messages);
        const initialUnread = Math.max(unreadCountFromState, unreadFromLoaded);
        setNewBelowCount(initialUnread);
        setShowScrollDown(initialUnread > 0);
      } else {
        container.scrollTop = container.scrollHeight;
        seenMessageIdRef.current = lastId;
        setNewBelowCount(0);
        setShowScrollDown(false);
      }

      lastMessageIdRef.current = lastId;
      initialViewportAppliedRef.current = true;
    });

    return () => window.cancelAnimationFrame(rafId);
  }, [chatState, countMessagesAfterId, isLoading, messages, syncReadState]);

  useEffect(() => {
    if (!messages.length) return;

    const lastId = Number(messages[messages.length - 1]?.id || 0);
    const prevLastId = Number(lastMessageIdRef.current || 0);
    if (!lastId) return;

    if (!prevLastId) {
      lastMessageIdRef.current = lastId;
      return;
    }

    if (lastId <= prevLastId) return;

    lastMessageIdRef.current = lastId;

    if (['search_context', 'unread_context'].includes(chatHistoryModeRef.current)) {
      setShowScrollDown(true);
      return;
    }

    if (autoScrollOnNextMessageRef.current) {
      autoScrollOnNextMessageRef.current = false;
      scrollToBottom('smooth');
      seenMessageIdRef.current = lastId;
      setNewBelowCount(0);
      setShowScrollDown(false);
      syncReadState(lastId, { immediate: true });
      return;
    }

    const unseenCount = countMessagesAfterId(seenMessageIdRef.current, messages);
    setShowScrollDown(unseenCount > 0);
    setNewBelowCount(unseenCount);
  }, [countMessagesAfterId, messages, scrollToBottom, syncReadState]);

  const loadOlder = useCallback(() => {
    if (isLoading || !hasMoreOlder || isLoadingOlderRef.current || messages.length === 0) return;

    const firstId = Number(messages[0]?.id || 0);
    if (!firstId) return;

    const container = chatScrollRef.current;
    if (container) {
      const rect = container.getBoundingClientRect();
      const sampleX = Math.max(
        rect.left + 16,
        Math.min(rect.right - 16, rect.left + (rect.width * 0.35))
      );
      const sampleY = rect.top + TOP_FETCH_THRESHOLD_PX;
      const elements = document.elementsFromPoint(sampleX, sampleY);
      const row = elements.find((el) => el?.dataset?.msgId);
      const messageId = Number(row?.dataset?.msgId || 0);

      olderAnchorRef.current = messageId > 0
        ? {
          messageId,
          offsetTop: row.getBoundingClientRect().top - rect.top,
        }
        : null;
    } else {
      olderAnchorRef.current = null;
    }

    const requestMeta = requestSubscribe({ beforeId: firstId, limit: PAGE_SIZE, mode: 'older' });
    if (!requestMeta) {
      setErrorText('Нет соединения для подгрузки истории');
      return;
    }

    console.debug('[chat:ws] loadOlder:start', {
      request_id: requestMeta.requestId,
      before_id: firstId,
    });

    isLoadingOlderRef.current = true;
    setIsLoadingOlder(true);
    if (olderRequestTimeoutRef.current) {
      clearTimeout(olderRequestTimeoutRef.current);
    }
    olderRequestTimeoutRef.current = setTimeout(() => {
      if (activeHistoryRequestRef.current?.requestId === requestMeta.requestId) {
        activeHistoryRequestRef.current = null;
      }
      olderRequestTimeoutRef.current = null;
      isLoadingOlderRef.current = false;
      setIsLoadingOlder(false);
      console.warn('[chat:ws] loadOlder:timeout', {
        request_id: requestMeta.requestId,
        before_id: firstId,
      });
    }, 5000);
  }, [hasMoreOlder, isLoading, messages, requestSubscribe]);

  const handleReplyPreviewClick = useCallback((event, message) => {
    event.preventDefault();
    event.stopPropagation();

    const replyPayload = message?.reply_to || null;
    const isReplyDeleted = Boolean(replyPayload?.deleted || replyPayload?.deleted_at);
    if (isReplyDeleted) {
      showChatNotice('Исходное сообщение удалено', 'info');
      return;
    }

    const targetId = Number(replyPayload?.id || message?.reply_to_message_id || 0);
    if (!targetId) return;

    const foundInLoaded = scrollToMessageById(targetId, 'smooth');
    if (foundInLoaded) {
      setPendingReplyJumpId(0);
      replyJumpLoadAttemptsRef.current = 0;
      return;
    }

    if (!hasMoreOlder) {
      showChatNotice('Исходное сообщение не найдено', 'error');
      return;
    }

    setPendingReplyJumpId(targetId);
    replyJumpLoadAttemptsRef.current = 0;
  }, [hasMoreOlder, scrollToMessageById, showChatNotice]);

  useEffect(() => {
    const targetId = Number(pendingReplyJumpId || 0);
    if (!targetId) return;

    if (scrollToMessageById(targetId, 'smooth')) {
      setPendingReplyJumpId(0);
      replyJumpLoadAttemptsRef.current = 0;
      return;
    }

    if (isLoading || isLoadingOlderRef.current || isLoadingOlder) return;

    if (!hasMoreOlder) {
      setPendingReplyJumpId(0);
      replyJumpLoadAttemptsRef.current = 0;
      showChatNotice('Не удалось найти исходное сообщение в истории', 'error');
      return;
    }

    if (replyJumpLoadAttemptsRef.current >= MAX_REPLY_JUMP_LOADS) {
      setPendingReplyJumpId(0);
      replyJumpLoadAttemptsRef.current = 0;
      showChatNotice('Сообщение слишком старое, дойдите до него через историю', 'error');
      return;
    }

    replyJumpLoadAttemptsRef.current += 1;
    loadOlder();
  }, [hasMoreOlder, isLoading, isLoadingOlder, loadOlder, pendingReplyJumpId, scrollToMessageById, showChatNotice]);

  const sendMessage = async () => {
    if (isSendingMessage || isUploadingPhoto) return;

    const rawInputValue = String(inputValue || '');
    const body = rawInputValue.trim();
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      setErrorText('Нет соединения с чатом');
      return;
    }

    const editingId = Number(editingMessage?.id || 0);
    if (editingId > 0) {
      const originalBody = String(editingMessage?.body || '').trim();
      if (body === originalBody) {
        clearEditingDraft();
        return;
      }

      const payload = {
        type: 'chat:edit',
        message_id: editingId,
        body,
      };
      try {
        pendingEditMessageIdRef.current = editingId;
        setIsEditingMessageSaving(true);
        ws.send(JSON.stringify(payload));
        console.debug('[chat:ws] edit:request', { message_id: editingId });
      } catch (ex) {
        pendingEditMessageIdRef.current = 0;
        setIsEditingMessageSaving(false);
        console.error('edit chat ws error', ex);
        setErrorText('Не удалось отредактировать сообщение');
      }
      return;
    }

    if (!body && !hasAttachedPhoto) return;

    let preparedPhoto = attachedPhoto;
    if (hasAttachedPhoto && !(attachedPhoto?.photoMediaId && attachedPhoto?.photoThumbMediaId)) {
      if (!attachedPhoto?.file) {
        setErrorText('Не удалось прикрепить фото. Выберите его заново.');
        return;
      }

      try {
        setIsSendingMessage(true);
        setIsUploadingPhoto(true);
        const uploadResult = await uploadMedia({
          profile: 'chat',
          file: attachedPhoto.file,
        });
        if (!uploadResult?.status || !uploadResult?.full?.id || !uploadResult?.thumb?.id) {
          throw new Error('Не удалось загрузить фото');
        }

        preparedPhoto = {
          ...attachedPhoto,
          photoMediaId: Number(uploadResult.full.id),
          photoThumbMediaId: Number(uploadResult.thumb.id),
          photoUrl: String(uploadResult.full.url || '').trim() || null,
          photoThumbUrl: String(uploadResult.thumb.url || '').trim() || null,
        };
        setAttachedPhoto(preparedPhoto);
      } catch (ex) {
        console.error('chat photo upload failed', ex);
        setIsSendingMessage(false);
        setErrorText(ex?.message || 'Не удалось загрузить фото');
        return;
      } finally {
        setIsUploadingPhoto(false);
      }
    }

    const payload = {
      type: 'chat:send',
      body,
      reply_to_message_id: replyTo?.id || null,
      client_msg_id: `${Date.now()}-${Math.random().toString(16).slice(2, 10)}`,
      photo_media_id: preparedPhoto?.photoMediaId || null,
      photo_thumb_media_id: preparedPhoto?.photoThumbMediaId || null,
    };
    try {
      pendingSendRef.current = {
        clientMsgId: payload.client_msg_id,
        body,
        inputValue: rawInputValue,
        replyToId: replyTo?.id || null,
        hadPhoto: Boolean(payload.photo_media_id || payload.photo_thumb_media_id),
      };
      rememberOwnSentClientMsgId(payload.client_msg_id);
      setIsSendingMessage(true);
      if (pendingSendTimeoutRef.current) {
        clearTimeout(pendingSendTimeoutRef.current);
      }
      pendingSendTimeoutRef.current = setTimeout(() => {
        const pending = pendingSendRef.current;
        if (!pending || pending.clientMsgId !== payload.client_msg_id) return;
        clearPendingSend(payload.client_msg_id);
        setErrorText('Сервер не подтвердил отправку. Сообщение осталось в поле ввода.');
        console.warn('[chat:ws] send:ack_timeout', { client_msg_id: payload.client_msg_id });
      }, 15000);
      ws.send(JSON.stringify(payload));
      console.debug('[chat:ws] send:request', { client_msg_id: payload.client_msg_id });
    } catch (ex) {
      clearPendingSend(payload.client_msg_id);
      forgetOwnSentClientMsgId(payload.client_msg_id);
      console.error('send chat ws error', ex);
      setErrorText('Не удалось отправить сообщение');
    }
  };

  const closeSellerModal = useCallback(() => {
    sellerItemsRequestSeqRef.current += 1;
    sellerItemsLoadingRef.current = false;
    if (sellerCopyNoticeTimerRef.current) {
      clearTimeout(sellerCopyNoticeTimerRef.current);
      sellerCopyNoticeTimerRef.current = null;
    }
    setSellerCopyNotice('');
    setSellerModal({
      show: false,
      sellerId: null,
      profile: null,
      items: [],
      loading: false,
      loadingMore: false,
      hasMore: false,
      selectedItem: null,
    });
  }, []);

  const showSellerCopyNotice = useCallback((text) => {
    if (sellerCopyNoticeTimerRef.current) {
      clearTimeout(sellerCopyNoticeTimerRef.current);
    }
    setSellerCopyNotice(text);
    sellerCopyNoticeTimerRef.current = setTimeout(() => {
      sellerCopyNoticeTimerRef.current = null;
      setSellerCopyNotice('');
    }, 1600);
  }, []);

  const handleSellerHandleCopy = useCallback(async () => {
    const handle = normalizeHandle(sellerModal.profile?.chat_handle);
    if (!handle) return;
    try {
      await copyTextToClipboard(`@${handle}`);
      showSellerCopyNotice('Профиль скопирован');
    } catch (ex) {
      console.error('copy seller handle failed', ex);
      showSellerCopyNotice('Не удалось скопировать');
    }
  }, [sellerModal.profile?.chat_handle, showSellerCopyNotice]);

  const openSeller = async (author) => {
    if (!author?.account_id) return;
    const sellerId = Number(author.account_id || 0);
    if (!sellerId) return;
    const requestSeq = sellerItemsRequestSeqRef.current + 1;
    sellerItemsRequestSeqRef.current = requestSeq;
    sellerItemsLoadingRef.current = true;
    setSellerModal({
      show: true,
      sellerId,
      profile: author,
      items: [],
      loading: true,
      loadingMore: false,
      hasMore: true,
      selectedItem: null,
    });
    try {
      const resp = await getMarketItemsBySeller(sellerId, { limit: SELLER_ITEMS_PAGE_SIZE });
      const nextItems = Array.isArray(resp?.items) ? resp.items : [];
      setSellerModal((prev) => {
        if (sellerItemsRequestSeqRef.current !== requestSeq || Number(prev.sellerId || 0) !== sellerId) {
          return prev;
        }
        return {
          ...prev,
          items: nextItems,
          loading: false,
          hasMore: nextItems.length >= SELLER_ITEMS_PAGE_SIZE,
        };
      });
    } catch (ex) {
      console.error('openSeller error', ex);
      setSellerModal((prev) => (
        sellerItemsRequestSeqRef.current === requestSeq && Number(prev.sellerId || 0) === sellerId
          ? { ...prev, loading: false, hasMore: false }
          : prev
      ));
    } finally {
      if (sellerItemsRequestSeqRef.current === requestSeq) {
        sellerItemsLoadingRef.current = false;
      }
    }
  };

  const loadMoreSellerItems = useCallback(async () => {
    const sellerId = Number(sellerModal.sellerId || sellerModal.profile?.account_id || 0);
    if (
      !sellerId
      || sellerItemsLoadingRef.current
      || sellerModal.loading
      || sellerModal.loadingMore
      || !sellerModal.hasMore
      || sellerModal.items.length === 0
    ) {
      return;
    }
    const beforeId = sellerModal.items[sellerModal.items.length - 1]?.id;
    if (!beforeId) return;

    const requestSeq = sellerItemsRequestSeqRef.current;
    sellerItemsLoadingRef.current = true;
    setSellerModal((prev) => (
      Number(prev.sellerId || prev.profile?.account_id || 0) === sellerId
        ? { ...prev, loadingMore: true }
        : prev
    ));
    try {
      const resp = await getMarketItemsBySeller(sellerId, {
        limit: SELLER_ITEMS_PAGE_SIZE,
        beforeId,
      });
      const nextItems = Array.isArray(resp?.items) ? resp.items : [];
      setSellerModal((prev) => {
        const sameSeller = Number(prev.sellerId || prev.profile?.account_id || 0) === sellerId;
        if (sellerItemsRequestSeqRef.current !== requestSeq || !prev.show || !sameSeller) return prev;
        return {
          ...prev,
          items: appendUniqueById(prev.items, nextItems),
          loadingMore: false,
          hasMore: nextItems.length >= SELLER_ITEMS_PAGE_SIZE,
        };
      });
    } catch (ex) {
      console.error('loadMoreSellerItems error', ex);
      setSellerModal((prev) => (
        sellerItemsRequestSeqRef.current === requestSeq
          && Number(prev.sellerId || prev.profile?.account_id || 0) === sellerId
          ? { ...prev, loadingMore: false }
          : prev
      ));
    } finally {
      if (sellerItemsRequestSeqRef.current === requestSeq) {
        sellerItemsLoadingRef.current = false;
      }
    }
  }, [getMarketItemsBySeller, sellerModal.hasMore, sellerModal.items, sellerModal.loading, sellerModal.loadingMore, sellerModal.profile, sellerModal.sellerId]);

  const handleSellerProductsScroll = useCallback(() => {
    if (sellerProductsScrollRafRef.current) return;
    sellerProductsScrollRafRef.current = window.requestAnimationFrame(() => {
      sellerProductsScrollRafRef.current = null;
      const container = sellerProductsScrollRef.current;
      if (!container || sellerItemsLoadingRef.current || sellerModal.loading || !sellerModal.hasMore) return;
      const distanceToBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
      if (distanceToBottom <= BOTTOM_THRESHOLD_PX) {
        void loadMoreSellerItems();
      }
    });
  }, [loadMoreSellerItems, sellerModal.hasMore, sellerModal.loading]);

  const handleScroll = useCallback(() => {
    if (scrollRafRef.current) return;

    scrollRafRef.current = window.requestAnimationFrame(() => {
      scrollRafRef.current = null;
      const container = chatScrollRef.current;
      const list = Array.isArray(messagesRef.current) ? messagesRef.current : [];
      if (!container || !list.length) return;

      if (container.scrollTop <= TOP_FETCH_THRESHOLD_PX) {
        loadOlder();
      }

      const lastId = Number(list[list.length - 1]?.id || 0);
      if (['search_context', 'unread_context'].includes(chatHistoryModeRef.current)) {
        const contextNewerSuppressed = Date.now() < Number(suppressContextNewerUntilRef.current || 0);
        if (!contextNewerSuppressed && isNearBottom(container)) {
          requestNewerFromContext();
        }
        if (chatHistoryModeRef.current === 'unread_context') {
          const visibleMessageId = getHighestVisibleMessageId();
          if (visibleMessageId > seenMessageIdRef.current) {
            seenMessageIdRef.current = visibleMessageId;
            consumeUnreadThrough(visibleMessageId);
            syncReadState(visibleMessageId);
          }
        }
        setShowScrollDown(true);
        return;
      }

      if (isNearBottom(container)) {
        if (lastId > seenMessageIdRef.current) {
          seenMessageIdRef.current = lastId;
        }
        setNewBelowCount(0);
        setShowScrollDown(false);
        if (lastId) {
          syncReadState(lastId, { immediate: true });
        }
        return;
      }

      const visibleMessageId = getHighestVisibleMessageId();
      if (visibleMessageId > seenMessageIdRef.current) {
        seenMessageIdRef.current = visibleMessageId;
        syncReadState(visibleMessageId);
      }

      const unseenCount = countMessagesAfterId(seenMessageIdRef.current, list);
      setNewBelowCount(unseenCount);
      setShowScrollDown(unseenCount > 0 || list.length > 0);
    });
  }, [countMessagesAfterId, getHighestVisibleMessageId, isNearBottom, loadOlder, requestNewerFromContext, syncReadState]);

  const handleScrollDownClick = useCallback(() => {
    if (chatHistoryModeRef.current === 'unread_context') {
      const firstUnreadId = Number(firstUnreadMessageIdRef.current || 0);
      const highestVisibleId = getHighestVisibleMessageId();
      const list = Array.isArray(messagesRef.current) ? messagesRef.current : [];
      const firstUnreadIndex = list.findIndex((msg) => Number(msg?.id || 0) === firstUnreadId);
      const highestVisibleIndex = list.findIndex((msg) => Number(msg?.id || 0) === highestVisibleId);
      const isFarAboveFirstUnread = (
        firstUnreadId > 0
        && highestVisibleId > 0
        && firstUnreadIndex >= 0
        && highestVisibleIndex >= 0
        && highestVisibleIndex < firstUnreadIndex - 10
      );
      if (
        isFarAboveFirstUnread
        && scrollToMessageById(firstUnreadId, 'smooth')
      ) {
        suppressContextNewerAfterProgrammaticScroll(800);
        return;
      }
      suppressContextNewerAfterProgrammaticScroll(1000);
      returnToLatestMessages();
      return;
    }
    if (chatHistoryModeRef.current === 'search_context') {
      suppressContextNewerAfterProgrammaticScroll(1000);
      returnToLatestMessages();
      return;
    }
    suppressContextNewerAfterProgrammaticScroll(500);
    scrollToBottom('smooth');
  }, [getHighestVisibleMessageId, returnToLatestMessages, scrollToBottom, scrollToMessageById, suppressContextNewerAfterProgrammaticScroll]);

  const isInactiveSubscription = userInfo?.status === false;
  const isChatLocked = isInactiveSubscription || subscriptionEnded;
  const composerSpace = isChatLocked ? 18 : composerHeight;
  const subscriptionNoticeTitle = subscriptionEnded ? 'Подписка закончилась' : 'У вас неактивна подписка';
  const subscriptionNoticeText = subscriptionEnded
    ? 'Пожалуйста, оформите подписку, чтобы снова получить доступ к чату.'
    : 'Пожалуйста, оформите подписку, чтобы получить доступ к чату.';
  const chatNoticeThemeClass = isDark ? 'text-white bg-dark' : 'text-dark bg-light';
  const chatSubscriptionCardClass = `d-flex align-items-center justify-content-center text-center ${chatNoticeThemeClass} ws-subscription-card ${isDark ? 'ws-subscription-card--dark' : 'ws-subscription-card--light'} chat-page-subscription-card`;

  useEffect(() => {
    if (!errorText || isChatLocked) return;
    showChatNotice(errorText, 'error');
    setErrorText('');
  }, [errorText, isChatLocked, showChatNotice]);

  const headerStatus = useMemo(() => {
    if (!isConnected) return 'Соединение...';
    if (onlineUsers > 0) return `В сети: ${onlineUsers}`;
    return 'В сети: 1';
  }, [isConnected, onlineUsers]);
  const chatPushModeLabel = useMemo(() => getChatPushModeLabel(chatPushMode), [chatPushMode]);
  const chatBackgroundImage = useMemo(() => {
    if (isDark) {
      return `${process.env.PUBLIC_URL}/${isLandscape ? 'backchatblacklandscape.png' : 'backchatblack.png'}`;
    }
    return `${process.env.PUBLIC_URL}/${isLandscape ? 'backchatwhitelandscape.png' : 'backchatwhite.png'}`;
  }, [isDark, isLandscape]);
  const useDesktopUnifiedRows = isLandscape && isWideDesktopLayout;
  const authorsByHandle = useMemo(() => {
    const map = new Map();
    const rememberAuthor = (author) => {
      if (!author || typeof author !== 'object') return;
      const handle = normalizeHandle(author.chat_handle);
      const accountId = Number(author.account_id || 0);
      if (!handle || accountId <= 0) return;
      if (!map.has(handle)) {
        map.set(handle, author);
      }
    };
    (messages || []).forEach((message) => {
      rememberAuthor(message?.author);
      rememberAuthor(message?.reply_to?.author);
      const mentionProfiles = Array.isArray(message?.mention_profiles) ? message.mention_profiles : [];
      mentionProfiles.forEach((entry) => rememberAuthor(entry?.profile));
    });
    return map;
  }, [messages]);
  const resolvedMyChatHandle = useMemo(() => {
    if (myChatHandle) return myChatHandle;
    for (const message of (messages || [])) {
      if (Number(message?.author_account_id || 0) !== myUserId) continue;
      const handle = normalizeHandle(message?.author?.chat_handle);
      if (handle) return handle;
    }
    return '';
  }, [messages, myChatHandle, myUserId]);
  const myFallbackAuthor = useMemo(() => {
    if (!myUserId) return null;
    const fallbackName = String(
      userInfo?.display_name || userInfo?.first_name || userInfo?.name || ''
    ).trim();
    return {
      account_id: myUserId,
      display_name: fallbackName || `User ${myUserId}`,
      chat_handle: resolvedMyChatHandle || null,
    };
  }, [myUserId, resolvedMyChatHandle, userInfo]);
  const swipePreviewMessageId = Number(swipePreview.messageId || 0);
  const sellerProfile = sellerModal.profile || null;
  const sellerName = sellerProfile ? getDisplayName(sellerProfile, sellerProfile.account_id) : '';
  const sellerAvatarUrl = String(sellerProfile?.avatar_thumb_url || sellerProfile?.avatar_url || '').trim();
  const sellerAvatarFullUrl = String(sellerProfile?.avatar_url || sellerProfile?.avatar_thumb_url || '').trim();
  const sellerInitials = getInitials(sellerName);
  const sellerAvatarColor = pickAvatarColor(sellerProfile?.account_id || sellerName);
  const selectedSellerItem = sellerModal.selectedItem || null;
  const selectedSellerContactLink = selectedSellerItem
    ? (selectedSellerItem.effective_contact_link || selectedSellerItem.contact_link || sellerProfile?.contact_link || '')
    : '';
  const isEditingMessage = Number(editingMessage?.id || 0) > 0;
  const editingMessageHasPhoto = messageHasPhoto(editingMessage);
  const normalizedInputValue = String(inputValue || '').trim();
  const normalizedEditingBody = String(editingMessage?.body || '').trim();
  const hasAttachedPhoto = Boolean(attachedPhoto?.file || attachedPhoto?.photoMediaId || attachedPhoto?.photoThumbMediaId);
  const promoIntent = useMemo(() => detectPromoIntent(inputValue), [inputValue]);
  const promoModeBlocked = Boolean(isEditingMessage || replyTo || hasAttachedPhoto);
  const isPromoMode = !promoModeBlocked
    && promoIntent.active
    && Boolean(promoIntent.normalized)
    && promoIntent.normalized !== promoDismissedQuery;
  const promoHintText = promoIntent.query
    ? `Запрос уйдет не в чат, а в подборщик: ${promoIntent.query}`
    : '';
  const isComposerSubmitDisabled = isEditingMessage
    ? isEditingMessageSaving
      || isSendingMessage
      || isUploadingPhoto
      || (!normalizedInputValue && !editingMessageHasPhoto)
      || normalizedInputValue === normalizedEditingBody
    : isSendingMessage
      || isUploadingPhoto
      || (!normalizedInputValue && !hasAttachedPhoto);

  const dismissPromoMode = useCallback(() => {
    if (!promoIntent.normalized) return;
    setPromoDismissedQuery(promoIntent.normalized);
  }, [promoIntent.normalized]);

  const handlePromoSearch = useCallback(async () => {
    if (!isPromoMode || isPromoLoading || !promoIntent.query) return;

    try {
      setIsPromoLoading(true);
      const result = await searchPromoAssistant(promoIntent.query);
      const markets = Array.isArray(result?.markets) ? result.markets : [];
      const firstMarket = markets[0]?.name ? { [markets[0].name]: true } : {};
      setPromoExpandedMarkets(firstMarket);
      setPromoResult({
        status: Boolean(result?.status),
        query: String(result?.query || promoIntent.query),
        comment: String(result?.comment || '').trim(),
        markets,
        allPromosUrl: String(result?.all_promos_url || '').trim(),
      });
      setInputValue('');
      setPromoDismissedQuery('');
    } catch (ex) {
      setErrorText(ex?.message || 'Не удалось подобрать промокоды');
    } finally {
      setIsPromoLoading(false);
    }
  }, [isPromoLoading, isPromoMode, promoIntent.query, searchPromoAssistant]);

  const closeChatImageViewer = useCallback(() => {
    chatImageViewerTouchRef.current = {
      active: false,
      startX: 0,
      startY: 0,
      lastY: 0,
    };
    setChatImageViewerDragY(0);
    setChatImageViewer({ show: false, src: '', alt: '' });
  }, []);

  const openChatImageViewer = useCallback((src, alt = 'Фото') => {
    const normalizedSrc = String(src || '').trim();
    if (!normalizedSrc) return;
    setChatImageViewerDragY(0);
    setChatImageViewer({
      show: true,
      src: normalizedSrc,
      alt: String(alt || 'Фото'),
    });
  }, []);


  const openChatMediaViewer = useCallback((index) => {
    setIsChatMediaTrackDragging(false);
    setChatMediaViewerTrackX(-33.333333);
    setChatMediaViewer({
      show: true,
      index: Math.max(0, Number(index || 0)),
    });
  }, []);

  const closeChatMediaViewer = useCallback(() => {
    setIsChatMediaTrackDragging(false);
    setChatMediaViewerTrackX(-33.333333);
    setChatMediaViewer((prev) => ({ ...prev, show: false }));
  }, []);

  const moveChatMediaViewer = useCallback((direction) => {
    // After a completed swipe the track is already on the side slide.
    // Reset it to the center without transition, otherwise the new slide
    // visibly "returns" back after index change.
    setIsChatMediaTrackDragging(true);
    setChatMediaViewerTrackX(-33.333333);
    setChatMediaViewer((prev) => {
      const nextIndex = Math.max(0, Math.min(mediaResults.length - 1, Number(prev.index || 0) + direction));
      if (direction > 0 && nextIndex >= mediaResults.length - 2 && mediaNextCursor && !isMediaLoading) {
        requestChatMedia({ append: true, cursor: mediaNextCursor });
      }
      return { ...prev, index: nextIndex };
    });
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setIsChatMediaTrackDragging(false);
      });
    });
  }, [isMediaLoading, mediaNextCursor, mediaResults.length, requestChatMedia]);

  const handleChatMediaViewerTouchStart = useCallback((event) => {
    const touch = event?.touches?.[0];
    if (!touch) return;
    setIsChatMediaTrackDragging(true);
    setChatMediaViewerTrackX(-33.333333);
    chatMediaViewerTouchRef.current = {
      active: true,
      startX: touch.clientX,
      startY: touch.clientY,
      lastX: 0,
    };
  }, []);

  const handleChatMediaViewerTouchMove = useCallback((event) => {
    const state = chatMediaViewerTouchRef.current;
    if (!state.active) return;
    const touch = event?.touches?.[0];
    if (!touch) return;

    const deltaX = touch.clientX - state.startX;
    const deltaY = touch.clientY - state.startY;
    if (Math.abs(deltaX) <= Math.abs(deltaY) || Math.abs(deltaX) < 10) return;

    const stageWidth = event.currentTarget?.clientWidth || 1;
    const limitedDelta = Math.max(-stageWidth, Math.min(stageWidth, deltaX));
    state.lastX = limitedDelta;
    event.preventDefault();
    setChatMediaViewerTrackX(-33.333333 + (limitedDelta / stageWidth) * 33.333333);
  }, []);

  const handleChatMediaViewerTouchEnd = useCallback(() => {
    const { lastX } = chatMediaViewerTouchRef.current;
    chatMediaViewerTouchRef.current = {
      active: false,
      startX: 0,
      startY: 0,
      lastX: 0,
    };

    const resetTrack = () => {
      setIsChatMediaTrackDragging(false);
      setChatMediaViewerTrackX(-33.333333);
    };

    if (Math.abs(lastX) < 58) {
      resetTrack();
      return;
    }

    const direction = lastX < 0 ? 1 : -1;
    const currentIndex = Number(chatMediaViewer.index || 0);
    if (direction < 0 && currentIndex <= 0) {
      resetTrack();
      return;
    }
    if (direction > 0 && currentIndex >= mediaResults.length - 1 && !mediaNextCursor) {
      resetTrack();
      return;
    }

    if (direction > 0 && currentIndex >= mediaResults.length - 1 && mediaNextCursor) {
      requestChatMedia({ append: true, cursor: mediaNextCursor });
      resetTrack();
      return;
    }

    setIsChatMediaTrackDragging(false);
    setChatMediaViewerTrackX(direction > 0 ? -66.666666 : 0);
    window.setTimeout(() => {
      moveChatMediaViewer(direction);
    }, 210);
  }, [chatMediaViewer.index, mediaNextCursor, mediaResults.length, moveChatMediaViewer, requestChatMedia]);

  const goToChatMediaMessage = useCallback((item) => {
    const messageId = Number(item?.message_id || 0);
    if (!messageId) return;
    setChatMediaViewer((prev) => ({ ...prev, show: false }));
    setIsSearchOpen(false);
    requestAroundMessage(messageId, { source: 'media' });
  }, [requestAroundMessage]);

  const openChatProductLink = useCallback((productEmbed) => {
    const href = String(productEmbed?.source_url || '').trim();
    if (!href) return;
    window.open(href, '_blank', 'noopener,noreferrer');
  }, []);

  const openChatProductGallery = useCallback((images, startIndex = 0, title = 'Товар') => {
    const normalizedImages = (Array.isArray(images) ? images : [])
      .map((item) => String(item || '').trim())
      .filter(Boolean);
    if (!normalizedImages.length) return;

    const safeIndex = Math.max(0, Math.min(Number(startIndex || 0), normalizedImages.length - 1));
    setChatProductGallery({
      show: true,
      images: normalizedImages,
      activeIndex: safeIndex,
      title: String(title || 'Товар').trim() || 'Товар',
    });
  }, []);

  const closeChatProductGallery = useCallback(() => {
    setChatProductGallery({
      show: false,
      images: [],
      activeIndex: 0,
      title: '',
    });
  }, []);

  const openChatProductChart = useCallback((diapaz, title = 'Диапазон цен') => {
    const normalizedDiapaz = normalizeChatProductDiapaz(diapaz);
    if (normalizedDiapaz.length === 0) return;
    setChatProductChart({
      show: true,
      title: String(title || 'Диапазон цен').trim() || 'Диапазон цен',
      diapaz: normalizedDiapaz,
    });
    setChatProductChartPeriod('all');
  }, []);

  const closeChatProductChart = useCallback(() => {
    setChatProductChart({
      show: false,
      title: '',
      diapaz: [],
    });
    setChatProductChartPeriod('all');
  }, []);

  const handleChatImageViewerTouchStart = useCallback((event) => {
    const touch = event?.touches?.[0];
    if (!touch) return;
    chatImageViewerTouchRef.current = {
      active: true,
      startX: touch.clientX,
      startY: touch.clientY,
      lastY: 0,
    };
    setChatImageViewerDragY(0);
  }, []);

  const handleChatImageViewerTouchMove = useCallback((event) => {
    const state = chatImageViewerTouchRef.current;
    if (!state.active) return;
    const touch = event?.touches?.[0];
    if (!touch) return;

    const deltaX = touch.clientX - state.startX;
    const deltaY = touch.clientY - state.startY;
    if (Math.abs(deltaY) <= Math.abs(deltaX) || deltaY <= 0) {
      return;
    }

    state.lastY = deltaY;
    event.preventDefault();
    setChatImageViewerDragY(Math.min(deltaY, CHAT_IMAGE_VIEWER_MAX_DRAG_PX));
  }, []);

  const handleChatImageViewerTouchEnd = useCallback(() => {
    const { lastY } = chatImageViewerTouchRef.current;
    chatImageViewerTouchRef.current = {
      active: false,
      startX: 0,
      startY: 0,
      lastY: 0,
    };

    if (lastY >= CHAT_IMAGE_VIEWER_CLOSE_SWIPE_PX) {
      closeChatImageViewer();
      return;
    }

    setChatImageViewerDragY(0);
  }, [closeChatImageViewer]);

  const closePromoResult = useCallback(() => {
    if (promoCopyNoticeTimerRef.current) {
      clearTimeout(promoCopyNoticeTimerRef.current);
      promoCopyNoticeTimerRef.current = null;
    }
    setPromoCopyNotice('');
    setPromoResult(null);
    setPromoExpandedMarkets({});
  }, []);

  const togglePromoMarket = useCallback((marketName) => {
    const normalizedName = String(marketName || '').trim();
    if (!normalizedName) return;
    setPromoExpandedMarkets((prev) => ({
      ...prev,
      [normalizedName]: !prev[normalizedName],
    }));
  }, []);

  const handlePromoCodeCopy = useCallback(async (code) => {
    try {
      await copyTextToClipboard(code);
      showGlassToast('Промокод скопирован', { duration: 1400 });
    } catch (ex) {
      setErrorText('Не удалось скопировать промокод');
    }
  }, []);

  const handleChatPhotoLoad = useCallback((messageId, event) => {
    const id = String(messageId || '').trim();
    const image = event?.currentTarget;
    const naturalWidth = Number(image?.naturalWidth || 0);
    const naturalHeight = Number(image?.naturalHeight || 0);
    if (!id || naturalWidth <= 0 || naturalHeight <= 0) return;
    const nextRatio = Number((naturalWidth / naturalHeight).toFixed(3));
    setChatPhotoRatios((prev) => {
      if (prev[id] === nextRatio) return prev;
      return {
        ...prev,
        [id]: nextRatio,
      };
    });
  }, []);

  const renderChatMessage = (_virtualIndex, msg) => {
    const isMine = Number(msg.author_account_id) === myUserId;
    const author = msg.author || {};
    const authorName = getDisplayName(author, author.account_id);
    const authorAvatarUrl = String(author.avatar_thumb_url || author.avatar_url || '').trim();
    const authorInitials = getInitials(authorName);
    const authorAvatarColor = pickAvatarColor(author.account_id || authorName);
    const replyAuthor = msg.reply_to?.author || null;
    const replyAuthorName = getDisplayName(replyAuthor, msg.reply_to?.author_account_id);
    const isReplyDeleted = Boolean(msg.reply_to?.deleted || msg.reply_to?.deleted_at);
    const mentionProfiles = Array.isArray(msg.mention_profiles) ? msg.mention_profiles : [];
    const mentionProfileMap = new Map();
    mentionProfiles.forEach((entry) => {
      const handle = normalizeHandle(entry?.handle || entry?.profile?.chat_handle);
      const accountId = Number(entry?.account_id || entry?.profile?.account_id || 0);
      if (!handle || accountId <= 0) return;
      if (!mentionProfileMap.has(handle)) {
        mentionProfileMap.set(handle, {
          ...(entry?.profile || {}),
          account_id: accountId,
          chat_handle: entry?.profile?.chat_handle || handle,
        });
      }
    });
    const mentionHandles = Array.isArray(msg.mentions)
      ? msg.mentions.map((raw) => normalizeHandle(raw)).filter(Boolean)
      : [];
    const isMentionedForMe = !isMine
      && Boolean(resolvedMyChatHandle)
      && mentionHandles.includes(resolvedMyChatHandle);
    const isReplyForMe = isReplyToMe(msg);
    const handleMentionClick = (event, handle) => {
      event.preventDefault();
      event.stopPropagation();
      const normalizedHandle = normalizeHandle(handle);
      if (!normalizedHandle) return;
      const targetAuthor = mentionProfileMap.get(normalizedHandle)
        || authorsByHandle.get(normalizedHandle)
        || (normalizedHandle === resolvedMyChatHandle ? myFallbackAuthor : null);
      if (targetAuthor?.account_id) {
        void openSeller(targetAuthor);
      }
    };
    const isSwipeTarget = swipePreviewMessageId === Number(msg.id || 0);
    const swipeOffsetX = isSwipeTarget ? Number(swipePreview.offsetX || 0) : 0;
    const swipeAction = isSwipeTarget ? String(swipePreview.action || '') : '';
    const showSwipeIndicator = Boolean(isSwipeTarget && swipeAction);
    const swipeSideClass = isMine ? 'chat-msg-swipe-indicator--left' : 'chat-msg-swipe-indicator--right';
    const isDeletingMessage = deletingMessageIds.includes(Number(msg.id || 0));
    const messagePhotoThumbUrl = getMessagePhotoThumbUrl(msg);
    const messagePhotoUrl = getMessagePhotoUrl(msg);
    const hasMessagePhoto = Boolean(messagePhotoThumbUrl || messagePhotoUrl);
    const productEmbed = msg.product_embed || null;
    const productSnapshot = productEmbed?.snapshot || null;
    const hasProductEmbed = Boolean(productEmbed && productSnapshot);
    const channelPostEmbed = msg.channel_post_embed || null;
    const hasChannelPostEmbed = Boolean(channelPostEmbed);
    const channelPostPhotoRatio = Number(channelPostEmbed?.photo_ratio || 0);
    const channelPostPhotoStyle = channelPostPhotoRatio > 0
      ? { '--channel-post-photo-ratio': `${Math.min(1.55, Math.max(0.62, channelPostPhotoRatio))}` }
      : undefined;
    const productPhotos = hasProductEmbed ? parseChatProductPhotos(productSnapshot.linkphoto) : [];
    const productPhotoUrl = String(productPhotos[0] || '/no-photo.webp').trim();
    const productMarketplace = String(productSnapshot?.marketplace || productEmbed?.marketplace || 'wb').trim().toLowerCase();
    const productSizes = hasProductEmbed ? parseChatProductSizes(productSnapshot.sizes) : [];
    const productStocks = hasProductEmbed ? parseChatProductStocks(productSnapshot.sklad) : [];
    const productDiapaz = hasProductEmbed ? normalizeChatProductDiapaz(productSnapshot.diapaz) : [];
    const isWbProduct = productMarketplace === 'wb';
    const productRangeBounds = hasProductEmbed
      ? getChatProductRangeBounds(productDiapaz, productSnapshot.diapazmin, productSnapshot.diapazmax)
      : null;
    const productPriceText = hasProductEmbed ? formatChatProductPrice(productSnapshot.price2 || productSnapshot.price) : '';
    const productOldPriceText = hasProductEmbed ? formatChatProductPrice(productSnapshot.oldprice) : '';
    const productRangeText = productRangeBounds
      ? buildChatProductRangeText(productRangeBounds.min, productRangeBounds.max)
      : '';
    const productSubject = hasProductEmbed ? String(productSnapshot?.subject || '').trim() : '';
    const productFeedbackPoints = Number(productSnapshot?.feedbackpoints || 0);
    const productFeedbacks = Number(productSnapshot?.feedbacks || 0);
    const productRating = Number(productSnapshot?.rating || 0);
    const productOutOfStock = hasProductEmbed
      ? Boolean(productSnapshot?.out_of_stock) || Number(productSnapshot?.col || 0) <= 0
      : false;
    const productRangeChange = productDiapaz.length >= 2 ? getChatProductPriceChange(productDiapaz) : null;
    const photoRatio = chatPhotoRatios[String(msg.id || '')] || 0;
    const photoPreviewStyle = getChatPhotoPreviewStyle(photoRatio);
    const isActionMenuTarget = Boolean(messageMenu.open && messageMenu.mobile && Number(messageMenu.message?.id || 0) === Number(msg.id || 0));
    const messageReactions = (Array.isArray(msg.reactions) ? msg.reactions : [])
      .filter((entry) => CHAT_REACTION_KEYS.has(String(entry?.key || '').trim().toLowerCase()) && Number(entry?.count || 0) > 0);
    const renderedBody = renderMessageBody(msg, {
      currentUserHandle: resolvedMyChatHandle,
      onMentionClick: handleMentionClick,
    });
    const hasRenderedBody = Boolean(String(msg?.body || '').trim());
    const isPhotoOnlyMessage = hasMessagePhoto && !hasRenderedBody && !msg.reply_to;
    const channelPostBodyMessage = hasChannelPostEmbed
      ? { body: String(channelPostEmbed.body || '') }
      : null;

    return (
      <div
        key={msg.id}
        data-msg-id={msg.id}
        className={`chat-msg-row ${isMine ? 'chat-msg-row--mine' : 'chat-msg-row--other'} ${Number(msg.id) === Number(highlightedMessageId) ? 'chat-msg-row--jump-target' : ''} ${useDesktopUnifiedRows ? 'chat-msg-row--desktop-unified' : ''} ${isDeletingMessage ? 'chat-msg-row--deleting' : ''} ${isActionMenuTarget ? 'chat-msg-row--action-target' : ''} ${(isMentionedForMe || isReplyForMe) ? 'chat-msg-row--notify-highlight' : ''}`}
      >
        {(!isMine || useDesktopUnifiedRows) && (
          <button
            type="button"
            className="chat-msg-avatar-btn"
            onClick={() => openSeller(author)}
            aria-label={`Профиль ${authorName}`}
          >
            {authorAvatarUrl ? (
              <img
                src={authorAvatarUrl}
                alt={authorName}
                className="chat-msg-avatar-img"
              />
            ) : (
              <span
                className="chat-msg-avatar-fallback"
                style={{ backgroundColor: authorAvatarColor }}
              >
                {authorInitials}
              </span>
            )}
          </button>
        )}

        <div
          className={`chat-msg-bubble ${isMine ? 'chat-msg-bubble--mine' : 'chat-msg-bubble--other'} ${hasMessagePhoto ? 'chat-msg-bubble--has-photo' : ''} ${hasProductEmbed ? 'chat-msg-bubble--has-product' : ''} ${hasChannelPostEmbed ? 'chat-msg-bubble--has-channel-post' : ''} ${isPhotoOnlyMessage ? 'chat-msg-bubble--photo-only' : ''} ${isMentionedForMe ? 'chat-msg-bubble--mentioned' : ''} ${isReplyForMe ? 'chat-msg-bubble--reply-for-me' : ''} ${isSwipeTarget ? 'chat-msg-bubble--swipe-active' : ''} ${isSwipeTarget && swipePreview.dragging ? 'chat-msg-bubble--swipe-dragging' : ''} ${isActionMenuTarget ? 'chat-msg-bubble--action-target' : ''}`}
          style={isSwipeTarget && swipeOffsetX ? { transform: `translate3d(${swipeOffsetX}px, 0, 0)` } : undefined}
          onPointerDown={(event) => handleMessagePointerDown(event, msg)}
          onPointerMove={clearLongPressTimer}
          onPointerUp={clearLongPressTimer}
          onPointerLeave={clearLongPressTimer}
          onPointerCancel={clearLongPressTimer}
          onTouchStart={(event) => handleMessageTouchStart(event, msg)}
          onTouchMove={(event) => handleMessageTouchMove(event, msg)}
          onTouchEnd={(event) => handleMessageTouchEnd(event, msg)}
          onTouchCancel={handleMessageTouchCancel}
          onDragStart={(event) => event.preventDefault()}
          onContextMenu={(event) => handleMessageContextMenu(event, msg)}
        >
          {!isMine && (
            <div className="chat-msg-author-wrap">
              <button
                type="button"
                className="chat-msg-author-btn"
                onClick={() => openSeller(author)}
              >
                {authorName}
              </button>
            </div>
          )}

          {msg.reply_to && (
            <button
              type="button"
              className={`chat-msg-reply-preview ${isReplyDeleted ? 'chat-msg-reply-preview--deleted' : ''}`}
              onClick={(event) => handleReplyPreviewClick(event, msg)}
              onPointerDown={(event) => event.stopPropagation()}
              onTouchStart={(event) => event.stopPropagation()}
              aria-label="Перейти к сообщению, на которое дан ответ"
            >
              <div className="chat-msg-reply-author">
                {isReplyDeleted ? 'Удалено' : replyAuthorName}
              </div>
              <div className="chat-msg-reply-text">
                {isReplyDeleted ? CHAT_DELETED_REPLY_TEXT : (msg.reply_to.body_preview || 'Сообщение')}
              </div>
            </button>
          )}

          {hasMessagePhoto && (
            <button
              type="button"
              className="chat-msg-photo-btn"
              style={photoPreviewStyle}
              onClick={() => openChatImageViewer(messagePhotoUrl || messagePhotoThumbUrl, authorName)}
              aria-label="Открыть фото"
            >
              <img
                src={messagePhotoThumbUrl || messagePhotoUrl}
                alt={authorName}
                className="chat-msg-photo-img"
                onLoad={(event) => handleChatPhotoLoad(msg.id, event)}
              />
            </button>
          )}

          {hasProductEmbed && (
            <div className={`chat-product-card ${isMine ? 'chat-product-card--mine' : 'chat-product-card--other'}`}>
              <div className="chat-product-card-media">
                {productPhotos.length > 0 ? (
                  <button
                    type="button"
                    className="chat-product-card-media-btn"
                    onClick={() => openChatProductGallery(productPhotos, 0, String(productSnapshot?.name || 'Товар'))}
                    onPointerDown={(event) => event.stopPropagation()}
                    onTouchStart={(event) => event.stopPropagation()}
                    aria-label="Открыть галерею товара"
                  >
                    <img
                      src={productPhotoUrl}
                      alt={String(productSnapshot?.name || 'Товар')}
                      className="chat-product-card-image"
                      loading="lazy"
                    />
                  </button>
                ) : (
                  <div className="chat-product-card-media-btn chat-product-card-media-btn--static">
                    <img
                      src={productPhotoUrl}
                      alt={String(productSnapshot?.name || 'Товар')}
                      className="chat-product-card-image"
                      loading="lazy"
                    />
                  </div>
                )}

                <div
                  className={`market-type-pill ${isDark ? 'market-type-pill--dark' : 'market-type-pill--light'} chat-product-card-market-pill`}
                  style={{ '--market-color': getChatProductMarketplaceColor(productMarketplace) }}
                >
                  <span className="market-type-pill__label">
                    {getChatProductMarketplaceLabel(productMarketplace)}
                  </span>
                </div>

                {(productRating > 0 || productFeedbacks > 0) && (
                  <div className={`rating-pill ${isDark ? 'rating-pill--dark' : 'rating-pill--light'} chat-product-card-rating-pill`}>
                    {productRating > 0 && (
                      <div className="rating-pill__stars">
                        {renderChatProductStars(productRating)}
                      </div>
                    )}
                    {productFeedbacks > 0 && (
                      <span className="rating-pill__count">{productRating > 0 ? ` | ${productFeedbacks}` : productFeedbacks}</span>
                    )}
                  </div>
                )}

                {isWbProduct && productFeedbackPoints > 0 && (
                  <div className="chat-product-card-feedback">
                    <Wallet2 size={12} />
                    <span>+{productFeedbackPoints}₽</span>
                  </div>
                )}

                {isWbProduct && productSnapshot?.isunrefusable && (
                  <div className="chat-product-card-return">Невозвратный</div>
                )}

                {isWbProduct && productSizes.length > 0 && (
                  <div className="sizes-scroll chat-product-card-sizes">
                    {productSizes.map((item, index) => (
                      <span
                        key={`${msg.id}-size-${index}`}
                        className={`size-tag size-tag--${item.color} chat-product-card-size`}
                      >
                        {item.text}
                      </span>
                    ))}
                  </div>
                )}

                {(isWbProduct ? (productStocks.length > 0 || productOutOfStock) : productOutOfStock) && (
                  <div className="chat-product-card-stocks">
                    {productOutOfStock && (
                      <span className="chat-product-card-stock chat-product-card-stock--out">
                        Нет в наличии
                      </span>
                    )}
                    {productStocks.map((item, index) => (
                      <span
                        key={`${msg.id}-stock-${index}`}
                        className={`chat-product-card-stock chat-product-card-stock--${item.color}`}
                      >
                        {item.text}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="chat-product-card-body">
                <div className="chat-product-card-topline">
                  <div className="chat-product-card-brand">
                    {String(productSnapshot?.brand || getChatProductMarketplaceLabel(productMarketplace))}
                  </div>
                  <button
                    type="button"
                    className="chat-product-card-open"
                    onClick={() => openChatProductLink(productEmbed)}
                    onPointerDown={(event) => event.stopPropagation()}
                    onTouchStart={(event) => event.stopPropagation()}
                    aria-label="Открыть товар"
                  >
                    <BoxArrowUpRight size={12} />
                    <span>Открыть</span>
                  </button>
                </div>

                <div className="chat-product-card-title">
                  {String(productSnapshot?.name || 'Товар')}
                </div>

                {(productPriceText || productOldPriceText) && (
                  <div className="chat-product-card-prices-row">
                    {productPriceText && (
                      <div className="chat-product-card-current-price">
                        {productPriceText}
                      </div>
                    )}
                    {productPriceText && productOldPriceText && productOldPriceText !== productPriceText && (
                      <div className="chat-product-card-old-price">
                        {productOldPriceText}
                      </div>
                    )}
                  </div>
                )}

                {productSubject && (
                  <div className="chat-product-card-subject">
                    {renderChatProductSubject(productSubject)}
                  </div>
                )}

                {isWbProduct && productRangeText ? (
                  <button
                    type="button"
                    className="chat-product-card-range-btn"
                    onClick={() => openChatProductChart(productDiapaz, String(productSnapshot?.name || 'Диапазон цен'))}
                    onPointerDown={(event) => event.stopPropagation()}
                    onTouchStart={(event) => event.stopPropagation()}
                    disabled={productDiapaz.length === 0}
                    aria-label="Открыть график диапазона цены"
                  >
                    <span className="chat-product-card-range-icon">📊</span>
                    <span className="diapaz-text chat-product-card-range-text">{productRangeText}</span>
                    {productRangeChange?.hasChange && (
                      <span
                        className={`chat-product-card-range-diff ${productRangeChange.isIncrease ? 'chat-product-card-range-diff--up' : 'chat-product-card-range-diff--down'}`}
                      >
                        {productRangeChange.isIncrease ? '↑' : '↓'}
                        {Math.abs(Number(productRangeChange.diff || 0)).toLocaleString('ru-RU')}₽
                      </span>
                    )}
                  </button>
                ) : isWbProduct ? (
                  <div className="chat-product-card-range-btn chat-product-card-range-btn--empty">
                    <span className="diapaz-text no-diapaz chat-product-card-range-text">Нет диапазона</span>
                  </div>
                ) : null}
              </div>
            </div>
          )}

          {hasChannelPostEmbed && (
            <div className={`chat-channel-post ${isDark ? 'chat-channel-post--dark' : 'chat-channel-post--light'}`}>
              {channelPostEmbed.photo_url && (
                <button
                  type="button"
                  className="chat-channel-post-photo-btn"
                  style={channelPostPhotoStyle}
                  onClick={() => openChatImageViewer(channelPostEmbed.photo_url, channelPostEmbed.title)}
                  onPointerDown={(event) => event.stopPropagation()}
                  onTouchStart={(event) => event.stopPropagation()}
                  aria-label="Открыть фото поста"
                >
                  <img
                    src={channelPostEmbed.photo_url}
                    alt={channelPostEmbed.title}
                    className="chat-channel-post-photo"
                    loading="lazy"
                  />
                </button>
              )}
              <div className="chat-channel-post-content">
                <div className="chat-channel-post-head">
                  <div
                    className={`market-type-pill ${isDark ? 'market-type-pill--dark' : 'market-type-pill--light'} chat-channel-post-market`}
                    style={{ '--market-color': getChatProductMarketplaceColor(channelPostEmbed.marketplace) }}
                  >
                    <span className="market-type-pill__label">
                      {getChatProductMarketplaceLabel(channelPostEmbed.marketplace)}
                    </span>
                  </div>
                  <span className="chat-channel-post-source">Промокоды</span>
                </div>
                <div className="chat-channel-post-title">
                  {channelPostEmbed.title}
                </div>
                {channelPostEmbed.body && (
                  <div className="chat-channel-post-body">
                    {renderMessageBody(channelPostBodyMessage, {
                      currentUserHandle: resolvedMyChatHandle,
                      onMentionClick: handleMentionClick,
                    })}
                  </div>
                )}
                {channelPostEmbed.source_url && (
                  <a
                    href={channelPostEmbed.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="chat-channel-post-open"
                    onClick={(event) => event.stopPropagation()}
                    onPointerDown={(event) => event.stopPropagation()}
                    onTouchStart={(event) => event.stopPropagation()}
                  >
                    <BoxArrowUpRight size={13} />
                    <span>Открыть ссылку</span>
                  </a>
                )}
              </div>
            </div>
          )}

          {hasRenderedBody && (
            <div className={`chat-msg-body ${(hasMessagePhoto || hasProductEmbed) ? 'chat-msg-body--with-photo' : ''}`}>
              {renderedBody}
            </div>
          )}
          {showSwipeIndicator && (
            <div
              className={`chat-msg-swipe-indicator ${swipeSideClass} ${swipeAction === 'delete' ? 'chat-msg-swipe-indicator--delete' : 'chat-msg-swipe-indicator--reply'}`}
              aria-hidden="true"
            >
              {swipeAction === 'delete' ? <Trash3Fill size={14} /> : <ReplyFill size={14} />}
            </div>
          )}
          {messageReactions.length > 0 && (
            <div className="chat-msg-reactions" aria-label="Реакции">
              {messageReactions.map((entry) => {
                const reaction = getChatReactionConfig(entry.key);
                if (!reaction) return null;
                return (
                  <button
                    key={entry.key}
                    type="button"
                    className={`chat-msg-reaction-badge ${entry.mine ? 'chat-msg-reaction-badge--mine' : ''}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleMenuReaction(msg, entry.key);
                    }}
                    onPointerDown={(event) => event.stopPropagation()}
                    onTouchStart={(event) => event.stopPropagation()}
                    aria-pressed={Boolean(entry.mine)}
                    aria-label={`${reaction.title}: ${entry.count}`}
                    title={entry.mine ? `${reaction.title} - ваша реакция` : reaction.title}
                  >
                    <ChatReactionVisual reaction={reaction} />
                    <span className="chat-msg-reaction-count">{entry.count}</span>
                  </button>
                );
              })}
            </div>
          )}
          <div className="chat-msg-footer">
            {msg.edited_at && <small className="chat-msg-edited">изм.</small>}
            <small className="chat-msg-time">{formatTs(msg.created_at)}</small>
          </div>
        </div>
      </div>
    );
  };

  const renderSearchResult = (result) => {
    const resultId = Number(result?.id || 0);
    if (!resultId) return null;
    const authorName = getDisplayName(result?.author, result?.author_account_id);
    return (
      <button
        key={resultId}
        type="button"
        className={`chat-search-result ${isDark ? 'chat-search-result--dark' : 'chat-search-result--light'}`}
        onClick={() => requestAroundMessage(resultId)}
      >
        <span className="chat-search-result-top">
          <span className="chat-search-result-author">{authorName}</span>
          <span className="chat-search-result-time">{formatTs(result?.created_at)}</span>
        </span>
        <span className="chat-search-result-preview">
          {getSearchPreview(result)}
        </span>
      </button>
    );
  };


  const renderMediaResult = (item, index) => {
    if (!item?.thumb_url) return null;
    return (
      <button
        key={item.id}
        type="button"
        className={`chat-media-tile chat-media-tile--${item.type}`}
        onClick={() => openChatMediaViewer(index)}
        aria-label={item.title || 'Медиа'}
      >
        <img
          src={item.thumb_url}
          alt={item.title || 'Медиа'}
          className="chat-media-tile-img"
          loading="lazy"
        />
        <span className="chat-media-tile-badge">{item.label || 'Медиа'}</span>
      </button>
    );
  };

  const activeMediaItem = chatMediaViewer.show
    ? mediaResults[Math.max(0, Math.min(chatMediaViewer.index, mediaResults.length - 1))]
    : null;

  return (
    <div
      ref={chatScrollRef}
      onScroll={handleScroll}
      className={`min-vh-100 chat-page-shell scrollable ${isDark ? 'chat-page-shell--dark' : 'chat-page-shell--light'} ${useDesktopUnifiedRows ? 'chat-page-shell--desktop-unified' : ''}`}
      style={{
        '--chat-composer-space': `${composerSpace}px`,
        '--chat-contest-space': `${contestSpace}px`,
        backgroundImage: `url("${chatBackgroundImage}")`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
      }}
    >
      <header className={`chat-page-header ${isDark ? 'chat-page-header--dark' : 'chat-page-header--light'}`}>
        {!isChatLocked ? (
          <button
            type="button"
            className={`chat-page-header-action chat-page-header-action--search ${isSearchOpen ? 'chat-page-header-action--active' : ''} ${isDark ? 'chat-page-header-action--dark' : 'chat-page-header-action--light'}`}
            onClick={() => setIsSearchOpen((prev) => !prev)}
            aria-label="Поиск по чату"
            title="Поиск по чату"
          >
            {isSearchOpen ? <X size={17} /> : <Search size={16} />}
          </button>
        ) : (
          <div className="chat-page-header-spacer" aria-hidden="true" />
        )}
        <div className="chat-page-header-text">
          <div className="chat-page-title">Чат БЕШЕНЫЕ СКИДКИ!</div>
          <div className="chat-page-subtitle">
            <span className={`chat-page-presence-dot ${isConnected ? 'chat-page-presence-dot--online' : 'chat-page-presence-dot--offline'}`} />
            {headerStatus}
          </div>
        </div>
        {!isChatLocked ? (
          <button
            type="button"
            className={`chat-page-header-action ${isDark ? 'chat-page-header-action--dark' : 'chat-page-header-action--light'}`}
            onClick={() => setIsChatPushModalOpen(true)}
            aria-label="Настройки push чата"
            title={`Push чата: ${chatPushModeLabel}`}
          >
            <BellFill size={16} />
          </button>
        ) : (
          <div className="chat-page-header-spacer" aria-hidden="true" />
        )}
      </header>

      {!isChatLocked && shouldShowContestPanel && (
        <section className={`chat-contest-panel ${isContestCollapsed ? 'chat-contest-panel--collapsed' : ''} ${isDark ? 'chat-contest-panel--dark' : 'chat-contest-panel--light'}`}>
          <div className="chat-contest-head">
            <button
              type="button"
              className="chat-contest-main-btn"
              onClick={() => setIsContestCollapsed((prev) => !prev)}
              aria-expanded={!isContestCollapsed}
            >
              <span className="chat-contest-icon"><TrophyFill size={15} /></span>
              <span className="chat-contest-title">{activeContest.title || 'Конкурс активности'}</span>
              {contestStatusText && <span className="chat-contest-timer">{contestStatusText}</span>}
              {isContestCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
            </button>
            <button
              type="button"
              className="chat-contest-info-btn"
              onClick={() => setIsContestInfoOpen(true)}
              aria-label="Условия конкурса"
              title="Условия конкурса"
            >
              <InfoCircle size={16} />
            </button>
            <button
              type="button"
              className="chat-contest-close-btn"
              onClick={() => setIsContestDismissed(true)}
              aria-label="Закрыть конкурс"
              title="Скрыть конкурс"
            >
              <X size={15} />
            </button>
          </div>
          {!isContestCollapsed && (
            <div className="chat-contest-body">
              {shouldShowContestRanks && contestTop.length > 0 ? (
                <div className="chat-contest-top">
                  {contestTop.map((entry) => (
                    <div key={`${entry.account_id}-${entry.rank}`} className={`chat-contest-rank chat-contest-rank--${entry.rank}`}>
                      <span className="chat-contest-place">{entry.rank}</span>
                      <span className="chat-contest-name">{getContestParticipantName(entry)}</span>
                      <span className="chat-contest-count">{entry.messages_count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="chat-contest-empty">{contestEmptyText}</div>
              )}
              {shouldShowContestRanks && (
                <div className="chat-contest-me">
                  {contestMe?.rank
                    ? <>Вы: {contestMe.rank} место · {contestMe.messages_count}</>
                    : <>Вы пока не участвуете</>}
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {!isChatLocked && isSearchOpen && (
        <button
          type="button"
          className="chat-search-backdrop"
          aria-label="Закрыть поиск"
          onClick={() => setIsSearchOpen(false)}
        />
      )}

      {!isChatLocked && isSearchOpen && (
        <section className={`chat-search-panel ${isDark ? 'chat-search-panel--dark' : 'chat-search-panel--light'}`}>
          <div className="chat-search-tabs" role="tablist" aria-label="Поиск по чату">
            <button
              type="button"
              className={`chat-search-tab ${searchTab === 'messages' ? 'chat-search-tab--active' : ''}`}
              onClick={() => setSearchTab('messages')}
            >
              <Search size={14} />
              <span>Сообщения</span>
            </button>
            <button
              type="button"
              className={`chat-search-tab ${searchTab === 'media' ? 'chat-search-tab--active' : ''}`}
              onClick={() => setSearchTab('media')}
            >
              <Images size={15} />
              <span>Медиа</span>
            </button>
          </div>

          {searchTab === 'messages' ? (
            <>
              <div className="chat-search-fields">
                <input
                  className="chat-search-input chat-search-input--query"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Текст сообщения или товара"
                  autoComplete="off"
                />
                <input
                  className="chat-search-input chat-search-input--author"
                  value={searchAuthor}
                  onChange={(event) => setSearchAuthor(event.target.value)}
                  placeholder="@логин"
                  autoComplete="off"
                />
              </div>
              <div className="chat-search-meta">
                {isSearchLoading ? (
                  <span>Ищу...</span>
                ) : searchError ? (
                  <span className="chat-search-error">{searchError}</span>
                ) : searchResults.length > 0 ? (
                  <span>Найдено: {searchResults.length}</span>
                ) : (
                  <span>Введите минимум 2 символа или логин автора</span>
                )}
              </div>
              {hasSearchCompleted && !isSearchLoading && !searchError && searchResults.length === 0 && (
                <div className="chat-search-empty">Ничего не найдено</div>
              )}
              {searchResults.length > 0 && (
                <div className="chat-search-results">
                  {searchResults.map(renderSearchResult)}
                  {searchNextCursor && (
                    <button
                      type="button"
                      className="chat-search-more-btn"
                      disabled={isSearchLoading}
                      onClick={() => requestChatSearch({ append: true, cursor: searchNextCursor })}
                    >
                      Показать ещё
                    </button>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="chat-search-meta">
                {isMediaLoading && mediaResults.length === 0 ? (
                  <span>Загружаю медиа...</span>
                ) : mediaError ? (
                  <span className="chat-search-error">{mediaError}</span>
                ) : mediaResults.length > 0 ? (
                  <span>Медиа: {mediaResults.length}</span>
                ) : (
                  <span>Фото, карточки товаров и промо-посты из чата</span>
                )}
              </div>
              {hasMediaCompleted && !isMediaLoading && !mediaError && mediaResults.length === 0 && (
                <div className="chat-search-empty">Медиа пока нет</div>
              )}
              {mediaResults.length > 0 && (
                <>
                  <div className="chat-media-grid">
                    {mediaResults.map(renderMediaResult)}
                  </div>
                  {mediaNextCursor && (
                    <button
                      type="button"
                      className="chat-search-more-btn chat-media-more-btn"
                      disabled={isMediaLoading}
                      onClick={() => requestChatMedia({ append: true, cursor: mediaNextCursor })}
                    >
                      {isMediaLoading ? 'Загружаю...' : 'Показать ещё'}
                    </button>
                  )}
                </>
              )}
            </>
          )}
        </section>
      )}

      {isChatLocked ? (
        <div className="chat-page-alert-wrap chat-page-alert-wrap--subscription">
          <Card className={chatSubscriptionCardClass}>
            <Card.Body>
              <Card.Title className="ws-subscription-title">{subscriptionNoticeTitle}</Card.Title>
              <Card.Text className="ws-subscription-text">
                {subscriptionNoticeText}
              </Card.Text>
              <button
                type="button"
                className="btn pay-primary-action ws-notice-pay-btn chat-page-subscription-btn"
                onClick={() => navigate('/subscribe')}
              >
                Оплатить подписку
              </button>
            </Card.Body>
          </Card>
        </div>
      ) : null}

      {!isChatLocked && chatNotice.show && (
        <button
          type="button"
          className={`chat-floating-notice chat-floating-notice--${chatNotice.variant || 'info'} ${isDark ? 'chat-floating-notice--dark' : 'chat-floating-notice--light'}`}
          onClick={hideChatNotice}
          role="alert"
        >
          {chatNotice.text}
        </button>
      )}

      {!isChatLocked && (
        <main
          className={`chat-page-messages ${isDark ? 'chat-page-messages--dark' : 'chat-page-messages--light'}`}
        >
          {isLoading ? (
            <div className="chat-page-loader">
              <Spinner animation="border" />
            </div>
          ) : (
            <>
              {isLoadingOlder && (
                <div className={`chat-page-history-loader ${isDark ? 'chat-page-history-loader--dark' : 'chat-page-history-loader--light'}`}>
                  Загружаю историю...
                </div>
              )}
              {messages.length === 0 && (
                <div className="chat-page-empty">Пока нет сообщений</div>
              )}
              {messages.map((msg, index) => renderChatMessage(index, msg))}
              {isLoadingNewer && (
                <div className={`chat-page-history-loader chat-page-history-loader--bottom ${isDark ? 'chat-page-history-loader--dark' : 'chat-page-history-loader--light'}`}>
                  Загружаю продолжение...
                </div>
              )}
            </>
          )}
        </main>
      )}

      {!isChatLocked && messageMenu.open && (
        <>
          <button
            type="button"
            className={`chat-msg-menu-backdrop ${messageMenu.mobile ? 'chat-msg-menu-backdrop--mobile' : ''}`}
            aria-label="Закрыть меню"
            onClick={closeMessageMenu}
          />
          <div
            className={`chat-msg-menu ${isDark ? 'chat-msg-menu--dark' : 'chat-msg-menu--light'} ${messageMenu.mobile ? 'chat-msg-menu--mobile' : 'chat-msg-menu--desktop'} chat-msg-menu--${messageMenu.placement || 'below'}`}
            style={{ left: `${messageMenu.x}px`, top: `${messageMenu.y}px` }}
            role="menu"
          >
            {messageMenu.mobile && (
              <div className="chat-msg-reaction-picker" role="group" aria-label="Реакции">
                {CHAT_REACTIONS.map((reaction) => {
                  const isActive = (Array.isArray(messageMenu.message?.reactions) ? messageMenu.message.reactions : [])
                    .some((entry) => entry?.mine && entry?.key === reaction.key);
                  return (
                    <button
                      key={reaction.key}
                      type="button"
                      className={`chat-msg-reaction-picker-btn ${isActive ? 'chat-msg-reaction-picker-btn--active' : ''}`}
                      onClick={() => handleMenuReaction(messageMenu.message, reaction.key)}
                      aria-label={reaction.title}
                      title={reaction.title}
                    >
                      <ChatReactionVisual reaction={reaction} />
                    </button>
                  );
                })}
              </div>
            )}
            <button
              type="button"
              className="chat-msg-menu-item"
              onClick={() => handleMenuReply(messageMenu.message)}
            >
              Ответить
            </button>
            <button
              type="button"
              className="chat-msg-menu-item"
              onClick={() => { void handleMenuCopy(messageMenu.message); }}
            >
              Скопировать
            </button>
            {Number(messageMenu.message?.author_account_id || 0) === myUserId && (
              <>
                <button
                  type="button"
                  className="chat-msg-menu-item"
                  onClick={() => handleMenuEdit(messageMenu.message)}
                >
                  Редактировать
                </button>
                <button
                  type="button"
                  className="chat-msg-menu-item chat-msg-menu-item--danger"
                  onClick={() => handleMenuDelete(messageMenu.message)}
                >
                  Удалить
                </button>
              </>
            )}
          </div>
        </>
      )}

      {!isChatLocked && showScrollDown && (
        <button
          type="button"
          className="chat-scroll-down-btn"
          onClick={handleScrollDownClick}
          aria-label={chatHistoryMode === 'search_context' ? 'К последним сообщениям' : 'Вниз'}
        >
          <ChevronDown size={18} />
          {newBelowCount > 0 && (
            <span className="chat-scroll-down-badge">{newBelowCount}</span>
          )}
        </button>
      )}

      {!isChatLocked && Number(replyMentionState.count || 0) > 0 && (
        <button
          type="button"
          className={`chat-reply-mention-btn ${isReplyMentionLoading ? 'chat-reply-mention-btn--loading' : ''}`}
          onClick={handleReplyMentionClick}
          disabled={isReplyMentionLoading}
          aria-label="Ответы мне"
        >
          <span className="chat-reply-mention-symbol">@</span>
          <span className="chat-scroll-down-badge">{replyMentionState.count}</span>
        </button>
      )}

      {!isChatLocked && (
        <ChatBottomBar
          ref={composerRef}
          value={inputValue}
          onChange={setInputValue}
          onSubmit={() => { void sendMessage(); }}
          onSubmitPromo={() => { void handlePromoSearch(); }}
          disabled={isComposerSubmitDisabled}
          replyName={replyTo ? getDisplayName(replyTo.author, replyTo.author_account_id) : ''}
          replyText={replyTo ? getMessageReplyPreview(replyTo) : ''}
          onCancelReply={() => setReplyTo(null)}
          isEditing={isEditingMessage}
          editText={(editingMessage?.body || '').slice(0, 160)}
          onCancelEdit={() => clearEditingDraft()}
          onPickPhoto={handlePickPhoto}
          photoPreviewUrl={attachedPhoto?.previewUrl || attachedPhoto?.photoThumbUrl || attachedPhoto?.photoUrl || ''}
          photoName={attachedPhoto?.fileName || ''}
          onRemovePhoto={clearAttachedPhoto}
          isPhotoUploading={isUploadingPhoto}
          disablePhotoPick={isEditingMessage || isSendingMessage || isUploadingPhoto || isEditingMessageSaving}
          disablePhotoRemove={isSendingMessage || isUploadingPhoto}
          isPromoMode={isPromoMode}
          promoHintText={promoHintText}
          onDismissPromoMode={dismissPromoMode}
          isPromoLoading={isPromoLoading}
          isSending={isSendingMessage}
          placeholder={isPromoMode ? 'Например: промокоды на еду' : undefined}
        />
      )}

      <Modal
        show={isContestInfoOpen}
        onHide={() => setIsContestInfoOpen(false)}
        centered
        className={`chat-contest-modal ${isDark ? 'chat-contest-modal--dark' : 'chat-contest-modal--light'}`}
        dialogClassName="chat-contest-modal-dialog"
        contentClassName={`chat-contest-modal-content ${isDark ? 'chat-contest-modal-content--dark' : 'chat-contest-modal-content--light'}`}
        backdropClassName="chat-push-modal-backdrop"
      >
        <Modal.Body className="chat-contest-modal-body">
          <div className="chat-contest-modal-head">
            <div>
              <div className="chat-contest-modal-title">{activeContest?.title || 'Конкурс активности'}</div>
              {contestTimerText ? (
                <div className="chat-contest-modal-subtitle">
                  {contestPhase === 'upcoming' ? 'До начала: ' : 'До окончания: '}
                  {contestTimerText}
                </div>
              ) : contestPhase === 'ended' ? (
                <div className="chat-contest-modal-subtitle">Конкурс завершен</div>
              ) : null}
            </div>
            <button
              type="button"
              className="chat-push-modal-close"
              onClick={() => setIsContestInfoOpen(false)}
            >
              Закрыть
            </button>
          </div>
          {contestPrizes.length > 0 && (
            <div className="chat-contest-modal-section">
              <div className="chat-contest-modal-section-title">Призы</div>
              <div className="chat-contest-prizes">
                {contestPrizes.map((prize, index) => (
                  <div key={`${prize?.place || index}-${prize?.prize || index}`} className="chat-contest-prize">
                    <span>{prize?.title || `${prize?.place || index + 1} место`}</span>
                    <strong>{prize?.prize || prize?.text || ''}</strong>
                  </div>
                ))}
              </div>
            </div>
          )}
          {contestRules.length > 0 && (
            <div className="chat-contest-modal-section">
              <div className="chat-contest-modal-section-title">Условия</div>
              <div className="chat-contest-rules">
                {contestRules.map((rule, index) => (
                  <div key={`${index}-${String(rule).slice(0, 16)}`} className="chat-contest-rule">
                    {String(rule || '')}
                  </div>
                ))}
              </div>
            </div>
          )}
          {contestRules.length === 0 && contestPrizes.length === 0 && (
            <div className="chat-contest-rule">Условия конкурса скоро появятся.</div>
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={isChatPushModalOpen}
        onHide={() => !isSavingChatPushMode && setIsChatPushModalOpen(false)}
        centered
        className={`chat-push-modal ${isDark ? 'chat-push-modal--dark' : 'chat-push-modal--light'}`}
        dialogClassName="chat-push-modal-dialog"
        contentClassName={`chat-push-modal-content ${isDark ? 'chat-push-modal-content--dark' : 'chat-push-modal-content--light'}`}
        backdropClassName="chat-push-modal-backdrop"
      >
        <Modal.Body className="chat-push-modal-body">
          <div className="chat-push-modal-head">
            <div>
              <div className="chat-push-modal-title">Push-уведомления чата</div>
              <div className="chat-push-modal-subtitle">
                Сейчас: {chatPushModeLabel}
              </div>
            </div>
            <button
              type="button"
              className="chat-push-modal-close"
              onClick={() => !isSavingChatPushMode && setIsChatPushModalOpen(false)}
              disabled={isSavingChatPushMode}
            >
              Закрыть
            </button>
          </div>

          <div className="chat-push-options">
            {CHAT_PUSH_MODE_OPTIONS.map((option) => {
              const isActive = chatPushMode === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  className={`chat-push-option ${isActive ? 'chat-push-option--active' : ''}`}
                  onClick={() => { void updateChatPushMode(option.value); }}
                  disabled={isSavingChatPushMode}
                >
                  <div className="chat-push-option-main">
                    <span className="chat-push-option-title">{option.title}</span>
                    {isActive && <span className="chat-push-option-badge">Активно</span>}
                  </div>
                  <div className="chat-push-option-desc">{option.description}</div>
                </button>
              );
            })}
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={Boolean(promoResult)}
        onHide={closePromoResult}
        className={`chat-promo-modal ${isDark ? 'chat-promo-modal--dark' : 'chat-promo-modal--light'}`}
        dialogClassName="chat-promo-modal-dialog"
        contentClassName={`chat-promo-modal-content ${isDark ? 'chat-promo-modal-content--dark' : 'chat-promo-modal-content--light'}`}
        backdropClassName="chat-promo-modal-backdrop"
        centered={false}
      >
        <Modal.Body className="chat-promo-modal-body">
          <div className="chat-promo-sheet-header">
            <div className="chat-promo-sheet-header-main">
              <div className="chat-promo-sheet-title">Подбор промокодов</div>
              <div className="chat-promo-sheet-subtitle">
                {promoResult?.comment || 'Подобрал подходящие варианты'}
              </div>
            </div>
            <button
              type="button"
              className="chat-promo-sheet-close"
              onClick={closePromoResult}
            >
              Закрыть
            </button>
          </div>

          {promoCopyNotice && (
            <div className={`chat-promo-copy-notice ${isDark ? 'chat-promo-copy-notice--dark' : 'chat-promo-copy-notice--light'}`}>
              {promoCopyNotice}
            </div>
          )}

          <div className="chat-promo-sheet-scroll">
            {Array.isArray(promoResult?.markets) && promoResult.markets.length > 0 ? (
              promoResult.markets.map((market) => {
                const marketName = String(market?.name || '').trim();
                const isExpanded = Boolean(promoExpandedMarkets[marketName]);
                return (
                  <section key={marketName} className="chat-promo-market">
                    <button
                      type="button"
                      className={`chat-promo-market-toggle ${isExpanded ? 'chat-promo-market-toggle--expanded' : ''}`}
                      onClick={() => togglePromoMarket(marketName)}
                    >
                      <span className="chat-promo-market-main">
                        <span className="chat-promo-market-name">{marketName}</span>
                        <span className="chat-promo-market-count">
                          {Number(market?.total_codes || 0)} кодов
                        </span>
                      </span>
                      <ChevronDown size={16} />
                    </button>

                    {isExpanded && (
                      <div className="chat-promo-market-body">
                        {(Array.isArray(market?.groups) ? market.groups : []).map((group, groupIndex) => (
                          <div key={`${marketName}-${groupIndex}`} className="chat-promo-group">
                            <div className="chat-promo-group-head">
                              <div className="chat-promo-group-title">
                                {group?.title || 'Предложение'}
                              </div>
                              {group?.link && (
                                <a
                                  href={group.link}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="chat-promo-group-link"
                                >
                                  Открыть предложение
                                </a>
                              )}
                            </div>

                            {(Array.isArray(group?.codes) ? group.codes : []).map((item, codeIndex) => (
                              <div key={`${marketName}-${groupIndex}-${codeIndex}`} className="chat-promo-code-row">
                                <div className="chat-promo-code-main">
                                  <div className="chat-promo-code-value">{item?.code || 'Код'}</div>
                                  <div className="chat-promo-code-comment">{item?.comment || 'Без описания'}</div>
                                </div>
                                <button
                                  type="button"
                                  className="chat-promo-code-copy"
                                  onClick={() => { void handlePromoCodeCopy(item?.code || ''); }}
                                >
                                  Скопировать
                                </button>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                );
              })
            ) : (
              <div className="chat-promo-empty">
                Точных совпадений не нашёл. Откройте общий каталог и посмотрите все доступные промокоды.
              </div>
            )}
          </div>

          {promoResult?.allPromosUrl && (
            <div className="chat-promo-sheet-footer">
              <a
                href={promoResult.allPromosUrl}
                target="_blank"
                rel="noreferrer"
                className="chat-promo-sheet-footer-link"
              >
                Все промокоды
              </a>
            </div>
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={chatProductChart.show}
        onHide={closeChatProductChart}
        size="lg"
        centered
        className={`chat-product-chart-modal ${isDark ? 'text-white modal-dark chat-product-chart-modal--dark' : 'text-dark modal-light chat-product-chart-modal--light'}`}
      >
        <Modal.Header
          closeButton
          closeVariant={isDark ? 'white' : undefined}
          className={isDark ? 'border-bottom border-secondary bg-dark text-white' : 'border-bottom border-secondary bg-light text-black'}
        >
          <Modal.Title>📈 Анализ цен</Modal.Title>
        </Modal.Header>
        <Modal.Body className={isDark ? 'bg-dark text-white' : 'bg-light text-dark'}>
          {Array.isArray(chatProductChart.diapaz) && chatProductChart.diapaz.length > 0 ? (
            <>
              <div className="d-flex justify-content-center mb-4 gap-2 flex-wrap">
                <Button
                  variant={chatProductChartPeriod === '1m' ? 'primary' : isDark ? 'outline-light' : 'outline-dark'}
                  onClick={() => setChatProductChartPeriod('1m')}
                  size="sm"
                >
                  1 мес
                </Button>
                <Button
                  variant={chatProductChartPeriod === '3m' ? 'primary' : isDark ? 'outline-light' : 'outline-dark'}
                  onClick={() => setChatProductChartPeriod('3m')}
                  size="sm"
                >
                  3 мес
                </Button>
                <Button
                  variant={chatProductChartPeriod === '6m' ? 'primary' : isDark ? 'outline-light' : 'outline-dark'}
                  onClick={() => setChatProductChartPeriod('6m')}
                  size="sm"
                >
                  6 мес
                </Button>
                <Button
                  variant={chatProductChartPeriod === 'all' ? 'primary' : isDark ? 'outline-light' : 'outline-dark'}
                  onClick={() => setChatProductChartPeriod('all')}
                  size="sm"
                >
                  Все время
                </Button>
              </div>

              <div className="d-flex justify-content-center gap-3 mb-4 flex-wrap">
                <div className="text-center">
                  <div className="d-flex align-items-center justify-content-center gap-1">
                    <span style={{ fontSize: '1.2rem', color: isDark ? '#4ade80' : '#16a34a' }}>📉</span>
                    <div>
                      <div className={`small ${isDark ? 'text-white-50' : 'text-muted'}`}>Минимум</div>
                      <div className="h5 mb-0 fw-bold" style={{ color: isDark ? '#60a5fa' : '#2563eb' }}>
                        {Math.min(...chatProductChart.diapaz.map((item) => item[1]))?.toLocaleString('ru-RU')}₽
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ width: '1px', backgroundColor: isDark ? '#444' : '#ddd' }} />

                <div className="text-center">
                  <div className="d-flex align-items-center justify-content-center gap-1">
                    <span style={{ fontSize: '1.2rem', color: isDark ? '#60a5fa' : '#2563eb' }}>⚡️</span>
                    <div>
                      <div className={`small ${isDark ? 'text-white-50' : 'text-muted'}`}>Умная цена</div>
                      <div className="h5 mb-0 fw-bold" style={{ color: isDark ? '#4ade80' : '#16a34a' }}>
                        {calculateChatProductWeightedMedian(processChatProductPrices(chatProductChart.diapaz))?.toLocaleString('ru-RU') || 'Н/Д'}₽
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ width: '1px', backgroundColor: isDark ? '#444' : '#ddd' }} />

                <div className="text-center">
                  <div className="d-flex align-items-center justify-content-center gap-1">
                    <span style={{ fontSize: '1.2rem', color: isDark ? '#f87171' : '#dc2626' }}>📈</span>
                    <div>
                      <div className={`small ${isDark ? 'text-white-50' : 'text-muted'}`}>Максимум</div>
                      <div className="h5 mb-0 fw-bold" style={{ color: isDark ? '#f87171' : '#dc2626' }}>
                        {Math.max(...chatProductChart.diapaz.map((item) => item[1]))?.toLocaleString('ru-RU')}₽
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <ChatProductPriceChart
                data={chatProductChart.diapaz}
                theme={theme}
                selectedPeriod={chatProductChartPeriod}
              />

              <div className={`text-center mt-3 small ${isDark ? 'text-white-50' : 'text-muted'}`}>
                * Умная базовая цена рассчитывается с учетом продолжительности периодов и исключаются резкие повышения
              </div>
            </>
          ) : (
            <div className="text-center py-4">
              <div className="h4">😕 Нет данных</div>
              <p className="mt-2">История изменения цен недоступна</p>
            </div>
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={chatProductGallery.show}
        onHide={closeChatProductGallery}
        centered
        className={`sales-gallery-modal ${isDark ? 'sales-gallery-modal--dark' : 'sales-gallery-modal--light'}`}
        dialogClassName="sales-gallery-dialog"
        backdropClassName="sales-gallery-backdrop"
        contentClassName={`sales-gallery-content ${isDark ? 'sales-gallery-content--dark' : 'sales-gallery-content--light'}`}
      >
        <Modal.Body className="sales-gallery-body">
          <button
            type="button"
            className="sales-gallery-close"
            onClick={closeChatProductGallery}
            aria-label="Закрыть галерею"
          >
            ×
          </button>

          <div className="sales-gallery-counter">
            {chatProductGallery.images.length > 0 ? `${chatProductGallery.activeIndex + 1} / ${chatProductGallery.images.length}` : ''}
          </div>

          <Carousel
            activeIndex={chatProductGallery.activeIndex}
            onSelect={(selectedIndex) => setChatProductGallery((prev) => ({ ...prev, activeIndex: selectedIndex }))}
            interval={null}
            controls={chatProductGallery.images.length > 1}
            indicators={chatProductGallery.images.length > 1}
            pause="hover"
            wrap={chatProductGallery.images.length > 1}
            touch
            className="sales-gallery-carousel"
          >
            {chatProductGallery.images.map((image, index) => (
              <Carousel.Item key={`${image}-${index}`}>
                <div className="sales-gallery-image-wrap">
                  <img
                    className="sales-gallery-image"
                    src={String(image || '').replace('c246x328', 'c516x688')}
                    alt={`${chatProductGallery.title || 'Фото товара'} ${index + 1}`}
                    loading="lazy"
                  />
                </div>
              </Carousel.Item>
            ))}
          </Carousel>
        </Modal.Body>
      </Modal>


      <Modal
        show={chatMediaViewer.show}
        onHide={closeChatMediaViewer}
        centered
        dialogClassName="chat-media-viewer-dialog"
        contentClassName={`chat-media-viewer-content ${isDark ? 'chat-media-viewer-content--dark' : 'chat-media-viewer-content--light'}`}
        backdropClassName="chat-image-viewer-backdrop"
      >
        <Modal.Body className="chat-media-viewer-body">
          <button
            type="button"
            className="chat-media-viewer-close"
            onClick={closeChatMediaViewer}
            aria-label="Закрыть медиа"
          >
            ×
          </button>

          {activeMediaItem && (
            <>
              <div
                className="chat-media-viewer-stage"
                onTouchStart={handleChatMediaViewerTouchStart}
                onTouchMove={handleChatMediaViewerTouchMove}
                onTouchEnd={handleChatMediaViewerTouchEnd}
                onTouchCancel={handleChatMediaViewerTouchEnd}
              >
                <div
                  className={`chat-media-viewer-track ${isChatMediaTrackDragging ? 'chat-media-viewer-track--dragging' : ''}`}
                  style={{ transform: `translate3d(${chatMediaViewerTrackX}%, 0, 0)` }}
                >
                  {[chatMediaViewer.index - 1, chatMediaViewer.index, chatMediaViewer.index + 1].map((mediaIndex, slideIndex) => {
                    const mediaItem = mediaResults[mediaIndex];
                    const src = String(mediaItem?.photo_url || mediaItem?.thumb_url || '').trim();
                    return (
                      <div
                        key={`${mediaIndex}-${mediaItem?.id || slideIndex}`}
                        className={`chat-media-viewer-slide ${slideIndex === 1 ? 'chat-media-viewer-slide--active' : ''}`}
                      >
                        {src && (
                          <img
                            src={src}
                            alt={mediaItem?.title || 'Медиа'}
                            className="chat-media-viewer-img"
                            draggable="false"
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
                {mediaResults.length > 1 && (
                  <>
                    <button
                      type="button"
                      className="chat-media-viewer-nav chat-media-viewer-nav--prev"
                      disabled={chatMediaViewer.index <= 0}
                      onClick={() => moveChatMediaViewer(-1)}
                      aria-label="Предыдущее медиа"
                    >
                      <span aria-hidden="true">&#8249;</span>
                    </button>
                    <button
                      type="button"
                      className="chat-media-viewer-nav chat-media-viewer-nav--next"
                      disabled={chatMediaViewer.index >= mediaResults.length - 1 && !mediaNextCursor}
                      onClick={() => moveChatMediaViewer(1)}
                      aria-label="Следующее медиа"
                    >
                      <span aria-hidden="true">&#8250;</span>
                    </button>
                  </>
                )}
              </div>

              <div className={`chat-media-viewer-info ${isDark ? 'chat-media-viewer-info--dark' : 'chat-media-viewer-info--light'}`}>
                <div className="chat-media-viewer-top">
                  <span className="chat-media-viewer-badge">{activeMediaItem.label || 'Медиа'}</span>
                  <span className="chat-media-viewer-date">{formatTs(activeMediaItem.created_at)}</span>
                </div>
                <div className="chat-media-viewer-title">{activeMediaItem.title || 'Медиа'}</div>
                {String(activeMediaItem.message?.body || activeMediaItem.message?.channel_post_embed?.body || '').trim() && (
                  <div className="chat-media-viewer-text">
                    {renderMessageBody({
                      body: String(activeMediaItem.message?.body || activeMediaItem.message?.channel_post_embed?.body || '').trim(),
                    })}
                  </div>
                )}
                <div className="chat-media-viewer-actions">
                  {activeMediaItem.source_url && (
                    <a
                      href={activeMediaItem.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="chat-media-viewer-action"
                    >
                      <BoxArrowUpRight size={13} />
                      <span>Открыть ссылку</span>
                    </a>
                  )}
                  <button
                    type="button"
                    className="chat-media-viewer-action chat-media-viewer-action--primary"
                    onClick={() => goToChatMediaMessage(activeMediaItem)}
                  >
                    Перейти к сообщению
                  </button>
                </div>
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={chatImageViewer.show}
        onHide={closeChatImageViewer}
        centered
        dialogClassName="chat-image-viewer-dialog"
        contentClassName={`chat-image-viewer-content ${isDark ? 'chat-image-viewer-content--dark' : 'chat-image-viewer-content--light'}`}
        backdropClassName="chat-image-viewer-backdrop"
      >
        <Modal.Body className="chat-image-viewer-body">
          <div
            className={`chat-image-viewer-stage ${chatImageViewerDragY > 0 ? 'chat-image-viewer-stage--dragging' : ''}`}
            style={{
              transform: `translate3d(0, ${chatImageViewerDragY}px, 0)`,
              opacity: Math.max(0.46, 1 - (chatImageViewerDragY / 260)),
            }}
            onTouchStart={handleChatImageViewerTouchStart}
            onTouchMove={handleChatImageViewerTouchMove}
            onTouchEnd={handleChatImageViewerTouchEnd}
            onTouchCancel={handleChatImageViewerTouchEnd}
          >
            {chatImageViewer.src && (
              <img
                src={chatImageViewer.src}
                alt={chatImageViewer.alt || 'Фото'}
                className="chat-image-viewer-img"
              />
            )}
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={sellerModal.show}
        onHide={closeSellerModal}
        className={`chat-seller-modal ${isDark ? 'chat-seller-modal--dark text-white' : 'chat-seller-modal--light text-dark'}`}
        dialogClassName="chat-seller-modal-dialog"
        contentClassName={`chat-seller-modal-content ${isDark ? 'chat-seller-modal-content--dark' : 'chat-seller-modal-content--light'}`}
        backdropClassName="chat-seller-modal-backdrop"
      >
        <Modal.Header closeButton closeVariant={isDark ? 'white' : undefined} className="chat-seller-modal-header">
          <Modal.Title className="chat-seller-modal-title">
            {selectedSellerItem ? 'Товар' : 'Профиль'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="chat-seller-modal-body">
          {sellerProfile && (
            <div className="chat-seller-profile-card">
              {sellerAvatarUrl ? (
                <button
                  type="button"
                  className="chat-seller-avatar-btn"
                  onClick={() => openChatImageViewer(sellerAvatarFullUrl || sellerAvatarUrl, sellerName || 'Аватар')}
                  aria-label={`Открыть аватар ${sellerName}`}
                >
                  <img
                    src={sellerAvatarUrl}
                    alt={sellerName}
                    className="chat-seller-avatar"
                  />
                </button>
              ) : (
                <span
                  className="chat-seller-avatar chat-seller-avatar--fallback"
                  style={{ backgroundColor: sellerAvatarColor }}
                >
                  {sellerInitials}
                </span>
              )}
              <div className="chat-seller-profile-main">
                <div className="chat-seller-name">{sellerName}</div>
                {sellerProfile.chat_handle && (
                  <div className="chat-seller-handle-row">
                    <button
                      type="button"
                      className="chat-seller-handle"
                      onClick={handleSellerHandleCopy}
                      title="Скопировать профиль"
                    >
                      @{sellerProfile.chat_handle}
                    </button>
                    {sellerCopyNotice && (
                      <span className="chat-seller-copy-note" aria-live="polite">
                        {sellerCopyNotice}
                      </span>
                    )}
                  </div>
                )}
                {sellerProfile.bio && <div className="chat-seller-bio">{sellerProfile.bio}</div>}
              </div>
              {sellerProfile.contact_link && (
                <a
                  href={sellerProfile.contact_link}
                  target="_blank"
                  rel="noreferrer"
                  className="chat-seller-contact-btn"
                >
                  Связаться
                </a>
              )}
            </div>
          )}

          {selectedSellerItem ? (
            <div className="chat-seller-detail-panel">
              <div className="chat-seller-detail-head">
                <button
                  type="button"
                  className="chat-seller-back-btn"
                  onClick={() => setSellerModal((prev) => ({ ...prev, selectedItem: null }))}
                >
                  Назад
                </button>
                <div className="chat-seller-detail-price">
                  {Number(selectedSellerItem.price || 0).toLocaleString('ru-RU')} ₽
                </div>
              </div>
              <div className="chat-seller-detail-scroll">
                {(selectedSellerItem.photo_url || selectedSellerItem.photo_thumb_url) && (
                  <img
                    src={selectedSellerItem.photo_url || selectedSellerItem.photo_thumb_url}
                    alt={selectedSellerItem.title}
                    className="chat-seller-detail-img"
                  />
                )}
                <h5 className="chat-seller-detail-title">{selectedSellerItem.title}</h5>
                {selectedSellerItem.description ? (
                  <div className="chat-seller-detail-description">{selectedSellerItem.description}</div>
                ) : (
                  <div className="chat-seller-detail-empty">Описание не добавлено</div>
                )}
                {selectedSellerContactLink && (
                  <a
                    href={selectedSellerContactLink}
                    target="_blank"
                    rel="noreferrer"
                    className="chat-seller-detail-contact"
                  >
                    Связаться
                  </a>
                )}
              </div>
            </div>
          ) : (
            <div className="chat-seller-products-panel">
              <div className="chat-seller-products-head">
                <h6 className="chat-seller-products-title">Товары</h6>

              </div>
              <div
                ref={sellerProductsScrollRef}
                onScroll={handleSellerProductsScroll}
                className="chat-seller-products-scroll"
              >
                {sellerModal.loading ? (
                  <div className="chat-seller-loader"><Spinner animation="border" size="sm" /></div>
                ) : sellerModal.items.length === 0 ? (
                  <div className="chat-seller-empty">Пока нет товаров</div>
                ) : (
                  <>
                    {sellerModal.items.map((item) => (
                      <Card key={item.id} className="chat-seller-product-card">
                        <Card.Body className="chat-seller-product-body">
                          <button
                            type="button"
                            className="chat-seller-product-row"
                            onClick={() => setSellerModal((prev) => ({ ...prev, selectedItem: item }))}
                          >
                            {item.photo_thumb_url ? (
                              <img
                                src={item.photo_thumb_url}
                                alt={item.title}
                                className="chat-seller-product-img"
                              />
                            ) : (
                              <div className="chat-seller-product-img chat-seller-product-img--empty">Нет фото</div>
                            )}
                            <div className="chat-seller-product-info">
                              <div className="chat-seller-product-title">{item.title}</div>
                              <small className="chat-seller-product-price">{Number(item.price || 0).toLocaleString('ru-RU')} ₽</small>
                            </div>
                          </button>
                        </Card.Body>
                      </Card>
                    ))}
                    {sellerModal.loadingMore && (
                      <div className="chat-seller-loader chat-seller-loader--more">
                        <Spinner animation="border" size="sm" />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ChatPage;
